import { Container } from 'components/container'
import { Layout } from 'components/layout'
import ChapterImageSrc from 'assets/section-image.png'
import { Section } from './components/section'
import { YouTube } from './components/iframe/youtube'
import { LongRead } from './components/long-read'
import { Nav } from './components/nav'
import { Slideshow } from './components/slideshow'
import { ClickableImage } from './components/clickable-image'
import { useChapters } from './useChapters'
import { Arrow, Chapter, Header, ImageTile, Title } from './styled'
import { MobileNav } from './components/nav/mobile-nav'

export const Home = () => {
  useChapters()

  return (
    <Layout>
      <Header>
        <Slideshow />
        <Title>
          <strong>Istorii despre ceea ce contează</strong>
        </Title>
        <Arrow>
          <span />
        </Arrow>
      </Header>

      <Container style={{ position: 'relative' }}>
        <Nav />
        <MobileNav />

        {/*} ----------- INTRO ----------- */}

        <Section isIntro>
          <p>
            În proiectul „Istorii despre ceea ce contează” ne ocupăm de teme
            care preocupă în mod profund publicul nostru, dar care nu sunt
            abordate îndeajuns: schimbarea climei și adoptarea energiei verzi,
            educația în epoca digitală și consecințele practice ale corupției în
            politică.
          </p>
          <p>
            Publicul Europei Libere Moldova de pe platformele noastre digitale a
            sugerat temele, într-un sondaj online, reporterii noștri, ai
            portalului moldova.org și ai comunității RISE Moldova au săpat la
            aceste teme, iar apoi, împreună cu o echipă de jurnaliști digitali
            ai Europei Libere au găsit cele mai bune soluții de prezentare:
            reportaje video, longread-uri și episoade de podcast. Conținutul
            digital creat cu ajutorul acestui proiect este difuzat treptat aici,
            pe paginile de Facebook, Instagram și pe canalul de YouTube al
            Europei Libere, precum și pe platformele instituțiilor partenere,
            iar această pagină se actualizează pe măsură ce producem materiale
            noi.
          </p>
        </Section>

        {/*} ----------- CHAPTER 1 ----------- */}

        <Chapter data-chapter="Să fie lumină">
          <Section menuTitle="Să fie lumină" menuHidden>
            <ImageTile image="https://gdb.rferl.org/01000000-0aff-0242-3a05-08db70c0c4cf_w1200.jpg">
              <div>
                <h2>Să fie lumină</h2>
                <p>
                  În iarna 2023, facturile la energie ale Republicii Moldova au
                  fost plătite din ajutoarele Occidentului. Rusia a redus
                  livrările de gaze, cauzând o criză a prețurilor fără
                  precedent. Lumina s-a stins în multe case și din cauza
                  prețului, și din cauza bombardamentelor rusești în Ucraina
                  vecină. Iarna aceasta o să treacă, dar ce se poate face până
                  la iarna următoare?
                </p>
                <p>
                  Proiectul special „Să fie lumină” e despre cum putem produce
                  energie la noi acasă. Sunt Paula Erizanu. Înainte, nu știam
                  prea multe despre cum funcționează energia verde. Am lucrat la
                  această serie video pentru a te ajuta și pe tine să înțelegi
                  mai bine pașii necesari pentru a-ți reduce facturile și a
                  deveni mai independent.
                </p>
              </div>
            </ImageTile>
          </Section>
          <Section
            menuTitle="Cum au ajuns instituțiile publice din Feștelița să achite zero lei pentru energie"
            hasNextButton
          >
            <YouTube id="1qJfg6CmYkI" />
          </Section>
          <Section
            menuTitle="De la sobă la cazan sau cum a reușit o antreprenoare să cultive flori pentru 8 martie"
            hasNextButton
          >
            <YouTube id="6ngKwxoQOVA" />
          </Section>
          <Section
            menuTitle="Ar putea orașul Chișinău să treacă de la gaz la biomasă?"
            hasNextButton
          >
            <YouTube id="yZL1L6Am2k0" />
          </Section>
          <Section menuTitle="Stâna cu panouri fotovoltaice" hasNextButton>
            <YouTube id="lYn7qFpqJy8" />
          </Section>
          <Section
            menuTitle="Cum îți construiești o casă eficientă energetic?"
            hasNextButton
          >
            <YouTube id="WU8MeThAu0M" />
          </Section>
          <Section
            menuTitle="Cum să ai apă caldă fără gaz sau electricitate"
            hasNextButton
          >
            <YouTube id="5DvLG0cFEVo" />
          </Section>
          <Section
            menuTitle="Soluții pentru a-ți reduce drastic cheltuielile la energie"
            hasNextButton
          >
            <YouTube id="DiIYkVakcVE" />
          </Section>
          <LongRead menuTitle="Tot ce trebuie să știi despre eficiență energetică și energie verde în Moldova">
            <h2>
              Tot ce trebuie să știi despre eficiență energetică și energie
              verde în Moldova
            </h2>
            <ClickableImage
              src="https://gdb.rferl.org/5E6C3C4E-61C1-40A1-8EAA-36BC4A45B454_w1023.jpg"
              caption="Autoritățile moldovene își pun ca scop ca până în 2030, 30% din sursele de energie să fie verzi."
            />
            <p>
              Odată cu scumpirea gazului în iarna care tocmai s-a încheiat,
              energia verde nu mai pare un moft sau un lux, ci o necesitate.
              Dacă în trecut, instalarea cazanelor care ard biomasă (adică
              lemne, vreascuri, peleți sau bricheți), a panourilor fotovoltaice
              și colectoarelor solare costau mult mai mult decât gazul rusesc,
              astăzi, diferența de preț dintre energia durabilă și cea
              tradițională nu mai este la fel de mare, iar investițiile se
              recuperează mult mai repede.
            </p>
            <p>
              Parte din strategia aplicată de guvernul Republicii Moldova,
              pentru a scăpa de șantajul Kremlinului, este să diversifice
              sursele de gaz. Dar aceasta ar însemna să depindem în continuare
              de importuri de energie. Ar putea Republica Moldova să producă
              propria energie? Ar putea autoritățile locale ale Republicii
              Moldova să ia exemplul orașului ucrainean Dubno, cu 40 000 de
              locuitori, care a înlocuit gazul cu biomasă? Care sunt cele mai
              potrivite surse de energie durabilă pentru clima și resursele
              noastre?
            </p>
            <p>
              Proiectul special{' '}
              <a href="https://moldova.europalibera.org/z/22906">
                Să fie lumină
              </a>{' '}
              e despre cum putem produce energie mai ieftină, la noi acasă.
            </p>

            <h3>Cea mai potrivită soluție pentru tine și localitatea ta</h3>

            <p>
              Nu există o singură soluție potrivită pentru toți. Dar vestea bună
              e că poți întreprinde niște pași simpli pentru a afla care sunt
              soluțiile potrivite pentru tine.
            </p>

            <p>
              Înainte de toate, e important să-ți eficientizezi consumul tău de
              energie.
            </p>

            <p>
              Dacă abia începi o construcție, nu uita de izolare.
              Zece-cincisprezece centimetri de termoizolant îți vor economisi
              zeci, poate sute de mii de lei pe termen lung și îți vor aduce și
              confort termic în casă. În medie, datorită izolării, vei consuma
              cu 50% mai puțină căldură.
            </p>

            <p>
              Dacă locuiești în același spațiu de mai mult timp, după
              specialistul în energie durabilă Eugen Camenșcic, primul pas este
              să îți analizezi, într-un tabel, consumul pentru ultimii trei ani:
              ce cheltuieli ai avut la electricitate, la căldură, la apă caldă,
              la gaz sau cât ai dat pe lemne.
            </p>
            <p>
              Dacă locuiești la bloc, o investiție medie, esențială pentru a-ți
              reduce consumul de energie, este punctul termic individual. Acesta
              măsoară temperatura aerului pentru a regla volumul de energie
              termică venită din rețea. Printr-un punct termic individual, care
              costă în jur de 25 000 de euro, un bloc poate economisi până la
              20-30% din cheltuielile pentru căldură. În blocurile vechi, cu un
              sistem vertical de încălzire, de obicei, se poate instala un punct
              termic individual, care e reglat automat, dar poate genera
              economii dacă este monitorizat, pentru a negocia o temperatură
              medie, care să le convină și celor din apartamentele de la primele
              etaje, dar și celor de la ultimele etaje. În blocurile noi, cu
              sistem orizontal de încălzire, se poate instala un punct termic
              individual în fiecare apartament, astfel încât temperatura nu mai
              trebuie negociată cu vecinii, ci poate fi ajustată exclusiv în
              baza preferințelor tale; astfel, poți economisi între 30 și 50%
              energie.
            </p>

            <p>
              Odată analizat consumul, colectoarele solare, care transformă
              energia solară în energie termică, sunt o soluție pentru a-i
              asigura cu apă caldă menajeră și pe cei care locuiesc la bloc, nu
              doar pe cei care stau în case. Dacă o parte considerabilă din
              consumul tău o constituie electricitatea, panourile fotovoltaice —
              care transformă energia solară în energie electrică — ar putea fi
              și ele o soluție potrivită pentru tine.
            </p>
            <p>
              Familia Mocan a găzduit 48 de refugiați ucraineni în ultimul an.
              Problema lor principală ca gazdă a fost lipsa apei calde. Un
              colector solar, care încălzește apa menajeră de la soare, i-a
              ajutat să depășească dificultatea.
            </p>

            <p>
              În timp ce panourile fotovoltaice necesare unei gospodării costă
              între 3 000 și 5 000 de euro, colectoarele solare sunt mult mai
              ieftine, ajungând la prețuri între 5 000 și 15 000 de lei. De
              asemenea, din placă, sticlă, material izolant, țevi și tablă
              zincată, îți poți confecționa un colector solar chiar tu, conform
              unui ghid realizat de organizația Renașterea Rurală.
            </p>
            <p>
              În cazul panourilor fotovoltaice, va trebui să obții avize de la
              furnizorii de energie și să livrezi apoi energie în rețea — având
              plăcerea de a primi facturi zero la energie electrică, în cazul în
              care, anual, vei livra cel puțin la fel de multă energie cât vei
              consuma. În cazul colectoarelor solare, nu ai nevoie de
              permisiuni, cu excepția unui certificat de urbanism dacă locuiești
              în oraș, iar de apă caldă te vei putea folosi fără a apela la un
              furnizor de energie.
            </p>
            <p>
              Fotograful Dinu Bubulici și-a propus să își construiască o casă
              eficientă energetic. Acesta și-a izolat bine casa și a
              achiziționat panouri fotovoltaice. În plus, Dinu și-a mai pus și
              creativitatea în mișcare, inventând un sistem de încălzire a
              podelii... dintr-un aparat de aer condiționat.
            </p>
            <h3>
              Poate Moldova fi o țară agrară care produce nu doar hrană, ci și
              plante energetice?
            </h3>
            <p>
              Dacă, la fel ca aproximativ 60% dintre moldoveni, te încălzești
              iarna cu lemne, poți să-ți reduci consumul investind într-un cazan
              în loc de o sobă. În timp ce sobele au o eficiență de 30%,
              cazanele au o eficiență de 70%-85%. Astfel, poți economisi până la
              50% din lemnul necesar pentru sezonul rece.
            </p>
            <p>
              De asemenea, poți înlocui lemnele cu vreascuri din livezi sau
              chiar peleți sau bricheți produși din coji de nuci sau de floarea
              soarelui, precum și din alte resturi materiale. Toate aceste
              materiale se numesc biomasă.
            </p>
            <p>
              Maria Josan folosea înainte o sobă pentru a crește plante timp de
              câteva săptămâni înainte de 8 martie. Dar acest an a fost diferit.
              Antreprenoarea și-a înmulțit plantele pe parcursul întregului
              sezon rece. Totul datorită unui cazan pe biomasă, unde doamna
              Maria arde lemn și vreascuri tocate din livezile din împrejurimile
              satului.
            </p>
            <p>
              Biomasa poate încălzi nu doar gospodării casnice, ci și localități
              întregi, prin intermediul sistemelor centrale de încălzire.
            </p>
            <p>
              „Fiecare sat trebuie să își cultive propria pădure de biomasă”,
              susține Julian Groger, fondatorul organizației EcoVisio.
            </p>
            <p>
              Folosind mai multe feluri de biomasă în loc de lemn, putem să ne
              păstrăm pădurile. La momentul actual, deși spunem că Moldova este
              o țară agrară, aproape toată producția agricolă este destinată
              pentru hrana oamenilor sau animalelor. Dar noi am putea să ne
              extindem producția de plante energetice pentru a ne salva astfel
              pădurile. Plante energetice pe care le putem crește și folosi pe
              foc în Moldova sunt salcia, plopul, paulownia sau miscanthus
              (cunoscut și drept iarba elefantului).
            </p>
            <p>
              Aceasta e folosită deja pentru a încălzi Institutul de Genetică,
              Fiziologie și Protecție a Plantelor din sectorul Botanica, din
              Chișinău.
            </p>
            <p>
              În afară de biomasă, după Secretara de Stat Carolina Novac,
              Ministerul Energiei a demarat discuții cu Ministerul Agriculturii
              pentru a încuraja agricultorii să producă biogaz care să genereze
              electricitate. Dacă biomasa este solidă, atunci biogazul este gaz
              produs din fermentarea materiei organice.
            </p>
            <p>
              În Republica Moldova, biogazul din bălegar asigură deja cu energie
              electrică, printre alții, fabrica Latesse din Bulboaca, compania
              Garma Grup din Hâncești — unde atât resturile de la ferma de
              bovine, cât și de la fabricile de alcool, sunt transformate în
              energie electrică. Tot din bălegar se încălzește și bucătăria
              Mănăstirii Zăbriceni. Fabrica Sudzuker transformă deșeurile din
              sfecla de zahăr în biomasă și apoi biogaz.
            </p>
            <p>
              Avantajul biogazului și a biomasei față de energia solară sau
              eoliană este că știi exact câtă energie poți produce, depinzând
              mult mai puțin de vreme.
            </p>
            <h3>Toți avem un loc sub soare?</h3>
            <p>
              Două treimi din an, Moldova este însorită, așadar, una dintre cele
              mai potrivite surse de energie regenerabilă este soarele, susțin
              experții. Însă aici e nevoie să distingem între soluții cu
              investiții foarte mari care centralizează energia în câteva mâini,
              cum sunt parcurile fotovoltaice, și soluții cu investiții mult mai
              mici, cum sunt colectoarele solare sau panourile fotovoltaice.
              Acestea descentralizează energia, transformând o mai mare parte a
              populației din simpli consumatori de energie în producători de
              energie.
            </p>
            <p>
              În toamnă, autoritățile vor demara prima licitație pentru
              investitorii în energie regenerabilă, de capacitate mare — adică
              mori de vânt care să producă 105MW și parcuri fotovoltaice care să
              genereze 65MW, ceea ce constituie în jur de un sfert din consumul
              de energie electrică al Republicii Moldova. Contractele cu acești
              investitori vor fi la un preț fix pentru 15 ani.
            </p>
            <p>
              Dar decizia nu a fost văzută cu ochi buni de unii experți. „De ce
              să achităm pentru faptul că cineva o să vină aici și o să facă
              bani din parcuri fotovoltaice?”, susține specialistul în energie
              durabilă Eugen Camenșcic. „Prioritatea trebuie să fie energia
              fotovoltaică descentralizată, pe fiecare acoperiș și pentru
              fiecare casă."
            </p>
            <p>
              După ani în care au folosit lanterna sau lumânarea, în 2020,
              familia Spinei și-a instalat un sistem fotovoltaic la stâna de la
              marginea satului Teleneuți. Costurile de circa 4000 de dolari au
              fost acoperite de un grant din partea Programului Națiunilor Unite
              pentru Dezvoltare și a Ambasadei Suediei.
            </p>
            <p>
              În aceeași cheie, Julian Groger de la EcoVisio spune că Moldova
              trebuie să evite greșelile Germaniei sale natale. „În Germania, în
              anii 90, au fost încurajați marii investitori să producă energie
              solară în loc să fie încurajate comunitățile să se unească și să
              își producă propria energie. Astfel, am ajuns să plătim prețuri
              mari atât pentru energie tradițională, cât și pentru energie
              regenerabilă. Moldova poate învăța din experiența Germaniei, iar
              energia solară să fie accesibilă la prețuri mici pentru o mai mare
              parte din populație."
            </p>
            <h3>Vântul</h3>
            <p>
              În afară de energie solară, și energia eoliană e o soluție pentru
              Moldova. Însă investițiile în energie eoliană sunt mult mai mari.
              „Energia eoliană însă poate deveni accesibilă dacă se unesc
              comunități care să creeze cooperative care să cumpere mori de vânt
              second-hand, la prețuri mai accesibile”, spune Julian Groger.
              „Atunci și sunetul morilor ar putea fi mai plăcut pentru
              comunitățile din apropiere: ar fi sunetul banilor economisiți pe
              energie."
            </p>
            <h3>
              Cum încurajăm eficiența energetică și utilizarea și producția
              energiei verzi?
            </h3>
            <p>
              Un impediment cu care se confruntă producătorii de energie eoliană
              în Republica Moldova este prețul foarte mic la care furnizorii de
              energie achiziționează energia eoliană, față de prețul energiei
              electrice.
            </p>
            <p>
              Autoritățile susțin că problema rezidă în faptul că producătorii
              sunt mici și au semnat contracte directe cu furnizorii de energie,
              pe termen scurt, fără a apela la instrumente de susținere din
              partea statului.
            </p>
            <p>
              O altă problemă este că rețeaua electrică învechită a Republicii
              Moldova nu poate face față la mai mult de 20% de energie solară și
              eoliană. Secretara de stat pe energie durabilă, Carolina Novac,
              spune că investirea în infrastructură este una dintre prioritățile
              guvernului. „Nu poți să te gândești la energie regenerabilă fără
              să dezvolți infrastructura de energie electrică a țării. Ele sunt
              interconectate.” O soluție ar fi crearea unor capacități de
              stocare a energiei solare.
            </p>
            <p>
              Dincolo de producători, ce instrumente pot folosi autoritățile
              statului pentru a încuraja eficiența energetică și energia verde?
              Eugen Camenșcic este promotorul instrumentelor financiare. „Dacă
              statul ar crea un mecanism prin care, de exemplu, să reducă
              dobânda și să crească durata de achitare a creditului pentru
              cazane sau panouri fotovoltaice, multe familii vor lua credite.
              Casa verde ar putea fi o replică a programului de creditare Prima
              casă."
            </p>
            <p>
              Carolina Novac, însă, susține că, în viziunea sa, statul trebuie
              să subsidieze programe de eficiență energetică și nu de
              achiziționare a panourilor fotovoltaice, colectoarelor solare sau
              alte surse de energie alternativă — care au devenit mult mai
              competitive pe piața Republicii Moldova.
            </p>
            <p>
              „Sunt foarte mândră de capacitatea de reacție a moldovenilor, de
              la modul în care au fost primiți refugiații la modul de a
              reacționa la crizele energetice. Ce-am văzut noi în 2021 versus
              2022? Capacitățile de energie alternativă instalate în gospodării
              au crescut cu 300%," spune oficiala. „Iar în primele două luni ale
              anului 2023, capacitățile de energie solară instalate au crescut
              cu 50% față de 2022. Asta e pentru că moldovenii înțeleg că, odată
              ce prețurile la gaz s-au ridicat, aceștia pot să își recupereze
              investiția mult mai rapid, în doar câțiva ani. Deci, la momentul
              de față, oamenii nu au nevoie de subvenții din partea statului
              pentru surse de energie verde. Dar noi vom oferi subvenții pentru
              eficiență energetică, pentru a cheltui mai puțin gaz și
              electricitate.”
            </p>
            <p>
              În aceeași direcție, Ministrul Energiei, Victor Parlicov, a
              anunțat că autoritățile vor demara un program similar cu programul
              Rabla pentru Electrocasnice, din România, conform căruia să poți
              schimba echipamente care consumă multă energie pe echipamente mai
              eficiente.
            </p>
            <p>
              De asemenea, acesta planifică programe de eficiență energetică
              pentru blocurile deținute de stat și pentru instituții publice,
              care urmează să fie implementate de Agenția pentru Eficiență
              Energetică. În baza unui grant de 15 milioane din partea Uniunii
              Europene și a unui împrumut de 70 de milioane de euro acordat de
              Banca Europeană de Reconstrucție și Dezvoltare (BERD) și de Banca
              Europeană pentru Investiții, urmează a fi eficientizate 139 de
              clădiri, printre care, în programul pilot, e vorba de 10
              instituții medicale.
            </p>
            <p>
              În satul Feștelița, instituțiile publice primesc facturi zero la
              energie. Astfel, autoritățile locale au reușit să economisească
              peste un milion de lei, pe care l-au redirecționat pentru a
              menține sistemul de iluminat public.
            </p>
            <h3>
              De ce au eșuat unele inițiative ce țin de energie verde în
              Republica Moldova?
            </h3>
            <p>
              Multe surse de energie durabilă au fost instalate în școli,
              grădinițe sau în alte instituții publice din Moldova prin
              granturi. Însă într-un audit realizat de Vladimir Ursu, fondatorul
              ONG-ului Renașterea Rurală, în unele instituții, colectoarele
              solare au fost uitate imediat după instalare.
            </p>
            <p>
              O problemă ține de instructajul neadecvat despre cum funcționează
              tehnologiile. „Într-un caz, doar paznicul fusese instruit cum
              funcționează colectorul solar și acesta și-a dat demisia după două
              săptămâni și nu a mai transmis informația mai departe,” spune
              Vladimir Ursu.
            </p>
            <p>
              O altă problemă ar fi că nu își asumă responsabilitatea și
              costurile de mentenanță nici donatorii, și nici autoritățile
              locale sau instituțiile publice. „La o grădiniță, se defectase
              colectorul și în loc să cheme un specialist să îl repare,
              conducerea instituției a încurajat părinții să pună mână de la
              mână pentru a cumpăra un boiler de gaz,” a mai adăugat
              specialistul.
            </p>
            <p>
              „Oamenii noștri sunt gata să investească în boilere de gaz,
              cheltuind, pe termen lung, mult mai mulți bani pe consum, și nu în
              instalarea sau mentenanța colectoarelor solare, de exemplu, care
              îi pot ajuta să se bucure de apă caldă gratis.”
            </p>
            <p>
              Aceeași gândire se observă și la scară mai largă. „Autoritățile
              noastre investesc propriii bani în noi stații de gaz, în loc să
              investească în noi centrale care să funcționeze, de exemplu, în
              bază de miscanthus,” spune Eugen Camenșcic.
            </p>
            <p>
              Alte inițiative, precum prima fabrică de biogaz, de la Colonița,
              s-a închis din cauza lipsei de materie primă. Însă inițiatorul ei,
              energeticianul Vasile Moraru, speră să reînvie proiectul cu
              ajutorul autorităților.
            </p>
            <h3>
              Poate energia verde să mărească independența energetică a
              Moldovei?
            </h3>
            <p>
              „Criza energetică am înțeles-o din 1985,” îmi spune Vasile Moraru.
              „Atunci a început discuția despre criza energetică și energia
              verde la noi. În Moldova, la Academia de Științe, era în anii 80
              un proiect de producere a biogazului din bălegar pentru toate
              fermele din țară — parțial, pentru a rezolva problema
              bălegarului." Moraru susține că atunci când a fondat fabrica de
              biogaz, a încercat să-i dea de urmă cercetătorului, care emigrase
              în Rusia, dar fără izbândă.
            </p>
            <p>
              „Noi n-avem economie, noi singuri trebuie să creăm economia și să
              mizăm pe propria energie. Trebuie să avem energia noastră. Când o
              produci cu munca ta, când vezi că prin proiectul dat, se face mai
              curat aerul și veniturile se măresc, vezi altfel situația din
              țară.”
            </p>
            <p>
              Și Carolina Novac, Secretara de Stat a Ministerului Energiei,
              insistă pe aceeași viziune. „Cred că noi avem nevoie în sectorul
              energetic să ne refocusăm pe energia verde, pentru că aceasta e
              energia pe care noi o putem produce la nivel local. Sunt resursele
              noastre. Plus, din punct de vedere economic, energia verde aduce
              valoare adăugată: imporți tehnologie, creezi locuri de muncă,
              capacități noi, generale, care nouă ne lipsesc.”
            </p>
            <p>
              Autoritățile moldovene își pun ca scop ca până în 2030, 30% din
              sursele de energie să fie verzi.
            </p>
            <p>
              Ținta este apropiată de{' '}
              <a
                href="https://romania.representation.ec.europa.eu/news/energie-din-surse-regenerabile-comisia-indeamna-romania-sa-transpuna-directiva-privind-energia-din-2022-05-19_ro"
                target="_blank"
              >
                directiva
              </a>{' '}
              UE, conform căreia statele membre sunt obligate să atingă o cotă
              de 32% de energie durabilă până în 2030. Mult mai ambițioasă,
              Germania își propune ca până în 2050, să își producă 80% din
              energie din surse regenerabile.
            </p>
            <p>
              În timp ce ambițiile noastre ar putea fi mai mari, îndeplinirea
              planului ar însemna, cel puțin, să ajungem cu aproape o treime mai
              independenți energetic — un scop care ține atât de guvernare și de
              finanțarea obținută de la partenerii occidentali, cât și de
              fiecare localitate, instituție, casă și cetățean.
            </p>
          </LongRead>

          <LongRead menuTitle="Un cercetător moldovean dezvoltă panouri fotovoltaice transparente, mai subțiri decât firul de păr">
            <h2>
              Un cercetător moldovean dezvoltă panouri fotovoltaice transparente, mai subțiri decât firul de păr
            </h2>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0a00-0242-9f12-08dc4ca85805_w1023.jpg"
              caption="Nicolae Spălatu este cercetător la Universitatea de Tehnologie din Tallinn, unde lucrează la dezvoltarea unor panouri fotovoltaice."
            />
            <p>
              Originar din satul Ghiliceni, raionul Telenești, Nicolae Spălatu este cercetător la Universitatea de Tehnologie din Tallinn, unde lucrează la dezvoltarea unor panouri fotovoltaice mai subțiri decât un fir de păr. De ce a plecat în Estonia și de ce munca sa rămâne relevantă pentru Republica Moldova?
            </p>
            <p>
              În vârstă de 39 de ani, Nicolae a început să lucreze la tehnologii fotovoltaice încă în 2003, la Chișinău, în timpul studiilor sale de licență și de masterat la Universitatea de Stat din Moldova (USM). Împreună cu mentora sa, Tamara Potlog, și o echipă de masteranzi și doctoranzi, a câștigat, 6 ani ani mai târziu, un grant într-un proiect european numit „Marie Curie”, în colaborare cu universități din Estonia, Elveția și Italia.
            </p>
            <p>
            „Eram fericiți, căci era primul nostru proiect internațional, în valoare de 3 milioane de euro, pentru 3-4 ani. Era o gură de aer proaspăt”, își amintește Nicolae într-o conversație cu Europa Liberă. Proiectul viza dezvoltarea unor panouri fotovoltaice dintr-un material numit cadmium teluric.
            </p>
            <p>
            Cercetătorii moldoveni erau responsabili de dezvoltarea tehnologiei, pentru că, spune Nicolae, R. Moldova are o școală „foarte bună” de electronică și semiconductoare, moștenită din perioada sovietică: „USM avea deja multă expertiză în domeniu și o clasă de ingineri puternici”.
            </p>
            <p>
            O colaborare duce la alta, iar astăzi Nicolae își continuă cercetarea, doar că ... la Universitatea de Tehnologie din Tallinn.
            </p>
            <h3>
              Ce sunt panourile fotovoltaice transparente
            </h3>
            <p>
            Tehnologia la care lucrează cercetătorul moldovean se numește tehnologie a straturilor subțiri (thin film technology, în engleză) și este una dintre{' '}
            <a
              href="https://ec.europa.eu/info/funding-tenders/opportunities/portal/screen/opportunities/topic-details/lc-sc3-res-9-2020"
              target="_blank"
            > prioritățile </a>{' '}de cercetare din Uniunea Europeană. Mai exact, este vorba de straturi semitransparente de materie, cu o grosime de un micron, care să poată fi depuse pe orice suprafață — geamuri, telefoane smart sau pereții serelor. În sere, aceste panouri ar și bloca o parte din razele ultraviolete nedorite pentru plante.
            </p>
            <p>
            Tehnologia este încă la stadiul de elaborare, cu scopul de a fi testată în industrie din 2026 și folosită pe larg din 2030. Asta pentru că UE își propune ca, până în anul 2050, 75% din energie să vină din surse regenerabile, ca soarele sau vântul.
            </p>
            <p>
            Pentru Nicolae, drumul către tehnologia straturilor subțiri a fost cu suișuri și coborâșuri. Și-a scris teza de doctorat despre panourile fotovoltaice care utilizează cadmium teluric, dar cercetătorii din Statele Unite făcuseră pași mai rapizi înainte. În Europa, continuarea în această direcție părea lipsită de perspectivă.
            </p>
            <p>
            Pe de altă parte, tehnologia clasică, pe bază de siliciu, era mult prea costisitoare și poluantă pentru Europa, iar companii chinezești cu sprijin generos din partea statului și costuri de producție mici au devenit lidere pe piață.
            </p>
            <p>
            Astfel, în 2019, UE a {' '}
            <a
              href="https://ec.europa.eu/info/funding-tenders/opportunities/portal/screen/opportunities/topic-details/lc-sc3-res-9-2020"
              target="_blank"
            >decis </a>{' '}cercetarea și dezvoltarea unor noi materiale, cu toxicitate redusă și care să implice cât mai puține resurse energetice în producție, iar Nicolae a văzut aici o șansă.
            </p>
            <p>
            Tehnologia straturilor subțiri, spune el, e un fel de sandviș compus dintr-un strat subțire de silicon (sau siliciu), din cadmium teluric și dintr-un compus ternar de cupru, indiu, galiu și sulf. Acest strat semitransparent nu are eficiența siliciului, care e de 25%, ci ar ajunge la 10% — ceea ce înseamnă că 10% din energia solară absorbită de panou ar fi transformată în energie electrică. Avantajul e că tehnologia e mai puțin poluantă decât siliconul.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/04230000-0aff-0242-a682-08daa141506a_w1023.jpg"
              caption="Panouri fotovoltaice la Molovata, septembrie 2022"
            />
            <p>
            Nicolae spune că R. Moldova are un potențial „foarte mare” pentru a produce energie solară și eoliană, datorită condițiilor sale climaterice, dar nu-i ajunge „voință politică” pentru a face o prioritate din adoptarea noilor tehnologii în domeniul energiei regenerabile. Războiul Rusiei împotriva Ucrainei a forțat autoritățile moldovene să caute soluții pentru a cumpăra energie în altă parte, dar Nicolae se arată dezamăgit de lipsa de inițiativă pe segmentul energetic alternativ.
            </p>
            <h3>
            De ce pleacă cercetătorii moldoveni în alte țări
            </h3>
            <p>
            În 2012, Nicolae și echipa sa au ajuns în impas. Cercetarea în domeniul tehnologiilor fotovoltaice durează, mai ales când nu ai acces la laboratoare bine dotate, și este costisitoare, iar proiectul „Marie Curie” era pe sfârșite.
            </p>
            <p>
            La prima sa deplasare la Tallinn, Nicolae a înțeles că, deși laboratorul de la Chișinău avea o tehnologie bună a vidului, importantă în funcționarea panourilor fotovoltaice, acestuia îi lipseau aparatele de măsurare, microscopia electronică, care le-ar fi permis să le arate structura atomică a materialelor cu care lucrau, pentru a le manipula mai ușor.
            </p>
            <p>
            Față-n față cu oportunitățile de cercetare noi, Nicolae a decis să rămână acolo... „A fost o despărțire foarte grea de echipă, căci eu eram responsabil de toată tehnologia”, recunoaște el, dar nu regretă: „Doamna Tamara Potlog n-a fost de acord să părăsesc laboratorul, dar, în doi-trei ani, toate părțile au înțeles că decizia a fost cea mai bună”.
            </p>
            <p>
            Decizia lui Nicolae și a colegei sale, Natalia Mataciuc (acum în Germania), de a pleca „m-a doborât la pământ”, a spus Europei Libere Tamara Potlog, care a rămas în R. Moldova, la USM. Între timp, ea și vechii colegi s-au împăcat și continuă să lucreze împreună, pentru a face ceva „care să rămână”.
            </p>
            <h3>
            Ce au lăsat în urmă cercetătorii plecați
            </h3>
            <p>
            Tamara Potlog lucrează la tehnologii fotovoltaice încă din 1996 și susține că unul dintre succesele sale a venit în 2002, când a măsurat, într-un laborator din Statele Unite, celulele solare pe care le-a creat la Chișinău și a demonstrat că aveau o eficiență de 10% (este standardul în industrie, dacă nu se folosește siliciu, care are o eficiență de 25%).
            </p>
            <p>
            Potlog a spus Europei Libere că, din păcate, dotarea tehnică a laboratorului de la USM nu s-a schimbat de când a plecat Nicolae, cu peste un deceniu în urmă. Ea cere crearea unui centru cu infrastructură modernă de măsurare în fiecare raport anual, dar fără rezultat. În momente de oboseală, chiar și ea se gândește la drumul emigrației, având deja experiența colaborării cu instituții din Elveția, Japonia sau SUA.
            </p>
            <p>
            Totuși, spune cercetătoarea, unele lucruri s-au îmbunătățit în ultimii ani la USM. Dacă, în afară de dotarea tehnică superioară cu aparate de măsurare, ceea ce l-a determinat pe Nicolae Spalatu să se stabilească în Estonia a fost abilitatea de a participa la conferințe internaționale, în ultimii ani și cercetătorii moldoveni au mult mai multe oportunități de a merge în deplasări și de a-i cunoaște pe specialiștii în domeniile lor.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-cbda-08dc4cb02a90_w1200.jpg"
              caption="Nicolae Spalatu la o conferință în cadrul proiectului pe care îl coordonează, Renew PV, prin care lucrează la inovație în energia fotovoltaică."
            />
            <p>
            Instituțiile de învățământ din UE au, totuși, mai multe resurse financiare pentru a aloca acestor deplasări și sunt eligibile pentru mai multe oportunități de finanțare, adaugă Potlog.
            </p>
            <p>
            Îmbunătățirile din ultimii ani în domeniul cercetării nu sunt, totuși, suficiente. Potlog crede că o mai mare implicare a autorităților este necesară, inclusiv pentru a pune în practică rezultatele cercetărilor locale în domeniul energiei regenerabile. „Doar atunci când vom crea linii pilot, vom avea energie verde”, spune Potlog.
            </p>
            <h3>
            Planuri moldovene noi în domeniul energiei alternative
            </h3>
            <p>
            Ministerul Energiei susține, însă, că sursele regenerabile de energie sunt printre prioritățile strategiei sale, propunându-și, ca până în 2030, țara să își crească consumul de electricitate din surse alternative de la 5% la 30%, astfel încât să asigure aproape integral iluminatul stradal, între alte destinații.
            </p>
            <p>
            Instituția planifică să facă acest salt prin licitații pentru parcuri fotovoltaice și eoliene, tarif fix pentru producătorii și consumatorii de energie eoliană și solară, investigând și perspectivele de dezvoltare a energiei geotermale.
            </p>
            <p>
            Ministerul Energiei urmează a lansat și un proiect care să permită și să încurajeze inovația și să atragă investiții locale și străine în sectorul energetic. Secretara de stat Carolina Novac, responsabilă de decarbonizare, promovarea energiei regenerabile și eficiență energetică, a spus Europei Libere că, deși nu a purtat discuții cu cercetători moldoveni din domeniu, ministerul este deschis față de „propuneri exacte” și vrea să creeze o rețea de experți locali și din diasporă care să contribuie la strategia sa energetică.
            </p>
            <p>
            Piața globală a panourilor fotovoltaice este dominată actualmente de China, care le produce și vinde ieftin. Țări ca Lituania, România sau Slovacia încearcă să concureze, producând și ele tehnologii de energie regenerabilă pentru o piață regională mai mică.
            </p>
            <p>
            În aceste condiții, sunt viabile inițiativele moldovene?
            </p>
            <p>
            Pentru Carolina Novac, „întrebarea e dacă avem suficiente resurse umane pentru a dezvolta tehnologii fotovoltaice care să poată fi competitive cu piața chinezească, care domină industria, datorită subvențiilor masive de stat”.
            </p>
            <h3>
            Politici de cercetare în domeniul energiei verzi: R. Moldova versus Estonia
            </h3>
            <p>
            În timp ce Tamara Potlog de la USM și Nicolae Spălatu de la Universitatea de Tehnologie din Tallinn și-au reluat colaborarea, fiecare are prioritățile sale.
            </p>
            <p>
            Cercetarea lui Nicolae în dezvoltarea tehnologiei straturilor subțiri este parte din proiectul național al Estoniei, stabilit de {' '}
            <a
              href="https://etag.ee/en/"
              target="_blank"
            >Consiliul de Cercetare al Estoniei</a>. În R. Moldova, cel care decide proiectele naționale de cercetare este Ministerul Educației și Cercetării.
            </p>
            <p>
            Și în prioritățile de cercetare ale R. Moldova intră cercetarea în domeniul energiei durabile, însă, bugetul alocat diferă. În timp ce în Estonia, proiectele de cercetare de care beneficiază Nicolae au o finanțare de aproximativ 2 milioane de euro pentru o durată de trei-patru ani, în R. Moldova, Tamara Potlog a avut, de exemplu, acces la sub 300.000 de euro pentru o perioadă de patru ani. Cercetătorii moldoveni se pot pot înscrie și în programul UE <a
              href="https://research-and-innovation.ec.europa.eu/funding/funding-opportunities/funding-programmes-and-open-calls/horizon-europe_en#:~:text=Horizon%20Europe%20is%20the%20EU%27s,budget%20of%20%E2%82%AC95.5%20billion%20."
              target="_blank"
            >Horizon</a>, dar să câștigi proiecte europene nu este simplu, spune cercetătoarea.
            </p>
            <p>
            După Nicolae, problema în mediul academic din R. Moldova este, în primul rând, lipsa de informație despre aceste oportunități și transparența în ceea ce privește proiectele în desfășurare. „Până azi, am colegi la USM care nu știu ce proiecte sunt deschise. În Estonia, orice membru al echipei, începând cu studentul, are acces la toată informația, filtrată pe site-ul universității, pe săptămână și pe lună, și poate aplica la toate proiectele”, spune Nicolae.
            </p>
            <h3>
            Cercetarea moldoveană - care sunt barierele principale și soluțiile
            </h3>
            <p>
            Întrebat ce ar putea să îl atragă înapoi acasă, Nicolae spune că ar avea nevoie de mai multă libertate în acțiune și mai puțină birocrație în ce privește obținerea finanțării: „E vital să se ofere spațiu talentelor tinere să poată participa la proiecte internaționale, să nu li se pună piedici”.
            </p>
            <p>
            Și Tamara Potlog confirmă că, în sistemul moldovean, doar doctoranzii sau post-doctoranzii pot aplica la proiecte internaționale, însă, din punctul său de vedere, marea barieră rămâne a fi nu birocratică, ci financiară și umană.
            </p>
            <p>
            „Desigur că e posibil să devenim competitivi ca țară”, în domeniul energiei regenerabile sau în alte domenii, spune Potlog, însă, pentru a face pași esențiali înainte, „trebuie să revină oameni din exterior, care au muncit cu adevărat și știu să facă ceva, ca Nicolae”.
            </p>
            <p>
            În opinia lui Nicolae Spălatu, colaborarea internațională poate rezolva criza demografică cu care se confruntă nu doar R. Moldova, ci și întreaga Europă. Anunțând câteva poziții de cercetări post-doctorale la Universitatea de Tehnologie din Tallinn, acesta a primit mai multe cereri de înscriere din India decât din Europa. „Este un fenomen întâlnit în multe țări la nivelul UE, mulți studenți din Europa aleg să facă masterat și să meargă în industrie, nu fac doctorate pentru a urma o carieră în cercetare”, explică Nicolae.
            </p>
            <p>
            În prezent, în pofida dificultăților, Tamara Potlog și Nicolae Spălatu continuă o colaborare prin care au implicat și USM într-un proiect de networking în cercetare și inovare, finanțat de UE prin programul COST, pe care Nicolae îl coordonează din 2022 și la care participă specialiști din aproape 50 de țări din Europa, Asia, America de Nord, America Latină și Africa.
            </p>
            <p>
            Unul dintre beneficiarii proiectului este un doctorand al Tamarei Potlog, care urmează linia de cercetare a lui Nicolae. Deși profesoara i-a propus studentului său să meargă să-și facă doctoratul la Tallinn, acesta a preferat să rămână la Chișinău, petrecând o lună în laboratorul lui Nicolae din Estonia, pentru a folosi aparate de măsurare. „Creierii aceștia trebuie ținuți în țară, dar țara trebuie să aibă grijă de ei”, spune Tamara Potlog.
            </p>
          </LongRead>

        </Chapter>

        {/*} ----------- CHAPTER 2 ----------- */}

        <Chapter data-chapter="Cum învață Moldova">
          <Section menuTitle="Cum învață Moldova" menuHidden>
            <ImageTile image="https://gdb.rferl.org/01000000-0aff-0242-54ec-08db70c0c4ba_w1200.jpg">
              <div>
                <h2>Cum învață Moldova</h2>
                <p>
                  Cum învață Moldova este o serie de materiale în care încercăm
                  să urmărim parcursul educațional al unui om în R. Moldova, de
                  la creșă la formarea continuă.
                </p>
                <p>
                  Sunt cu adevărat incluși toți cetățenii în sistemul de
                  educație din Moldova? Cum are loc digitalizarea în învățământ?
                  Aceste două întrebări ne-au ghidat proiectul.
                </p>
                <p>
                  Am stat de vorbă cu elevi, studenți, părinți, dascăli și
                  experți în educație. Ne uităm la fiecare etapă prin prisma
                  incluziunii și a necesităților secolului XXI. Am ales să ne
                  concentrăm asupra problemelor mari și să găsim soluții
                  potrivite pentru ele, dar și să spunem povești individuale,
                  care ilustrează Cum învață Moldova.
                </p>
              </div>
            </ImageTile>
          </Section>
          <LongRead menuTitle="Primii pași sunt cei mai grei. Cât de ușor e să (NU) dai copilul la creșă">
            <h2>
              Primii pași sunt cei mai grei. Cât de ușor e să (NU) dai copilul
              la creșă
            </h2>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-9689-08db4565923c_w1200.jpg"
              caption="Ilie și Tatiana Novac împreună cu cei doi copii, Dragomir și Alistar.  Photo: Alexandru Vengher (Courtesy Image)"
            />
            <p>
              Ne-am dat întâlnire cu Tatiana Novac la Valea Trandafirilor, la
              terenul de joacă. Are doi copii, Alistar de doi ani și șase luni
              și Dragomir, de un an. E profesoară de artă plastică, dar acum e
              în concediu de îngrijire a copilului.
            </p>
            <p>
              Pentru că cei mici nicidecum nu-și pot sincroniza programul zilei
              și dorm la ore diferite, Tatiana spune că nu prea are timp liber.
              Povestește că îi este foarte greu să se descurce singură cu cei
              doi fii, mai ales când iese în oraș, așa că a acceptat să stăm de
              vorbă doar când și soțul ei, Ilie Lupu, și-a făcut timp să vină.
            </p>
            <p>
              Ne întâlnim pe o alee însorită și, cât timp Alistar încă doarme în
              căruciorul împins de Tatiana, Ilie îl ține în brațe pe Dragomir,
              care caută de ce să se agațe cu privirea. Și Ilie e profesor de
              artă plastică, la centrul Lăstărel, dar Tatiana și-a pus cariera
              pe pauză. Încă îl alăptează pe Dragomir, așa că decizia a venit de
              la sine. Familia și-ar dori foarte mult să-l înscrie pe Alistar la
              creșă, ca să le fie mai ușor, dar la grădinița din zona din
              centrul Chișinăului în care locuiesc ar putea merge doar după ce
              împlinește 3 ani și doar dacă vor fi locuri disponibile.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-af11-08db4565923c_w1023.jpg"
              caption="Jurnalista Anastasia Condruc, de vorbă cu soții Novac."
            />
            <h3>
              Nici măcar 2 din 10 copii cu vârsta până la 3 ani nu sunt
              încadrați în creșe
            </h3>
            <p>
              În situația lor mai sunt mii de părinți din țară. Un{' '}
              <a
                href="https://progen.md/wp-content/uploads/2020/12/6760_8075_cpd_servicii_alternative_crese_la_intreprinderi_ajustat.pdf"
                target="_blank"
              >
                raport
              </a>{' '}
              realizat în 2018 a calculat că între anii 2016 și 2018, în
              Republica Moldova s-au născut aproximativ 104 mii de copii, iar
              asta însemna că aproximativ 80 de mii de familii nu au beneficiat
              de servicii de educație timpurie. Situația nu s-a schimbat mult de
              atunci. Dintre copiii de 2 ani, aproximativ 44% merg la grădiniță
              sau creșă. Am discutat cu zeci de mame, părinți, educatori și
              experți în educație pentru a vedea de ce se întâmplă asta, dar și
              ce soluții s-ar putea găsi în viitor.
            </p>
            <p>
              Cum se întâmplă adesea, majoritatea activiștilor pentru educație
              sunt părinții, care au întâlnit diverse probleme în drumul lor
              complicat spre o educație mai bună pentru copiii lor. Alina
              Andronache este una dintre cele mai vocale activiste pe subiectul
              creșelor.
            </p>
            <p>
              S-a ciocnit de problema asta în 2017, atunci când a născut cel
              de-al treilea copil, jongla cu câteva locuri de muncă în același
              timp și ar fi vrut să poată încadra copilul într-un sistem
              educațional cât mai curând. Totuși, reprezentanții creșei la care
              ar fi trebuit să fie repartizată Ioana, fiica ei, i-au spus că
              lista de așteptare e atât de lungă, încât Ioana va putea merge la
              creșă când va împlini 3 ani și 7 luni.
            </p>
            <p>
              Atunci Alina a început să studieze modul în care funcționează
              sistemul de educație timpurie în Moldova și a ajuns chiar să
              participe și la elaborarea unor legi în domeniu. Astăzi este
              expertă în domeniu din partea Centrului Parteneriat pentru
              Dezvoltare.
            </p>

            <ClickableImage
              src="https://gdb.rferl.org/1e910142-e1ba-4454-af62-2cd8f264ce30_w650.jpg"
              caption="Alina Andronache"
            />

            <p>
              Ea spune că prima mare problemă este Codul Educației, „un document
              învechit și de neatins, mai ceva ca Biblia”. Asta pentru că
              ultimele modificări esențiale au fost făcute în 2018, dar și
              pentru că partea care descrie educația timpurie este limitată,
              spune experta.
            </p>

            <p>
              Codul Educației a fost adoptat în 2014, în perioada în care
              ministră a Educației era Maia Sandu, și modificat în 2018, în
              perioada Guvernului Filip și a ministerului de resort condus de
              Monica Babuc, astfel încât incluziunea copiilor într-un sistem de
              educație să înceapă de la 2 ani și nu de la 3. Modificările de
              atunci au fost motivate de faptul că peste 20 de mii de copii mai
              mici de 3 ani rămân anual în afara procesului educațional și de
              îngrijire, dar și de „disponibilitatea tinerelor mame de a revenit
              pe piața forței de muncă”.
            </p>
            <p>
              Odată cu{' '}
              <a
                href="https://www.legis.md/cautare/getResults?doc_id=109818&lang=ro"
                target="_blank"
              >
                modificarea legii
              </a>{' '}
              în 2018 statul s-a angajat să vină cu un program național de
              extindere a creșelor publice până în 2020, lucru care nu s-a
              întâmplat. Astfel, legea a rămas doar pe hârtie și nu a avut mare
              efect în realitate.
            </p>
            <p>
              Astăzi educația timpurie cuprinde grupele antepreșcolare (0-2
              ani), adică creșe; și grupele preșcolare (2-6/7 ani), inclusiv
              pregătitoare. Învățământul obligatoriu începe cu grupa
              pregătitoare din învățământul preșcolar.
            </p>

            <p>
              Tot din 2018 părinții au două opțiuni de concediu pentru perioada
              de îngrijire a copilului: de 2 ani și 2 luni sau 3 ani, ambele
              oferind aceeași valoare totală a indemnizației. Și această
              schimbare a fost menită să readucă părinții mai rapid la locurile
              de muncă, dar până astăzi numărul celor care optează pentru un
              concediu mai scurt a rămas aproape neschimbat.
            </p>

            <p>
              În prezent în Moldova sunt 1483 de instituții de educație timpurie
              (inclusiv grădinițele și creșele publice), iar numărul acestora a
              crescut cu doar 4% în ultimii 10 ani.
            </p>
            <h3>De ce copiii trebuie să meargă la creșă sau grădiniță?</h3>
            <p>
              Pentru Tatiana și Ilie, încadrarea lui Alistar într-un sistem
              educațional ar aduce mai multe beneficii. În primul rând, mai
              multă libertate pentru Tatiana, dar și posibilitatea să-i dedice
              mai mult timp fiului mai mic, Dragomir. Dar mai ales, părinții
              consideră că Alistar are nevoie de socializare și de interacțiuni
              cu semenii lui. „El vrea foarte mult [la creșă?], îi plac copiii”,
              zice Tatiana.
            </p>
            <p>
              Dreptul la educație începe la naștere, spune un{' '}
              <a
                href="https://unesdoc.unesco.org/in/documentViewer.xhtml?v=2.1.196&id=p::usmarcdef_0000383668&file=/in/rest/annotationSVC/DownloadWatermarkedAttachment/attach_import_8215860c-1814-407a-b7ac-becdeb8daae0%3F_%3D383668eng.pdf&locale=en&multi=true&ark=/ark:/48223/pf0000383668/PDF/383668eng.pdf#Education%20starts%20early%20%28UNESCO%29%20V10.indd%3A.67060%3A254"
                target="_blank"
              >
                raport
              </a>{' '}
              al UNESCO. Studiile demonstrează că incluziunea timpurie a
              copiilor în sistemul de educație are numeroase beneficii pentru
              copii și societate în general. Primii ani de viață ai unui copil
              sunt cei care pun{' '}
              <a
                href="https://read.oecd-ilibrary.org/education/starting-strong-2017_9789264276116-en#page13"
                target="_blank"
              >
                baza
              </a>{' '}
              pentru dezvoltarea abilităților, bunăstarea și capacitatea de a
              învăța a unei persoane.
            </p>

            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-31c1-08db45c308e0_w1023.jpg"
              caption="Tatiana Novac și Dragomir, de 1 an."
            />

            <p>
              Un{' '}
              <a
                href="https://www.sciencedirect.com/science/article/pii/S0272775718305855"
                target="_blank"
              >
                studiu
              </a>{' '}
              din Columbia a arătat că integrarea timpurie a copiilor într-un
              program educațional a ajutat la dezvoltarea vocabularului
              acestora. Un alt{' '}
              <a
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4574862/"
                target="_blank"
              >
                studiu
              </a>
              , din Jamaica, a demonstrat că în calitate de adulți copiii care
              au beneficiat de educație timpurie au câștigat financiar cu 25%
              mai mult decât semenii lor.
            </p>
            <p>
              Elena Matalasova este educatoare la un centru privat de dezvoltare
              timpurie și informează părinții despre beneficiile încadrării
              copilului într-un sistem de educație. „Cu cât mai devreme copilul
              vede că există și altceva în afară de ogradă, mama, frați, cu atât
              este mai degajat, mai independent și se dezvoltă mai natural”,
              spune ea. „Copilul trebuie să se adapteze. Tempoul vieții noastre
              s-a schimbat, dar părinții care țin copiii acasă spun «eu îi ofer
              totul». Apoi ajungem la vârsta de 4 anișori și apar probleme.
              Copilul efectiv nu poate să-și facă prieteni, să interacționeze cu
              copii mai agresivi, nu știe să răspundă. Cu maturii astfel de
              copii știu să se înțeleagă, dar cu semenii lor mai greu”, spune
              educatoarea.
            </p>

            <p>
              Elena Matalasova crede că uneori reticența părinților față de
              creșe e cauzată și de lipsa încrederii în sistemul de educație.
              Iar cazurile recente, ca cel de la o creșă din Bălți, unde o
              educatoare a umilit și bătut copiii fac situația și mai
              complicată.
            </p>
            <p>
              Un alt motiv pentru reticența părinților este stereotipul că mai
              mult mamele trebuie să aibă grijă de copiii, inclusiv stau acasă
              cu ei când sunt bolnavi, îi duc sau îi aduc de la școală, etc.
              Deși în ultimii ani situația s-a îmbunătățit, un{' '}
              <a
                href="https://progen.md/wp-content/uploads/2021/03/INDEXUL-EGALITATII-DE-GEN-2021-1.pdf"
                target="_blank"
              >
                studiu
              </a>{' '}
              arată că aproximativ 50% din moldoveni consideră că este datoria
              femeilor să se ocupe de familie și gospodărie, iar 62% consideră
              că e datoria bărbaților să aducă bani în casă. Astfel, renunțarea
              mamelor la viețile profesionale este văzută ca o normalitate.
            </p>
            <h3>Creșele private, o soluție parțială</h3>
            <p>
              Totuși, reticența părinților e un obstacol mai mic decât lipsa
              locurilor și numărul insuficient de creșe. Atunci când nu găsesc
              un loc la creșă în zona în care locuiesc, mulți părinți aleg
              creșele private. Asta a făcut și Alina Andronache, atunci când a
              aflat că fiica ei va putea merge la grădiniță abia la 3 ani și 7
              luni, conform reședinței. „La creșă privată eu am stat 6 luni la
              rând. Creșa mă costa 7000 de lei și salariul meu era 12 mii”,
              povestește Alina.
            </p>
            <p>
              Creșele private, însă, nu coincid mereu cu posibilitățile
              financiare ale moldovenilor. Tatiana și Ilie au luat în calcul o
              creșă privată, dar nu-și permit asta. „Trebuie să dai un salariu
              întreg numai pe creșă. Mai bine stai acasă, că e același lucru”,
              spune Ilie. „E foarte obositor, dar mai bine stau eu acasă”,
              adaugă Tatiana. „Mai vin părinții, mai merg eu la ei și mă ajută.”
            </p>
            <p>
              În medie, creșele private costă părinții aproximativ 350-400 de
              euro pe lună, în timp ce salariul mediu pe economie este de 11700
              lei sau 580 de euro. Una dintre grădinițele recent deschise la
              Chișinău, în stil scandinav, are o{' '}
              <a href="https://ro.wonki.md/enter" target="_blank">
                taxă
              </a>{' '}
              de înscriere de 250 de euro și un tarif lunar de 750 de euro pe
              lună. Prin urmare, creșele private ar putea crea discrepanțe
              sociale și exclude copiii din familii vulnerabile din procesul
              educațional.
            </p>
            <p>
              Pe lângă asta, nici creșele private nu sunt ușor de deschis. Alina
              Andronache spune că cele mai multe dintre acestea activează „pe
              sur”, adică semilegal. Pentru că nu sunt controlate, acreditate și
              încadrate în sistemul educațional de către ministerul Educației,
              de cele mai multe ori creșele private funcționează sub formă de
              SRL, Asociație Obștească, Întreprindere Individuală sau Centru
              Comunitar. De aceea e aproape imposibil de estimat cât din
              necesitate acoperă creșele private sau, în general, câte creșe
              private există în Moldova.{' '}
              <a
                href="https://statbank.statistica.md/PxWeb/pxweb/ro/30%20Statistica%20sociala/30%20Statistica%20sociala__07%20INV__INV020/INV020100.px/table/tableViewLayout1/?rxid=9a62a0d7-86c4-45da-b7e4-fecc26003802"
                target="_blank"
              >
                Datele
              </a>{' '}
              Băncii Naționale de Statistici pentru 2021 spun că în Moldova
              există 15 instituții de educație timpurie private, dar în
              realitate numărul lor este mult mai mare.
            </p>
            <h3>Migrarea la oraș și bugetele locale mici</h3>
            <p>
              Alina Andronache, experta CPD, mai vede două mari probleme legate
              de accesul la creșe în Moldova. Prima se referă la migrarea
              oamenilor de la sate la orașe și în Chișinău. În 2018 în
              municipiul Chișinău rata de integrare a copiilor de până la 3 ani
              în educația timpurie era de 8,9%, în timp ce media pe țară este de
              11,6%. De aceea părinții se grăbesc să-și înscrie copilul la creșă
              sau grădiniță cât mai devreme. „S-a născut copilul, i-ai făcut
              adeverința de naștere și fugi repede și înscrie-l la grădiniță, că
              altfel ai scăpat locul”, spune Alina.
            </p>

            <p>
              Tatiana și Ilie sunt conștienți de acest fenomen și chiar au
              încercat să-l înscrie pe Alistar la o instituție de educație
              timpurie la Florești, un orășel cu 12 mii de locuitori din nordul
              țării. Asta pentru că părinții Tatianei locuiesc acolo și îi
              vizitează des. Dar nici la Florești nu au avut succes. Li s-a spus
              că nu sunt locuri, iar Alistar e acum pe lista de așteptare.
            </p>

            <p>
              Între timp, în Chișinău se construiesc tot mai multe blocuri noi
              și apar cartiere întregi, dar acestea nu sunt asigurate cu
              infrastructura necesară, inclusiv creșe. Asta și pentru că,
              conform legislației, tot ceea ce ține de clădirea în care se
              desfășoare serviciile de educație timpurie este responsabilă
              Administrația Publică Locală, nu Ministerul Educației. Bugetele
              locale sunt modeste, iar finanțarea de la Ministerul Educației se
              calculează doar pentru copiii de la 2 ani în sus. Astfel, APL
              trebuie să creeze creșe din bugetele proprii.
            </p>

            <h3>Regulamentele rigide</h3>
            <p>
              Și dacă Codul Educației este învechit și vag în ceea ce ține
              creșele, în schimb{' '}
              <a
                href="https://www.legis.md/cautare/getResults?doc_id=100971&lang=ro"
                target="_blank"
              >
                Regulamentul sanitar
              </a>{' '}
              pentru instituțiile de educație timpurie este foarte strict, rigid
              și detaliat. De exemplu, regulamentul stipulează strict că e
              nevoie de încăperi separate pentru fiecare activitate, dimensiunea
              exactă a paturilor sau cât timp trebuie spălate sub un jet de apă
              curgătoare jucăriile noi aduse în grădinițe (15 minute). Toate
              stipulările regulamentului sunt obligatorii.
            </p>
            <p>
              În satul Congaz compania Asena-Textil a încercat acum câțiva ani
              să deschidă o grădiniță-creșă pentru copiii celor peste 900 de
              angajați. Aveau un spațiu pentru grădiniță la 30 de metri de la
              clădire, în timp ce regulamentul prevede că spațiul trebuie să fie
              la cel puțin 50 metri distanță. Nu au primit aprobare de la
              Centrul de Sănătate din Comrat și grădinița-creșă nu a fost
              deschisă.
            </p>

            <h3>De ce acte ai nevoie ca să dai copilul la creșă?</h3>
            <p>
              Sistemul este rigid și pentru înscrierea copilului la creșă. Deși
              dreptul la educație e un drept universal, garantat de constituție,
              pentru a înscrie copilul la o instituție de educație timpurie e
              nevoie de un dosar de acte și de mai multe vizite la medic.
            </p>

            <p>
              O mamă de la Bălți mi-a povestit că a fost nevoită să facă zece
              vizite la medic pentru a obține toate actele necesare. Pentru a
              accede la toată lista de medici e nevoie de programări, iar asta
              înseamnă că uneori întregul proces poate dura luni de zile. Alina
              Andronache spune că „este ireal. Eu nu știu cât timp trebuie să ai
              tu ca părinte, ca să stai pe coridoarele astea la nesfârșit,
              câteva săptămâni la rând, și să dai toate analizele acestea. Eu am
              senzația câteodată că autoritățile fac asta intenționat, ca să
              favorizeze corupția.”
            </p>
            <p>
              Lista actelor necesare pentru înmatricularea copiilor într-o
              instituție de învățământ timpurie:
            </p>
            <p>
              Cererea părintelui sau a altui reprezentant legal al copilului la
              care se anexează:
            </p>

            <ul>
              <li>
                copia buletinului de identitate sau a altui document care
                confirmă locul de ședere în districtul arondat;
              </li>
              <li>copia certificatului de naştere a copilului;</li>
              <li>
                extrasul din istoria dezvoltării copilului cu concluziile despre
                starea sănătăţii lui, fișa cu date despre vaccinare și, după
                caz, recomandări privind asistența/îngrijirea, confirmate de
                semnătura medicului și ștampila centrului local de sănătate;
              </li>
              <li>
                certificat-confirmare despre absența contactului cu bolile
                contagioase în perioada pre-înscriere, conformă indicațiilor
                structurilor de sănătate, validat prin semnătura și ștampila
                medicului de familie și eliberat cu 24 ore înainte de începerea
                frecventării instituției;
              </li>
              <li>alte documente necesare stabilirii suportului social.</li>
            </ul>
            <p>
              <strong>
                Înainte de înmatriculare copilul trebuie să treacă și o serie de
                examene medicale complexe, dirijate de medicul de familie.
                Copilul trebuie consultat de: pediatru, chirurg/ortoped,
                neurolog, oftalmolog, otorinolaringolog şi stomatolog.
              </strong>
            </p>
            <h3>Ce efecte au aceste obstacole asupra copiilor și părinților</h3>
            <p>
              Desigur, primii afectați sunt copiii, dar lipsa creșelor
              influențează negativ asupra egalității de gen și vieții
              profesionale a femeilor. În general, rata de ocupare a femeilor cu
              copii de vârstă preșcolară este cu 20% mai mică, comparativ cu cea
              a femeilor care nu au copii preșcolari. Situația se înrăutățește
              cu cât vârsta copiilor este mai mică. Conform ultimelor date BNS,
              dintre femeile care au cel puțin un copil cu vârsta 0-3 ani doar
              aproximativ 19% sunt angajate. Pentru bărbați, cifra celor
              angajați cu un copil de aceeași vârstă este de 66%. „În fiecare an
              scade numărul femeilor care au copii mici încadrate pe piața
              muncii. Ele dispar”, spune Alina Andronache.
            </p>
            <p>
              Dintre toate persoanele din Republica Moldova care nu sunt{' '}
              <a
                href="https://progen.md/wp-content/uploads/2020/12/6760_8075_cpd_servicii_alternative_crese_la_intreprinderi_ajustat.pdf"
                target="_blank"
              >
                încadrate
              </a>{' '}
              pe piața muncii pentru că au grijă de copii sau gospodărie 97%
              sunt femei.
            </p>

            <h3>Ce soluții sunt?</h3>
            <p>
              În luna martie Guvernul a{' '}
              <a
                href="https://mecc.gov.md/ro/content/guvernul-aprobat-strategia-de-dezvoltare-educatia-2030"
                target="_blank"
              >
                adoptat
              </a>{' '}
              Strategia de dezvoltare 2030, care spune că statul va asigura
              „condiții pentru ca toate fetele și băieții să aibă acces la
              dezvoltarea timpurie de calitate, îngrijire și educația
              preșcolară”. Documentul nu menționează cum anume va fi atins acest
              obiectiv.
            </p>
            <p>
              Totuși, statul, împreună cu activiștii din domeniul educației,
              caută soluții pentru lipsa accesului la creșe și încadrarea
              copiilor în educația timpurie.
            </p>
            <p>
              O victorie recentă a fost aprobarea legii pentru servicii
              alternative de îngrijire a copiilor cu vârsta de până la 3 ani,
              înaintată de ministerul Muncii și Protecției Sociale. Legea va
              permite deschiderea creșelor la locul de muncă, de către
              angajator; creșele de tip familial (servicii de creșă acasă la
              îngrijitorul calificat) și certificarea acestor îngrijitori și
              îngrijitoare, adică a bonelor. Pentru creșele la locul de muncă a
              fost creat și un regulament sanitar mai flexibil, iar angajații
              vor putea beneficia de compensații de până la 2500 de lei pentru
              serviciile alternative de îngrijire a copiilor.
            </p>
            <p>
              Alina Andronache, una dintre autorii legii, spune că este
              conștientă că soluțiile aduse se referă mai mult la domeniile
              private și lasă pe dinafară creșele publice. De aceea, Ministerul
              Muncii și Protecției Sociale în frunte cu Alexei Buzu, împreună cu
              experții, creează în prezent un „Program național privind
              serviciile de îngrijire a copiilor cu vârsta de până la 3 ani.”
              Soluțiile propuse în acest document prevăd extinderea serviciului
              de creșe publice, private și a serviciilor alternative de
              îngrijire.
            </p>
            <p>
              Pentru extinderea creșelor publice o soluție este implicarea
              Fondului Național pentru Dezvoltare Regională și Locală (FNDRL),
              explică Alina Andronache. Extinderea ar însemna în primul rând
              crearea unui număr mai mare de creșe, dar și îmbunătățirea celor
              existente și întreținerea lor.
            </p>
            <p>
              Autoritățile Publice Locale ar putea aplica și obține finanțare
              pentru deschiderea creșelor. Desigur, e nevoie de creșterea
              bugetului FNDRL, care în prezent este de 1% din veniturile
              bugetului de stat în fiecare an. „Dar aici pot fi atrase inclusiv
              mijloace oferite prin programe de asistență ale Uniunii Europene
              și alți donatori externi”, spune Alina. În prezent programul este
              prezentat donatorilor, pentru a atrage cât mai multe fonduri.
            </p>
            <p>
              Pentru creșele private soluția principală presupune modificarea
              mai multor legi, „astfel încât ele să intre în albia economiei
              reale”, spune Alina. „Acum ce facem noi, ca experți, împreună cu
              ministerul și parlamentul este fundraising pentru acest Program
              Național, că acolo avem nevoie de miliarde ca să facem față
              situației.”
            </p>

            <h3>„Până se pun în aplicare ne vor crește copiii”</h3>
            <p>
              Părinții cu care am vorbit spun că se bucură de noile modificări,
              în special de diversitatea lor. „Nouă ne-ar conveni creșele de tip
              familial”, spune Ilie, soțul Tatianei. „Așa am putea apela chiar
              la cunoscuți și am avea mai multă încredere, că știm cu cine lăsăm
              copiii”.
            </p>
            <p>
              Dar sunt îngrijorați că lucrurile nu se vor mișca suficient de
              repede pentru ei. „E bine că s-a adoptat, dar până se pun în
              aplicare nouă ne vor crește copiii”, spune Tatiana Novac. „Ne
              facem griji că nici după 3 ani n-o să putem să-l dăm la
              grădiniță”, spune Ilie. Acum familia își împarte viața între
              Florești și Chișinău, în așteptarea unui loc la creșă sau
              grădiniță pentru Alistar.
            </p>
            <p>
              Alina Andronache este optimistă, dar spune că nu există timp de
              pierdut. „Eu nu vreau să laud pe nimeni, dar am lucrat cu trei
              parlamente și cinci guverne, făcând advocacy din societatea
              civilă. Și de obicei era o problemă, fie ministerul bloca
              inițiativele sau nu era deschis, fie parlamentul bloca guvernul,
              pentru că era în opoziție și niciodată nu puteai duce proiectele
              mai departe. Acum este o sinergie dintre societate civilă,
              minister și parlament, care nu știu dacă o să se mai repete.”
            </p>
          </LongRead>
          <Section menuTitle="Cunoașterea limbii române, între necesitate și lipsă de specialiști">
            <YouTube id="X9TXUcT2y_4" />
          </Section>
          <Section
            menuTitle="Roboți, realitate virtuală și digitalizare la liceul din Sipoteni"
            hasNextButton
          >
            <YouTube id="CeqcDeCtYkM" />
          </Section>
          <Section
            menuTitle="Școala incluzivă, între reformă și realitate"
            hasNextButton
          >
            <YouTube id="rG6nS7U0Gk8" />
          </Section>
          <LongRead menuTitle="Sunt elevi și primesc salariu pentru asta. De vorbă cu viitorii ingineri din Strășeni">
            <h2>
              Sunt elevi și primesc salariu pentru asta. De vorbă cu viitorii
              ingineri din Strășeni
            </h2>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0a00-0242-c6b2-08db5c36ddad_w1200.jpg"
              caption="Colegiul de Inginerie din Strășeni are două specializări: Mecatronică și Utilaj Tehnic și Accesorii Industriale, unice în țară și create conform necesităților companiilor din Zona Liberă Economică Bălți. Photo: Tatiana Beghiu (Courtesy Image)"
            />
            <p>
              Ion Șumuleanschi avea cinci ani când a găsit uneltele din garaj, a
              scos ceasul cu cuc de pe perete și l-a desfăcut pe bucăți. Era un
              ceas vechi, rămas de la bunelul, dar, spune el: „îmi era
              interesant să văd ce are înăuntru”.
            </p>

            <p>
              Povestește că n-a mai putut să-l strângă la loc, era prea greu. A
              fost doar primul din șirul lung de obiecte pe care Ion le-a
              dezasamblat de-a lungul vieții. Acum, la 20 de ani, e elev la
              Colegiul de Inginerie din Strășeni și cel mai tare îi place să
              repare chestii sau să le „fărâme”, dacă nu reușește să le dea de
              capăt.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-bcac-08db5c3be734_w1023.jpg"
              caption="Ion Șumuleaschi"
            />
            <h3>Ce este învățământul dual?</h3>
            <p>
              Colegiul de Inginerie din Strășeni este singura instituție de
              învățământ din R. Moldova în care este implementat exclusiv
              învățământul dual. Acesta reprezintă un parteneriat între
              instituțiile de învățământ și agenții economici în care elevii
              petrec jumătate din timp la instituția de învățământ și primesc
              bursă, iar cealaltă jumătate ca ucenici la un agent economic, unde
              primesc și un salariu pe deasupra.
            </p>
            <p>
              Învățământul dual a venit ca o soluție atât pentru lipsa forței de
              muncă, cât și pentru calificarea neadaptată la cerințele pieței.
              Anul trecut 17% dintre agenții economici{' '}
              <a
                href="https://www.expert-grup.org/ro/biblioteca/item/2488-c%C3%A2t-de-oportun%C4%83-este-liberalizarea-condi%C8%9Biilor-de-admitere-a-lucr%C4%83torilor-str%C4%83ini-pe-pia%C8%9Ba-muncii-din-republica-moldova?&category=7"
                target="_blank"
              >
                chestionați
              </a>{' '}
              de Agenția Națională pentru Ocuparea Forței de Muncă spuneau că
              duc lipsă de forță de muncă calificată în ultimele 12 luni, iar
              71% declarau că nu pot găsi personal calificat cu experiență.
              Primele elemente de învățământ dual au început în Moldova în 2014,
              cu menirea să răspundă cerințelor companiilor.
            </p>
            <p>
              Viorica Corlat-Țurcan este expertă în învățământul profesional
              tehnic, dar și profesoară de germană la Colegiul de Inginerie. Ea
              crede că învățământul dual mai rezolvă și problema de imagine a
              învățământului profesional tehnic. Din 2016 până astăzi numărul
              elevilor înscriși la școlile profesionale a scăzut cu 24%. În
              schimb, în aceeași perioadă, a crescut numărul elevilor înscriși
              la colegii cu 5,8%.
            </p>
            <p>
              „Tradițional se spunea că cei care nu pot absolvi liceul trebuie
              să meargă să învețe o meserie, dar eu nu subscriu sub această
              idee.” Experta crede că învățământul dual are o imagine bună și că
              „se consideră prestigios să primești salariu de la agentul
              economic și bursă din partea statului și să ai șanse foarte mari
              să te angajezi după absolvire”.
            </p>
            <h3>Colegiul de Inginerie din Strășeni, unic în țară</h3>
            <p>
              Colegiul de Inginerie din Strășeni e o instituție de învățământ
              relativ nouă, deschisă în 2018. Stagiile de practică au loc atât
              în laboratoarele colegiului, cât și la cei 17 agenți economici cu
              care are stabilite parteneriate.
            </p>

            <p>
              Colegiul e dotat cu utilaje noi și moderne: printere și scannere
              3D, un laborator de electrotehnică, unul de automare și
              robotizare, un spațiu de sudură și lăcătușărie și o sală întreagă
              cu câteva mașini pentru frezare și strunjire.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0a00-0242-6f59-08db5c36ddc1_w1023.jpg"
              caption=""
            />
            <p>
              Unele sunt cumpărate din bugetul public, iar altele donate de
              companiile partenere. Una dintre cele mai atractive zone e o masă
              cu cablaj tehnic pentru Lamborghini, pe care unii dintre elevi îl
              cunosc deja cu ochii închiși. Aproape toți poartă uniforme roșii
              și gri, cu insigna colegiului.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-dd22-08db5c36f844_w1023.jpg"
              caption="Ion Șumuleanschi, la masa cu cablaj tehnic pentru Lamborghini."
            />
            <p>
              În prezent colegiul are două specializări: Mecatronică și Utilaj
              Tehnic și Accesorii Industriale, la fel, unice în țară și create
              conform necesităților companiilor din Zona Liberă Economică Bălți.
            </p>

            <p>
              Deși are o capacitate mult mai mare, colegiul numără doar 52 de
              elevi, iar managementul spune că face promovare și depune toate
              eforturile ca să atragă cât mai mulți. Studiile durează doi ani.
            </p>

            <h3>Jumătate de an în Germania</h3>
            <p>
              Bursele de la stat sunt aproximativ 1000 de lei pe lună, iar
              salariul pe care-l primesc în timp ce fac stagiul practic la
              agentul economic e de cel puțin 4000 de lei net, adică salariul
              minim pe economie. Dar unele companii dau și salarii mai mari, de
              6-7 mii de lei.
            </p>

            <p>
              Totuși, unul dintre cele mai atrăgătoare aspecte pentru elevi e
              programul Jump, care le permite să meargă la practică în Germania
              pentru șase luni. În primul an de studii toți elevii au și lecții
              de germană, timp de o lună, apoi trec un interviu și câțiva sunt
              selectați.
            </p>

            <p>
              „Am înțeles că gata, nu-s în Moldova” Mihai Radvan e unul dintre
              cei șase elevi care au făcut practica anul trecut în Buchen,
              Germania. E din satul Roșcani, Strășeni, dar a absolvit liceul la
              Cojușna. Apoi a venit la Colegiul de Inginerie, pentru că e
              pasionat „de tehnică”.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0a00-0242-986c-08db5c4987e5_w1023.jpg"
              caption=""
            />
            <p>
              „Aici am găsit ceea de ce am nevoie”, zice Mihai. Spune că la
              colegiu au o relație bună cu profesorii. „Ne simțim liberi, nu e o
              relație tensionată, când stai așa, cu frică”. De fapt, unii dintre
              actualii instructori sunt absolvenții primelor generații de la
              colegiu.
            </p>

            <p>
              Mihai va absolvi specializarea Mecatronică, adică programarea și
              mentenanța roboților industriali. „Eu fac programa pentru
              strunguri, freze, iar operatorul primește programa, o inserează în
              calculatorul mașinii în panoul de control și deja începe lucrul”,
              povestește Mihai. Asta a făcut și în Germania timp de șase luni. A
              lucrat la o companie care se ocupă cu injecția de plastic. De
              exemplu, în una dintre mașini producea întrerupătoare industriale,
              cu un grad sporit de siguranță.
            </p>

            <p>
              Deși nu e neapărat foarte vorbăreț, Mihai se entuziasmează când
              povestește despre lucrurile tehnice sau despre Germania. „Când am
              ajuns acolo, parcă nu am realizat că sunt încă într-o țară
              străină. Dar cum am început a merge prin magazine, a da de oameni
              străini, deja am înțeles că gata, nu-s în Moldova.”
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0a00-0242-02b1-08db5c3e3765_w1000.jpg"
              caption="Mihai Radvan"
            />
            <p>
              L-a ajutat să se simtă mai bine și faptul că nu era singur, ci
              împreună cu alți cinci colegi. Locuiau împreună, într-o casă pe
              care le-a pus-o la dispoziție compania, gratis. Au primit și o
              mașină cu care să meargă la serviciu. „Primeam o bursă de 850 de
              euro și îmi ajungea. Noi plăteam doar hrana și benzina, ca să ne
              ducem la lucru”, spune Mihai. „Eu acolo am înțeles un pic și viața
              de matur. Cum e să faci mâncare, toate chestiile casnice. Pentru
              că acasă este mama, nu-i așa mare bătaie de cap. Dar acolo deja
              vii de la lucru, faci mâncare, trebuie să-ți calculezi timp ca să
              reușești și la fotbal, și încolo-încoace.”
            </p>

            <p>
              Compania le oferea și cursuri gratuite de limba germană, iar acum
              Mihai se pregătește de examenul B1. În rest, tot timpul său liber
              „e încărcat cu fotbal”, o altă pasiune. Joacă pentru echipa
              raionului și două echipe private din Strășeni.
            </p>

            <p>
              Din toamnă, însă, Mihai se întoarce în Germania. Deja a primit
              oferta de la compania la care a lucrat anterior și a acceptat
              bucuros, „pentru că nu toți au așa ocazii”. La întrebarea dacă n-o
              să-i fie dor de Moldova răspunde: „o să vedem. Dar acolo am și
              fotbal, iar asta e cel mai important”.
            </p>

            <h3>De la vioară la profil tehnic</h3>
            <p>
              Nicoleta Meaun e singura fată din cei 52 de elevi din colegiu, dar
              spune că se înțelege foarte bine cu băieții, se mai ajută reciproc
              cu temele. E și singura care locuiește în căminul din incinta
              Colegiului, în timp ce băieții stau la căminele din oraș. „Nu mă
              tem, dar mi-i urât”, spune Nicoleta.
            </p>

            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-30e0-08db5c3ec67c_w1200.jpg"
              caption=""
            />
            <p>
              De aceea în fiecare weekend se duce acasă, la Sîngerei, nu departe
              de Bălți. La Bălți a absolvit Școala Profesională Nr. 5,
              specializarea operator de depozite. Deși s-ar fi putut angaja după
              asta, a preferat să-și continue studiile. „M-am sfătuit cu
              maistrul meu de producție și mi-a spus că este colegiu la
              Strășeni, unde poți să ai studii mai bune și am decis să vin
              aici.”
            </p>

            <p>
              De fapt, Nicoleta nu și-a imaginat niciodată că va alege un profil
              tehnic. „De mică nu-mi plăcea nici matematica, nici româna,
              absolut niciun obiect. Și nu știam ce cale să iau, la ce școală să
              mă duc”, spune ea.
            </p>

            <p>
              A făcut mulți ani școala muzicală și vioară, de aceea toți se
              așteptau că Nicoleta va merge pe drumul artei. Dar, după multe
              ezitări și un an în care a exersat tot mai puțin la vioară, a
              decis să urmeze calea părinților ei. Mama și tatăl Nicoletei
              lucrează ambii la Bălți, la două dintre cele mai mari companii
              străine producătoare de cabluri și utilaj industrial. La una
              dintre ele a făcut și ea practica, în calitate de operatoare de
              depozite.
            </p>

            <p>
              „Pentru că e mai aproape de casă. Mie mi-a plăcut foarte mult
              acolo, e o șansă de a mă ridica în postul de lucru, după
              absolvire. Iar ceea ce facem la colegiu și la fabrică practic se
              aseamănă foarte mult”, povestește ea. Avea un salariu de 4000 de
              lei și o bursă de 1200.
            </p>

            <p>
              Spune că îi place și partea teoretică la colegiu, mai ales
              lecțiile despre automatizare și robotică.
            </p>

            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-b78b-08db5c36ddf7_w1023.jpg"
              caption=""
            />
            <p>
              Nicoleta e printre puținii elevi care încă nu sunt hotărâți să
              plece peste hotare imediat după absolvire. Preferă să rămână
              acasă, mai aproape de familie și prieteni. A primit chiar și o
              ofertă de la compania la care a făcut ucenicia, dar spune că-și
              dorește ceva mai mult.
            </p>

            <p>
              „Nu vreau să fiu operator simplu sau tehnician. Aș vrea [să fiu]
              șef de schimb, ceva nu chiar la cel mai jos nivel,” mărturisește
              ea. De aceea, planul ei e să-și continue studiile și planifică să
              aplice la Universitatea Tehnică din Moldova la toamnă. „Și
              părinții tot mă îndeamnă să-mi fac studiile”, spune Nicoleta.
              Apoi, speră la „un lucru stabil, o familie și atât.”
            </p>

            <h3>„Primeam 6200 de lei și eram mulțumit”</h3>
            <p>
              Și Ion, meșterul ceasului cu cuc, are planuri să plece după
              examenele de calificare și absolvire. Destinația lui e Franța,
              acolo unde are rude apropiate. A stat acolo toată vara trecută și
              a venit acasă foarte impresionat și hotărât să se întoarcă. „Mi-a
              plăcut cum sunt acolo casele, cum e totul aranjat și curat”, își
              amintește Ion.
            </p>

            <p>
              Deja știe că cel mai probabil va lucra la o companie de cablaj
              auto din regiunea Bordeaux. De asta nici nu a frecventat prea
              asiduu cursurile de germană. „Mi se încurcă cu franceza”, râde el.
            </p>

            <p>
              Ion vine de la Căușeni, acolo unde a absolvit școala profesională
              și a învățat meseria de electrician în construcții și rețele de
              iluminat. E unul dintre cei care dă o mână de ajutor când e nevoie
              de instalat sau reparat ceva prin colegiu. Mai câștigă un ban
              făcând instalații electrice la comandă în satul lui.
            </p>

            <p>
              Și el a fost ucenic la una din companiile partenere, unde avea
              grijă de mentenanța sculelor, adică era sculer, motiv de glume din
              partea colegilor. „Primeam salariul de bază de 6200 de lei și eram
              mulțumit. Trăiam la un cămin de la universitate, costa o mie de
              lei. Am bursă de gradul unu, aproximativ 1000 de lei”, povestește
              Ion. „La practică iei multă experiență, dar la colegiu oricum
              acumulezi mai multe cunoștințe.”
            </p>

            <p>
              Cât stăm de vorbă, Ion deschide computerul și începe să proiecteze
              o piesă în software-ul SolidWorks, despre care mi-au povestit mai
              mulți elevi. Vorbim despre planurile lui de viitor în timp ce
              desenează în 3D cilindrul rotativ al unui revolver. „E prima ce
              mi-a venit în minte”, zice, dar poate face „orice” acolo.
            </p>

            <p>
              „După SolidWorks mai este programul AutoCAD, dar acolo poți desena
              numai în 2D. Faci schița, cu ajutorul lui SolidWorks poți să tai
              piesa la laser sau s-o scoți la 3D.”
            </p>

            <p>
              Trebuie de dus peste hotare cât ești tânăr, să faci bani de o
              casă, de o mașină, să ai ceva al tău... Și el a primit oferta să
              rămână la compania germană din Bălți, dar preferă să meargă în
              Franța. „E prea devreme să stai amuia în Moldova, trebuie de dus
              peste hotare cât ești tânăr, să faci bani de o casă, de o mașină,
              să ai ceva al tău. Și apoi se poate de venit înapoi,” râde Ion.
              Spune că cheltuie mulți bani și în Moldova ar vrea un salariu de
              cel puțin o mie de euro, iar în Franța de la 3500. „Dar casă o
              să-mi cumpăr în Chișinău, cred că.”
            </p>

            <h3>Mai mulți elevi din toamnă</h3>
            <p>
              De curând Colegiul de Inginerie are o nouă directoare, Gabriela
              Frunză, care spune că abia se acomodează la noul loc de muncă. La
              întrebarea „cum vă simțiți în această poziție?” răspunde
              „întrebați-mă peste șase luni”.
            </p>

            <p>
              Împreună cu vicedirectorii Andrei Nastas și Pavel Gordelenco, care
              sunt și profesori la colegiu, povestește despre cum încearcă să
              aducă mai mulți elevi în instituție. Cea mai importantă schimbare
              în aceasta direcție se va întâmpla chiar la toamnă, când la
              Colegiu se vor putea înscrie și absolvenții de gimnaziu, nu doar
              cei de la școlile profesionale și licee. Vor apărea și două noi
              specializări pentru absolvenții de gimnaziu: Strungar multiprofil
              și Operator pentru introducerea, prelucrarea și validarea datelor.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-e86c-08db5c36fc1e_w1023.jpg"
              caption=""
            />
            <p>
              Deși sunt optimiști, managerii instituției spun că mai sunt multe
              de făcut. În primul rând să reușească să convingă tot mai mulți
              elevi să le calce pragul, să implice agenții economici mai mult în
              recrutarea lor, dar și, în general, să se dezvolte industria în
              Moldova.
            </p>

            <p>
              Deocamdată, chiar dacă mulți dintre ei își schimbă locul de muncă
              sau pleacă peste hotare, rata de angajare după absolvire e de 84%,
              de la 61% în 2020.
            </p>

            <p>
              Anul trecut în Moldova a fost adoptată și legea învățământului
              dual, care prevede că cheltuielile pentru organizarea procesului
              pot fi compensate companiilor de către stat în proporție de până
              la 50%. Ministrul Educației a declarat că pentru asta în 2023 sunt
              alocate 10 milioane de lei.
            </p>

            <p>
              În prezent aproximativ 1500 de elevi beneficiază de învățământul
              dual, în 27 de instituții, care au parteneriate cu 127 de agenți
              economici.
            </p>
          </LongRead>
          <Section menuTitle="De la jurist la IT-ist">
            <YouTube id="Bokfby5066s" />
          </Section>
          <LongRead menuTitle="„Atunci nu aveam să învăț cum învăț acum.” Studentă la 39 de ani">
            <h2>
              „Atunci nu aveam să învăț cum învăț acum.” Studentă la 39 de ani
            </h2>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-b008-08db673cea27_w1200.jpg"
              caption="Eugenia Timco Photo: Grigorii Zarețchii (Courtesy Image)"
            />
            <p>
              Eugenia Timco are 39 de ani și este psihologă. Chiar în aceste
              zile își susține teza de master, în care vorbește despre strategii
              de prevenire a stresului la părinți. Locuiește la Bălți, iar din
              luna martie lucrează și la Centrul Național de Prevenire a
              Abuzului față de Copii.
            </p>

            <p>
              Însă acum 5 ani Eugenia era casnică, o mamă care absolvise liceul
              și avea grijă de cei doi copii ai săi și de gospodărie.
            </p>

            <h3>Școală în Moldova, nu la Moscova</h3>
            <p>
              Eugenia are ochi pătrunzători și o voce caldă, liniștitoare. „Eu
              m-am căsătorit foarte, foarte repede. Am născut și m-am dedicat
              familiei”, își începe ea povestea. Avea 19 ani când a rămas
              însărcinată. Tot atunci, împreună cu soțul și fiul Vladislav, au
              decis să emigreze în Rusia. Timp de cinci ani la Moscova au reușit
              să-și cumpere un apartament și chiar să obțină dreptul de a avea
              cetățenia rusă. Acolo l-a născut și pe cel de-al doilea fecior,
              Stanislav. Totuși, în momentul în care primul băiat trebuia să
              meargă la școală, ceva i-am împins să revină înapoi în Moldova.
              „Soțul meu mereu spunea: copiii care învață în Republica Moldova
              au mai multe șanse să ajungă în Europa”, își amintește Eugenia.
              Mai spune că se simțea singură acolo și oricum își vizita părinții
              în satul de baștină, Costuleni, din raionul Ungheni, o dată la
              câteva luni.
            </p>

            <p>
              După revenire au decis să se stabilească la Bălți. „Pe Vladislav
              l-am dat la școală, pe Stanislav la grădiniță și eu mă ocupam de
              treburile casnice. Acum, privind în urmă, suntem foarte mulțumiți
              de decizia de a reveni acasă.”
            </p>

            <h3>Criza de la 30 de ani</h3>
            <p>
              Totul a început cu o școală de coaching din România despre care a
              aflat de pe Facebook. „Eu stăteam acasă și aveam grijă de copii,
              așa a început să mă intereseze dezvoltarea personală. Acum înțeleg
              că treceam prin perioada crizei de la 30 de ani”, râde Eugenia.
            </p>

            <p>
              Cu toate că nu am dus niciodată lipsă de bani, eu voiam banii mei,
              știți, ca orice femeie... Atunci a decis să meargă în România,
              pentru un curs intensiv de coaching. Deși a revenit acasă foarte
              impresionată, simțea că are nevoie de mai mult. „Cu toate că nu am
              dus niciodată lipsă de bani, eu voiam banii mei, știți, ca orice
              femeie.” Așa că a decis să meargă la facultate. Familia și
              prietenii „au reacționat foarte bine, dar cel mai tare s-a bucurat
              soacra mea. Ea este o doamnă citită, cu carte, pe vremuri a
              absolvit cu diplomă roșie,” povestește Eugenia. „Își dorea o noră
              «învățată».”
            </p>

            <p>
              În 2018, a devenit studentă de Psihologie în anul I, în cadrul
              Universității „Alecu Russo” din Bălți. Avea 34 de ani. A ales să
              meargă la cursuri zi de zi.{' '}
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-dcfd-08db673cea19_w1023.jpg"
              caption="Eugenia Timco"
            />
            <p>
              „Psihologia nici într-un caz nu se face fără frecvență!”, se
              revoltă ea, când o întreb dacă s-a gândit să studieze la distanță.
              „Pentru că este foarte complex, nu poți să înveți psihologie, doar
              așa, studiind niște cărți. Este foarte multă muncă și trebuie să
              te monitorizeze cineva zi de zi, să te întrebe: ai înțeles
              corect?”
            </p>

            <p>
              În Republica Moldova implicarea femeilor tinere, în special cu
              copii mici, în procesul educațional și în câmpul muncii este
              relativ mică. Dintre toate femeile din Moldova între 15 și 29 de
              ani, 35% se{' '}
              <a
                href="https://progen.md/wp-content/uploads/2021/03/INDEXUL-EGALITATII-DE-GEN-2021-1.pdf"
                target="_blank"
              >
                încadrează
              </a>{' '}
              în categoria NEET, adică nu fac studii formale/informale sau
              activează în câmpul muncii. Lipsa accesului la{' '}
              <a
                href="https://www.moldova.org/primii-pasi-sunt-cei-mai-grei-cat-de-usor-e-sa-nu-dai-copilul-la-cresa/"
                target="_blank"
              >
                creșe
              </a>
              , stereotipurile prezente în societate, dar și pandemia au
              accentuat inegalitățile de gen și au forțat tot mai multe femei să
              se dedice gospodăriei, în detrimentul studiilor și al carierei.
            </p>
            <h3>9, 10 și bursa guvernului</h3>
            <p>
              Eugenia povestește că în primele zile la facultate nu știa nici pe
              unde trebuie să intre sau unde să meargă. „Normal că fetelor de
              18-19 ani li se părea wow, că eu sunt așa de mare, ce caut eu
              acolo? Dar eu, de fapt, m-am simțit întotdeauna tânără și mă
              simțeam studentă în rând cu ele”. Asta chiar dacă nu stătea la
              cămin și nu participa la petrecerile studențești. „De aici a
              început totul, viața mea pot să spun.”
            </p>

            <p>
              Spune că nici nu știa la ce să se aștepte când vine vorba de
              studii, dar în cel de-al doilea semestru lucrurile au început să
              capete sens. „Citeam ceva din istoria psihologiei și îmi dădeam
              seama că asta are legătură cu introducerea în psihologie.”
            </p>

            <p>
              Din al doilea semestru Eugenia a fost votată șefa grupei și și-a
              păstrat rolul până azi, când absolvește masteratul. „Dacă trebuia
              ceva de făcut, de hotărât, de dus, eu acolo eram prima, eu mă
              implicam în toate.” Foarte repede și-a dat seama că a făcut
              alegerea corectă, studiile o fascinau și îi mențineau curiozitatea
              aprinsă. „Eu sunt atât de devotată psihologiei și atât de mult îmi
              place, că eu nu știu dacă aș face altceva, decât să aprofundez
              ceea ce cunosc.”
            </p>

            <p>
              Lua note bune, participa în diverse proiecte, iar în anul II a
              obținut și bursa guvernului, acordată studenților care au o bună
              performanță academică și sunt implicați „în viața socio-economică
              a țării”.
            </p>

            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-e2c7-08db673cf008_w1023.jpg"
              caption=""
            />
            <h3>„Mamă, vreau și eu ca tine”</h3>
            <p>
              O zi obișnuită din viața ei însemna să vină spre Universitate
              împreună cu cei doi fii care mergeau la școală, cel mai mic în
              clasa a patra, iar cel mai mare în clasa a noua. Uneori, dacă se
              sincronizau la numărul de lecții, veneau și acasă împreună.
              „Mâncam și, pe la 5 seara, gata, noi făceam temele. Soțul meu
              spunea că are trei studenți în familie, că n-are loc de atâtea
              cărți când vine acasă.”
            </p>

            <p>
              Rezultatele ei i-au inspirat și pe cei din jur, dar cel mai mult
              pe fiul cel mic. „Eu când îi arătam numai note de 9 și 10 el zicea
              «mamă, vreau și eu ca tine». Între noi parcă era concurență, cine
              va avea note mai bune”, râde Eugenia.
            </p>

            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-1602-08db67429740_w1200.jpg"
              caption="Eugenia Timco alături de cei doi fii."
            />

            <h3>Stereotipuri și provocări</h3>
            <p>
              Spre finalul anului III de licență, Eugeniei deja i-a fost propus
              primul loc de muncă. Era nevoie de un psiholog la Școala
              profesională nr.3 din Bălți și s-a lăsat convinsă să încerce.
              Atunci și-a dat seama că la facultate i-a lipsit partea practică.
              În primele două săptămâni de lucru nu prea a ieșit din cabinet.
              „Eu îmi spuneam că lucrez la plan, că nu sunt pregătită încă, dar
              de fapt aveam așa o frică”, își amintește ea. Apoi și-a pus la
              punct un plan: a mers din clasă-n clasă și s-a prezentat, iar
              copiii au început să vină la ea.
            </p>

            <p>
              Unii copii voiau să-și aducă și părinții, mă întrebau cât costă să
              vină cu mama la psiholog...
            </p>
            <p>
              Uneori și profesorii, în special diriginții, o căutau când era
              nevoie de intervenția ei. „Eu mergeam și rezolvam problema,
              stăteam de vorbă, dar foarte voalat, după care ei singuri începeau
              să vină la cabinet. Dacă venea un copil și rămânea impresionat, le
              povestea celorlalți și apoi veneau și alții, și singuri, și în
              grup. Unii copii voiau să-și aducă și părinții, mă întrebau cât
              costă să vină cu mama la psiholog”, spune Eugenia. Deși n-a mai
              ajuns să facă sesiuni cu părinții și copiii, spune că ajuta
              cadrele didactice „când aveau ceva probleme de hotărât pe moment”.
            </p>

            <p>
              Totuși, încă se confruntă cu stereotipurile: „chiar și printre
              studenți se mai crede uneori că psihologul lucrează cu cei
              «nebuni»”.
            </p>

            <p>
              La începutul lui 2023, în urma unui concurs, a primit o nouă
              ofertă - psihologă în cadrul Centrului Național de Prevenire a
              Abuzului față de Copii. Acum Eugenia lucrează cu minori care au
              fost victime ale violenței fizice, sexuale, neglijării sau
              traficului de ființe umane. „E complicat, e stresant, e o
              responsabilitate foarte mare. Câteodată sunt foarte obosită și nu
              mă pot detașa de cazurile la care lucrez, pot să le visez noaptea
              și să umblu câteva zile stresată, dar suntem mai mulți colegi și
              ne susținem, ne încurajăm.”
            </p>

            <h3>La masterat, cu fina</h3>
            <p>
              Îi mai complică lucrul cu normă întreagă și faptul că își
              pregătește și teza de master în același timp. „Acum am puțin timp
              la dispoziție ca să-l acord scrierii și cercetării.”
            </p>

            <p>
              Recunoaște că uneori a trebuit să renunțe și la viața personală,
              poate chiar să acorde mai puțină atenție propriilor copii, care
              treceau prin perioada adolescenței - acum Stanislav are 15 ani și
              Vladislav 19. Dar și-a asumat această decizie și speră să devină
              un model pentru ei. „Dacă băiatul meu va ajunge la vârsta mea și
              va vrea să mai facă o facultate, căreia să i se dedice așa cum am
              făcut-o eu, îl voi susține, pentru că studiile de acum, la 19 ani…
              Nu știu, mie maturitatea mi-a adus avantaje,” zice hotărâtă
              Eugenia.
            </p>

            <p>
              „Mama mea îmi spunea «învață acum, dacă n-ai avut când să înveți
              când ai fost tânără», iar eu îi răspund că atunci nu aveam să
              învăț cum învăț acum, n-aș fi fost la fel de dedicată”, povestește
              Eugenia.
            </p>

            <p>
              Unii deja sunt gata să lase totul, dar eu abia mă implic...
              Vecinii, prietenii, rudele - cu toții au susținut-o, iar ea a
              înțeles că și față de ei are câteva atuuri. „Mi-au spus că este
              greu, unii deja sunt gata să lase totul, dar eu abia mă implic.
              Înțeleg de ce spun asta, pentru că apare o ardere profesională în
              timp, dar eu încă vin cu idei noi, cu energie și sunt gata să mă
              implic.”
            </p>

            <p>
              Pe alții chiar „i-a molipsit” de virusul învățării. Fina ei, care
              absolvise ASEM-ul cu mulți ani în urmă, după discuții înflăcărate
              despre psihologie cu Eugenia, a depus dosarul la masterat împreună
              cu ea. Astfel, fina și nașa au studiat în aceeași grupă, la
              specializarea Consiliere Psihologică în Instituții și Organizații,
              iar acum absolvesc tot împreună.
            </p>

            <p>
              Studiile și alegerea unui domeniu potrivit au ajutat-o și
              personal. „În primul rând mi-am răspuns mie la foarte multe
              întrebări, studiind psihologia. Am devenit mai încrezătoare,
              echilibrată.”
            </p>

            <p>
              După susținerea tezei de masterat, Eugenia va merge mai departe,
              se gândește să depună dosarul și la doctorat, iar între timp face
              și o serie de cursuri de trei ani care o vor ajută să devină și
              psihoterapeută, pentru că visează să aibă și un cabinet propriu
              într-o zi.
            </p>
          </LongRead>
          <Section menuTitle="Ultimul sunet la gimnaziul dintr-o comunitate romă">
            <YouTube id="sNDsqawg6YE" />
          </Section>
        </Chapter>

        {/*} ----------- CHAPTER 3 ----------- */}

        <Chapter data-chapter="În vizor">
          <Section menuTitle="În vizor" menuHidden>
            <ImageTile image="https://gdb.rferl.org/6ac000d6-99c4-4e29-9d67-998e984856b5_w1200.jpg">
              <div>
                <h2>În vizor</h2>
                <p>
                  În vizor cuprinde o serie de articole digitale de
                  investigație, la care au colaborat jurnaliștii Europei Libere
                  Moldova și RISE Moldova, înaintea alegerilor locale din toamna
                  2023. Reporterii noștri au săpat în declarațiile de avere și
                  interese ale mai multor șefi de întreprinderi municipale din
                  Chișinău și s-au uitat în culisele unor concerte „pentru
                  diaspora” organizate de primăria capitalei în mai multe orașe
                  europene. Pe măsură ce alegerile se apropie, intră „În vizor”
                  și principalii candidați la funcția de primar al capitalei.
                </p>
              </div>
            </ImageTile>
          </Section>
          <LongRead
            isFollowedByLongread
            menuTitle="Concertele primăriei în diaspora: Nota de plată pentru chișinăuieni"
          >
            <h2>
              Concertele primăriei în diaspora: Nota de plată pentru
              chișinăuieni
            </h2>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-1ba8-08db8e6ccd4d_w1200.jpg"
              caption="Edilul Ion Ceban alături de mai mulți artiști la un concert organizat de primăria Chișină la Viena, 23 iunie."
            />
            <p>
              Primăria Chișinău, în frunte cu primarul Ion Ceban, a planificat
              pentru 2023 cheltuieli de milioane de lei pentru o serie de
              concerte organizate în diaspora.
            </p>
            <p>
              Deși sunt bani publici, iar Ceban susține că Primăria Chișinău
              este „cea mai transparentă instituție din țară”, acesta evită să
              răspundă jurnaliștilor atunci când este întrebat de sume concrete.
            </p>

            <p>
              Pe 20 iunie curent, cu câteva luni înainte de încheierea
              mandatului său, primarul Chișinăului, Ion Ceban, reamintește pe
              pagina sa de Facebook despre un eveniment mai puțin obișnuit
              pentru instituția pe care o conduce: „Zilele Chișinăului” la
              Viena, un concert cu acces gratuit organizat de Primăria
              Chișinăului.
            </p>

            <p>
              Acesta adună artiști și diaspora la un loc, cu scopul declarat de
              promovare a culturii naționale și a informării cu privire la
              programele oferite de instituția municipală pentru moldovenii
              stabiliți peste hotare.
            </p>
            <p>
              În documentul privind bugetul primăriei pentru acest an, sunt
              enumerate mai multe spectacole în diaspora. Pentru concertele din
              Austria, Italia și Spania, potrivit documentului, au fost estimate
              cheltuieli totale de 9 milioane de lei.
            </p>

            <p>
              Este vorba și despre concertele din România, organizate în șase
              orașe – Buzău, Suceava, Alba Iulia, Botoșani, Piatra Neamț și
              Vaslui – pentru care s-au planificat cheltuieli de încă 3 milioane
              de lei.
            </p>

            <h3>Ion Ceban la Viena</h3>
            <p>
              În seara de 23 iunie, Ion Ceban apare, alături de viceprimarii
              săi, Olga Ursu și Irina Gutnic, în sala de concerte „Das MuTh” de
              la Viena. Potrivit primăriei, la eveniment au fost prezenți în jur
              de 500 de moldoveni.
            </p>

            <p>
              La intrarea în sală, oaspeții sunt întâmpinați de un afiș care
              anunță că Primăria Chișinău este organizatorul evenimentului, iar
              pe hol sunt distribuite pliante publicitare despre activitățile
              echipei lui Ceban de la primărie.
            </p>

            <p>
              În acest an, primăria a cheltuit aproape un milion de lei pentru
              materiale informative privind activitatea sa. Instituția a
              organizat două{' '}
              <a
                href="https://tender.gov.md/ro/contracte-atribuite?field_num_rul_procedurii_value=&field_operator_economic_value=&field_autoritatea_contractant__value=Chisinau&field_obiectul_achizi_iei_value=informativ&field_cpv3_ca_tid=&field_data_documentului_value%5Bmin%5D%5Bdate%5D=&field_data_documentului_value%5Bmax%5D%5Bdate%5D=&field_raion_tid=All&field_idno_ca_value="
                target="_blank"
              >
                {' '}
                licitații
              </a>{' '}
              pentru servicii de tipărire, pentru care a plătit 540.000 de lei
              și, respectiv, 348.000 de lei.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0a00-0242-f1e6-08db8e6d6c63_w1200.jpg"
              caption="Materiale informative despre activitatea Primăriei Chișinău afișate în holul sălii unde a avut loc concertul de la Viena"
            />
            <p>
              O parte din aceste materiale au ajuns, inclusiv, în mâinile
              moldovenilor veniți la concertul din capitala Austriei.
            </p>

            <p>
              Dintre artiști, la concertul de la Viena au urcat pe scenă
              Camerata Chișinău, Brio Sonores, Natalia Tanasiiciuc, Vitalie și
              Cătălin Advahov. Concertul a fost prezentat de jurnalistul Alex
              Luca, în trecut prezentator la televiziunea Prime Moldova.
            </p>

            <p>
              Contactați de Europa Liberă, băieții de la „Brio Sonores” au spus
              că, din acest an, sunt angajați ai Primăriei Chișinău. Același
              lucru l-au confirmat și membrii trupei „Zdob și Zdub”, care au
              cântat la concertul primăriei de la Barcelona.
            </p>

            <p>
              „Din anul 2023, noi suntem angajați la Direcția Cultură a
              municipiului Chișinău și primim salarii. Noi nu am primit bani
              pentru acest eveniment. Acestea sunt obligații pe care noi trebuie
              să le facem”, au spus artiștii de la „Zdob și Zdub”, precizând că
              primăria le-a achitat cazarea și biletele de avion.
            </p>

            <h3>Cheltuieli de milioane, netransparente</h3>
            <p>
              În contextul primului concert, Ion Ceban a spus Europei Libere că
              primăria a acoperit doar costurile pentru deplasarea artiștilor și
              a negat cheltuielile de 9 milioane de lei pentru evenimentele
              respective, sumă indicată în bugetul primăriei pentru anul 2023.
            </p>

            <p>
              „Nu este adevărat absolut deloc. Nu știu de unde ați luat această
              informație. […] Sala este achitată de colegii noștri, care ne-au
              întins o mână atunci când am început să discutăm cu Primăria
              Vienei și cu mulți alții de-ai casei, de aici, de la Viena. Deci,
              Primăria Chișinău, să fie foarte clar, atât în cazul acestui
              eveniment, cât și în cazul evenimentelor pe care le vom avea la
              Milano și la Barcelona, practic achită doar deplasarea
              artiștilor”, a comentat primarul.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0a00-0242-181f-08db8e6cf492_w1200.jpg"
              caption="Edilul Ion Ceban, alături de mai mulți moldoveni la concertul organizat de primăria Chișinău la Milano."
            />
            <p>
              Primarul nu a explicat clar nici cine a plătit pentru chiria
              sălii, localizată în unul dintre districtele centrale ale Vienei.
              Iar primăria nu a răspuns la solicitările repetate ale Europei
              Libere privind plățile făcute în 2023 pentru organizarea
              concertelor în diaspora.
            </p>

            <p>
              Pe de altă parte, într-un răspuns pentru Europa Liberă, Primăria
              Vienei a negat că ar fi achitat sala respectivă. Ambasada
              Republicii Moldova din Austria a punctat, de asemenea, că nu a
              fost implicată în organizarea evenimentului.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-3f7d-08db8e6e448b_w1200.jpg"
              caption="Mai mulți artiști, alături de Ion Ceban, pe scenă, la concertul din Viena, organizat de Primăria municipiului Chișinău."
            />
            <p>
              Evenimentul de la Viena a fost fotografiat cap-coadă de Style
              Arts, o companie din Austria specializată în producții foto și
              video, reprezentanții căreia au spus Europei Libere că ar fi făcut
              acest gest pentru autopromovare, gratuit.
            </p>

            <p>
              Totodată, la concertul din capitala Austriei au mers mai mulți
              funcționari din cadrul primăriei. Cel puțin patru dintre aceștia
              sunt și membri ai{' '}
              <a
                href="https://moldova.europalibera.org/a/partidul-prim%C4%83rie-sau-prim%C4%83ria-partid-cum-a-revenit-ion-ceban-%C3%AEn-politic%C4%83-%C3%AEmpreun%C4%83-cu-subalternii-s%C4%83i/32197374.html"
                target="_blank"
              >
                noului partid
              </a>{' '}
              condus de Ion Ceban, Mișcarea Alternativă Națională (MAN).
            </p>

            <p>
              Potrivit unor{' '}
              <a
                href="https://actelocale.gov.md/ral/search?q=Viena&cautare_text_metod=2"
                target="_blank"
              >
                dispoziții
              </a>
              , din delegația primăriei au făcut parte 10 funcționari, pentru
              care s-au achitat cheltuielile de transport, inclusiv aerian,
              diurnele prevăzute pentru Republica Austria și asigurarea medicală
              obligatorie de călătorie peste hotarele țării.
            </p>

            <p>
              Portalul achizițiilor publice arată că, în 2023, Primăria Chișinău
              a contractat servicii de transport aerian spre mai multe orașe
              europene, inclusiv Viena, Milano și Barcelona, în valoare totală
              de 1,3 milioane de lei. Contractul cu compania câștigătoare a
              licitației{' '}
              <a
                href="https://mtender.gov.md/tenders/ocds-b3wdp1-MD-1680871192581?tab=cans"
                target="_blank"
              >
                a fost încheiat pe 15 iunie 2023
              </a>
              .
            </p>

            <h3>Concertele din Rusia vs cele din UE</h3>
            <p>
              Primăria a planificat și în 2022 concerte asemănătoare, doar că la{' '}
              <a
                href="https://www.chisinau.md/files/decizia-nr-14_partea_a_patra.pdf"
                target="_blank"
              >
                Moscova și Sankt Petersburg
              </a>
              .
            </p>

            <p>
              „Nu știu de unde aveți astfel de informații. Noi nu am avut
              concerte la Sankt Petersburg sau la Moscova”, a spus Ceban Europei
              Libere, fără să explice însă de ce spectacolele planificate în
              Rusia n-au mai avut loc.
            </p>

            <p>
              Primarul a ținut să adauge că, de fapt, concertele din cele trei
              țări europene au loc drept urmare a seriei de evenimente sub
              același nume, care se organizează de 3 ani în mai multe orașe din
              România și că se desfășoară la solicitarea moldovenilor stabiliți
              în diaspora.
            </p>

            <ClickableImage
              src="https://gdb.rferl.org/01000000-0a00-0242-6b93-08db8e6ce11c_w1200.jpg"
              caption="Zilele Chișinăului la Barcelona."
            />
            <p>
              Pe de altă parte, co-organizatorul concertului „Zilele Chișinăului
              la Barcelona”, Igor Cereteu de la Asociația VIS, spune că
              inițiativa nu i-a aparținut lui, dar a fost contactat de Direcția
              Cultură a municipiului Chișinău pentru a se implica.
            </p>

            <p>
              „După cum am înțeles, se organizează astfel de evenimente și la
              Viena, și la Iași, și în Italia. Eu pot să răspund doar pentru cel
              de aici (la Barcelona, n.r.). Noi avem propriile resurse și nu
              avem nevoie de ajutoare. Scena, tot ceea ce ține de scenă, de
              sunet și așa mai departe… avem. Sunt de 20 de ani aici și cunosc
              suficienți oameni pentru ca să organizăm. […] Direcția Cultură
              (ne-a contactat, n.r.) pentru că avea deja evenimente planificate.
              Noi, cum mereu organizăm evenimente, am fost contactați și da, au
              întrebat dacă ne-ar interesa”, a precizat Cereteu.
            </p>

            <p>
              Primăria Chișinău nu a răspuns la solicitările repetate ale
              Europei Libere privind cheltuielile exacte pentru concertele
              organizate în diaspora.
            </p>
          </LongRead>
          <LongRead
            isFollowedByLongread
            menuTitle="Primesc donații de mii de euro. Cine sunt șefii municipali care trăiesc din generozitatea rudelor"
          >
            <h2>
              Primesc donații de mii de euro. Cine sunt șefii municipali care
              trăiesc din generozitatea rudelor
            </h2>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-71f1-08db91ada59f_w1200.png"
              caption="Colaj Europa Liberă. De la stânga la dreapta:Roman Vitiuc, Ion Pîntea, Olesea Pșenițchi și Andrei Ciochină."
            />
            <p>
              Mai mulți șefi de întreprinderi municipale și Direcții generale
              din cadrul Primăriei Chișinău trăiesc din generozitatea rudelor.
              În unele cazuri, valoarea donațiilor depășește suma salariilor
              funcționarilor. Cadourile bănești sunt făcute inclusiv de persoane
              fără venituri oficiale în R. Moldova.
            </p>
            <h3>Zeci de mii de euro pentru șeful Pieței Centrale</h3>
            <p>
              Ion Pîntea, șeful interimar al Întreprinderii Municipale „Piața
              Centrală”, se bucură de rude și prieteni generoși. În ultimii
              șapte ani, acesta a obținut donații de circa 2,2 milioane de lei
              (echivalentul a 80 de mii de euro), dar și un apartament.
            </p>

            <p>
              Pîntea a fost numit în funcția de director al Pieței Centrale în
              februarie 2020. Potrivit{' '}
              <a
                href="https://docs.google.com/document/d/124bkGAmk9uZVBSaHqQFRl0fu6elgwDN7/edit"
                target="_blank"
              >
                CV-ului
              </a>{' '}
              său, este membru al Partidului Socialiștilor din Republica Moldova
              (PSRM) din 2011. Tot în CV menționează că a absolvit Facultatea de
              Drept a Universității de Stat din Moldova în 2008.
            </p>

            <p>
              Activitatea profesională, funcționarul declară că a început-o în
              2016, la vârsta de 31 de ani. Atunci, Pîntea a fost numit
              asistentul deputatului socialist Corneliu Furculiță în Parlament.
              După alegerile parlamentare din februarie 2019, pleacă din
              funcție, dar este angajat la o firmă afiliată lui Corneliu
              Furculiță, în calitate de jurist-consultant. Deși a lucrat la
              Exclusiv Sale House SRL timp de opt luni, nu-și trece funcția în
              CV.
            </p>

            <p>
              În noiembrie 2019, Pîntea revine la Parlament, fiind angajat în
              calitate de consilier al Fracțiunii parlamentare PSRM, funcție pe
              care a deținut-o până a fost numit director interimar al Pieței
              Centrale de Ion Ceban, primarul Chișinăului susținut de PSRM la
              alegerile din 2019.
            </p>

            <p>
              La rândul său, Ion Pîntea a fost și donator.{' '}
              <a
                href="drive.google.com/file/d/1nk-DzmKBuJkzfIu9AnlgiOE81u7XHUUA/view?pli=1"
                target="_blank"
              >
                Informațiile disponibile
              </a>{' '}
              de la Comisia Electorală Centrală, pe care le-am analizat, arată
              că Pîntea a donat regulat în campaniile electorale ale PSRM din
              perioada 2017-2020. În total – 48 de mii de lei.
            </p>

            <p>
              Tot Pîntea a luat bani cu împrumut de la Exclusiv Media SRL, o
              altă companiei afiliată socialistului Furculiță. Suma - 500.000 de
              lei. RISE Moldova a dezvăluit, în ancheta{' '}
              <a
                href="www.rise.md/articol/banii-lui-dodon-din-bahamas/"
                target="_blank"
              >
                „Banii lui Dodon din Bahamas”
              </a>
              , că, la câteva luni înainte de prezidențialele din 2016,
              câștigate de liderul socialiștilor, Igor Dodon, Exclusiv Media a
              fost folosită într-o tranzacție financiară cu o firmă din Bahamas.
              Mai exact, milioane de lei primiți din off-shore au ajuns la
              sponsori ai partidului, în baza unor contracte de împrumut. „Eu nu
              știu de unde au venit sursele. Și nu știu dacă e oportun să discut
              careva chestii personale”, a răspuns Pîntea când l-am sunat să-l
              întrebăm de împrumut.
            </p>

            <p>
              Până a intrat sub aripa fracțiunii parlamentare a PSRM, Pîntea nu
              a avut tangență nici cu domeniul public, nici cu cel juridic.
              Datele obținute de noi arată că Ion Pîntea a fost angajat oficial,
              pentru prima dată, în august 2014 (la vârsta de 29 de ani), în
              calitate de manager în comerț la o firmă care prestează servicii
              IT, iar ulterior ca șofer în cadrul aceleiași companii.
            </p>

            <p>
              Din 2016, de când depune declarații de avere, Pîntea a primit
              donații regulat. În anul în care s-a angajat la Parlament, a
              primit{' '}
              <a
                href="public-pdf-declaratii.ani.md/pdf/get/4587/3"
                target="_blank"
              >
                opt transferuri din Israel și Federația Rusă
              </a>
              , în sumă de puțin peste 10 mii de dolari și patru mii de ruble,
              majoritatea fiind cu destinația „achitarea serviciilor comunale”.
              Următorul an a fost mai caritabil. Pîntea a primit peste jumătate
              de milion de lei, cei mai mulți bani venind de la mamă și sora sa
              mai mică. Ultima i-a donat aproape 16,5 mii de euro.
            </p>

            <p>
              În{' '}
              <a
                href="public-pdf-declaratii.ani.md/pdf/get/269936/3"
                target="_blank"
              >
                2018
              </a>
              , valoarea donațiilor a fost de peste 150 de mii de lei, iar în{' '}
              <a
                href="public-pdf-declaratii.ani.md/pdf/get/418272/3"
                target="_blank"
              >
                2019
              </a>
              , puțin peste 135 de mii de lei. Următorul an – 2020 – a fost cel
              mai modest din această perspectivă. Pîntea a declarat donații de{' '}
              <a
                href="public-pdf-declaratii.ani.md/pdf/get/534396/3"
                target="_blank"
              >
                66 de mii de lei
              </a>
              .
            </p>

            <p>
              În{' '}
              <a
                href="public-pdf-declaratii.ani.md/pdf/get/634669/3"
                target="_blank"
              >
                2021
              </a>{' '}
              funcționarul s-a căsătorit, iar evenimentul i-a adus tinerei
              familii peste 200 de mii de lei. Pe lângă darul de la nuntă, șeful
              de la Piața Centrală a primit, ca de obicei, donații. În total,
              aproape 300 de mii de lei.
            </p>

            <p>
              2022 a fost mai bun din perspectiva donațiilor. Pîntea a obținut
              aproape{' '}
              <a
                href="public-pdf-declaratii.ani.md/pdf/get/833394/3"
                target="_blank"
              >
                621 de mii de lei
              </a>
              .
            </p>
            <ClickableImage
              data-graphics
              src="https://gdb.rferl.org/01000000-0a00-0242-0065-08db92976e74_w1200.png"
              caption=""
            />
            <h3>Cine sunt donatorii șefului de la piață</h3>
            <p>
              Mama, sora, tatăl biologic și tatăl vitreg sunt cei care i-au
              donat cei mai mulți bani. Un donator fidel este și Alexandr
              Cojuhari care îi donează lui Pîntea, aproape în fiecare an, câte
              zece mii de ruble rusești.
            </p>

            <p>
              „Ăsta-i unchiul meu”, ne spune șeful de la Piața Centrală. Și
              fosta concubină a lui Cojuhari, Natalia, la fel donează bani lui
              Ion Pîntrea. Doar anul trecut i-a donat peste o mie de euro.
              Potrivit unei decizii a instanței de judecată, în 2010, Natalia
              l-a dat în judecată pe Alexandr Cojuhari, obligându-l să plătească
              pensie alimentară celor doi copii ai săi. „La noi relațiile sunt
              bune, pentru toată familia. Nu văd o problemă”, explică Ion
              Pîntea, cu referire la donațiile fostei concubine a unchiului său.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-da5e-08db91bc184e_w1200.jpg"
              caption="Ion Pîntea, șeful interimar al Întreprinderii Municipale „Piața Centrală”"
            />
            <p>
              În discuția cu reporterul, administratorul Pieței Centrale a ținut
              să menționeze că nu a admis niciodată acte de corupție: „Permanent
              am avut relații de muncă conforme. […] Ceea ce ține de alte
              activități, eu alte întreprinderi sau afaceri nu am. Ceea ce avem,
              valorificăm veniturile din salarii și dacă rudele s-au expus, au
              vrut să doneze o sumă oarecare, este dreptul lor, este dreptul meu
              să beneficiez de aceste donații. N-am încălcat niciodată legea”.
            </p>

            <p>
              Apartamentul cumpărat de soră, dat la schimb unei pensionare,
              primit donație de Ion Pîntea
            </p>

            <p>
              În martie 2019, Pîntea s-a ales cu încă o donație – un apartament.
              Mai exact este vorba despre o garsonieră de 33 de metri pătrați
              situată în sectorul Rîșcani al capitalei. L-a primit de la Natalia
              Cușnir, o femeie în vârstă de 66 de ani, cu condiția că Pîntea o
              va întreține pe viață.
            </p>

            <p>
              Istoricul imobilului arată că locuința fusese cumpărată de Clavdia
              Pîntea, sora lui Ion Pîntea, în aprilie 2018. Ulterior, printr-un
              contract de schimb, apartamentul a ajuns de la Clavdia Pîntea la
              Natalia Cușnir. În schimbul apartamentului de 33 de metri pătrați,
              pensionara îi dădea Clavdiei un apartament la sol, de trei ori mai
              mare.
            </p>

            <p>
              Ion Pîntea susține că pensionara este o „prietenă de familie” și
              s-a arătat deranjat de întrebările pe care i le adresăm: „Eu nu
              văd necesitatea de a vă reflecta îndeobște chestii personale. Ceea
              ce ține de relațiile contractuale mai ales. Eu nu sunt funcționar
              public, nu sunt în serviciu public, noi nu prestăm servicii
              publice. Eu sunt administrator de întreprindere municipală. Asta e
              tot agent economic care desfășoară activitate în domeniul privat”.
            </p>

            <p>
              În 2022, Ion Pîntea și soția sa au cumpărat un apartament de
              aproape 60 de metri pătrați, tot în sectorul Rîșcani. Potrivit
              declarației de avere, imobilul a costat în jur de 45 de mii de
              euro, pentru care familia s-a împrumutat la bancă.
            </p>

            <h3>Donatorii șefului de la Construcții capitale</h3>
            <p>
              Donații a primit și Andrei Ciochină, șeful interimar al Î.M.
              „Direcția construcții capitale”. Întreprinderea pe care o
              administrează Ciochină se ocupă de gestionarea bugetelor publice
              destinate pentru construcţia, reparaţia locuinţelor, drumurilor și
              trecerilor subterane din municipiul Chișinău.
            </p>

            <p>
              Ciochină e șef la Construcții capitale de la începutul anului
              curent. Anterior a lucrat tot la stat. Și-a început activitatea în
              2008, la Ministerul Finanțelor, în calitate de specialist, iar la
              sfârșitul anului 2010 s-a angajat la Inspecția Financiară, unde a
              fost inspector superior. În ultimii cinci ani a activat ca șef
              adjunct de direcție la Inspecția Financiară.
            </p>

            <p>
              Andrei Ciochină depune declarații de avere începând cu anul 2012
              și declară venituri modeste. De exemplu, în 2015, când și-a
              procurat un automobil Audi A6 (produs în 1999), familia Ciochină a
              avut un venit de puțin peste 84 de mii de lei. A declarat că
              automobilul l-a costat 20 de mii de lei (circa 1.000 de euro).
              Site-urile de vânzări arată că astăzi (la distanță de șapte ani),
              prețul unui asemenea automobil variază între 1.800 și 4.800 de
              euro.
            </p>

            <p>
              În 2017 își vinde automobilul mai ieftin decât l-a cumpărat – cu
              15 mii de lei – și, din acești bani, își cumpără unul mai nou. E
              vorba de un Nissan X-trail din 2006. La șase ani diferență, am
              căutat pe piață un astfel de model la un preț mai mic de o mie de
              euro, însă pe site-urile de specialitate, un asemenea model din
              2006 costă de la 4.900 de euro și ajunge până la 6.900 de euro.
              Chiar și așa, în declarația de avere depusă pentru 2022, Ciochină
              declară că și-a vândut Nissanul cu același preț cu care l-a
              cumpărat – 15 mii de lei.
            </p>

            <p>
              Familia nu deține nici un bun în proprietate, Ciochină declarând
              că locuiesc într-un apartament de 82 de metri pătrați care nu le
              aparține.
            </p>
            <h3>Mama și soacra, donatoare fidele</h3>
            <p>
              Din 2020, familia Ciochină, care are în prezent patru copii,
              declară anual donații. Potrivit declarației de avere depuse pentru{' '}
              <a
                href="https://public-pdf-declaratii.ani.md/pdf/get/559955/3"
                target="_blank"
              >
                2020
              </a>
              , soții Ciochină au primit o donație de șase mii de euro de la
              mama soției. În{' '}
              <a
                href="https://public-pdf-declaratii.ani.md/pdf/get/695284/3"
                target="_blank"
              >
                următorul an
              </a>
              , mama lui Ciochină le donează 1.500 de euro, iar soacra – 2.900
              de euro. Și{' '}
              <a
                href="https://public-pdf-declaratii.ani.md/pdf/get/760880/3"
                target="_blank"
              >
                anul trecut
              </a>{' '}
              mama lui Andrei Ciochină i-a donat 1.500 de euro.
            </p>

            <p>
              „Soacra mea lucrează în Germania la un azil de bătrâni, este
              angajată oficial, este cetățeană a Uniunii Europene și primește
              salariu muncind, având grijă de bătrâni. Și mama, de asemenea, are
              grijă de bătrâni, tot este angajată oficial, lucrează și ne ajută
              cu ce poate […]. Mama e de peste 20 de ani în Grecia, lucrează,
              soacra de vreo zece [ani este peste hotare – n.r.]”, a spus șeful
              de la Construcții.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-ac80-08db91bc42e9_r5_w900.png"
              caption="Andrei Ciochină, șeful interimar al Î.M. „Direcția construcții capitale”"
            />
            <p>
              Tot anul trecut, familia a primit 4.000 de dolari de la Octavian
              Caragia. Ciochină ne-a spus că e verișorul său, care muncește în
              America, iar suma de 4000 de euro a împărțit-o cu familia vărului
              aflată în Moldova.
            </p>

            <p>
              „Noi suntem verișori drepți. El lucrează de mai mulți ani în
              America. Respectiv, făcând remitențe soției, Western Union i-a
              blocat conturile și nu mai poate să primească suma de bani
              necesară pentru a se întreține cu copilul aici. Prin intermediul
              meu își achită, mă rog, o mai ajută, își primește banii. […] Din
              4000, o parte din bani, nu mai țin minte, au fost pentru noi, și o
              parte din bani i-am împărțit ei [soției verișorului – n.r.]”, a
              explicat Andrei Ciochină.
            </p>

            <p>
              În 2022, grosul banilor în familia Ciochină a venit din
              indemnizațiile de creștere a copiilor – în total, circa 215 mii de
              lei. Andrei Ciochină declară și un salariu pe care l-a ridicat de
              la Inspecția Financiară – aproape 113 mii de lei, ceea ce înseamnă
              un venit lunar de 9.400 de lei. Mutarea la Direcția construcții
              capitale i-a adus și un salariu mai bun. Ciochină ridică lunar un
              salariu net de circa 24 de mii de lei.
            </p>
            <ClickableImage
              data-graphics
              src="https://gdb.rferl.org/01000000-0aff-0242-f1d9-08db92976e7b_w1200.png"
              caption=""
            />
            <h3>Firma soției</h3>
            <p>
              Soția lui Ciochină, Victoria, a lucrat mai mulți ani în sistemul
              bancar. Din august 2020 nu este angajată, însă tot atunci și-a
              deschis o firmă.{' '}
              <a
                href="https://drive.google.com/file/d/1GU_1yVM5u1HhDyTAYEs-moNUrdrtXc7U/view?usp=drive_link"
                target="_blank"
              >
                Andevi Stil Grup SRL
              </a>{' '}
              se numește compania și se ocupă de comerțul cu amănuntul al
              textilelor, îmbrăcămintei și încălțămintei. Firma administrează un
              butic în Centrul comercial UNIC, pentru care a primit autorizație
              de comerț de la Primăria Chișinău la o lună de la fondare. Deși
              este administratoare, Victoria Lozovan nu declară venituri.
            </p>

            <p>
              Potrivit legii, Andrei Ciochină este obligat să declare dacă el
              sau un alt membru al familiei sale dețin acțiuni în capitalul
              social al unui agent economic, lucru pe care nu l-a făcut însă.
              Doar la capitolul „Interese personale”, Ciochină a indicat că
              soția sa are calitate de fondator al companiei Andevi Stil Grup
              SRL.
            </p>

            <p>
              Datele Biroului Național de Statistică arată că în 2021 firma a
              avut venituri din vânzări în valoare de aproape 290 de mii de lei,
              însă se află în pierdere de peste 100 de mii de lei.
            </p>

            <h3>Șefa de la Finanțe, cu bani de la tata</h3>
            <p>
              Olesea Pșenițchi este șefa Direcției generale finanțe din cadrul
              Consiliului municipal Chișinău. Și-a început activitatea
              profesională în 2013, la Ministerul Finanțelor. În 2017 s-a
              angajat la Direcția finanțe, fiind numită șefă adjunctă, iar în
              2020 a ajuns să dețină interimatul funcție de directoare, până în
              2022, când a fost numită șefă a Direcției.
            </p>
            <p>
              Și Pșenițchi declară donații. Femeia, în vârstă de 35 de ani, a
              primit{' '}
              <a
                href="https://public-pdf-declaratii.ani.md/pdf/get/716345/3"
                target="_blank"
              >
                18 mii de euro
              </a>{' '}
              în 2021. Donator este tatăl său, Vasilii Pșenițchi, în vârstă de
              64 de ani. În 2022,{' '}
              <a
                href="https://public-pdf-declaratii.ani.md/pdf/get/828191/3"
                target="_blank"
              >
                tatăl i-a donat
              </a>{' '}
              încă 10 mii de euro, dar și două terenuri agricole în satul
              Bravicea, raionul Călărași.
            </p>
            <ClickableImage
              data-graphics
              src="https://gdb.rferl.org/01000000-0aff-0242-fa90-08db92976e69_w1200.png"
              caption=""
            />
            <p>
              Și veniturile funcționarei au crescut în ultimii ani. De exemplu,
              în 2017, când s-a angajat la Direcție, Olesea Pșenițchi a avut un
              venit din salariu de 89 de mii de lei. Tot atunci a făcut un
              împrumut de 350 de mii de lei la bancă, pentru a-și procura un
              apartament de 44 de metri pătrați în sectorul Rîșcani al
              capitalei. Potrivit declarației de avere depuse pentru 2017,
              creditul a fost contractat pe 20 de ani, însă doi ani mai târziu,
              începând cu declarația de avere din 2019, împrumutul dispare.
            </p>

            <p>
              În 2020, veniturile anuale au ajuns la 245 de mii de lei. Atunci
              și-a cumpărat un automobil de model Renault Captur din 2013.
              Prețul declarat – 30 de mii de lei (echivalentul a 1.500 de euro).
              Potrivit site-urilor de profil, astăzi prețul de piață al unui
              asemenea automobil variază între nouă și zece mii de euro, aproape
              cât tot venitul lui Pșenițchi pentru anul 2020. În următorul an,
              își vinde mașina cu același preț cu care a cumpărat-o și își
              cumpăra alta – un Hyundai Tucson din 2018, pentru care declară că
              a plătit 330 de mii de lei.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-9119-08db91bc42e9_w1200.png"
              caption="Olesea Pșenițchi este șefa Direcției generale finanțe din cadrul Consiliului municipal Chișinău"
            />
            <p>
              În 2022, Pșenițchi a ridicat un salariu de peste 337 de mii de
              lei, dar și indemnizații de aproape 140 de mii de lei, fiind
              membră a consiliului de administrație și a comisiei de cenzori de
              la patru dintre întreprinderile municipale – Piața Centrală,
              Parcul Urban de Autobuze, Infocom și Regia Autosalubritate.
            </p>

            <p>
              Tot anul trecut, șefa de la Finanțe a devenit membră a comisiei de
              cenzori a Partidului Mișcarea Alternativă Națională (MAN), fondat
              și condus de primarul capitalei, Ion Ceban.
            </p>

            <p>
              Am contactat-o pe Pșenițchi pe numărul său de la birou, însă ne-a
              răspuns adjuncta sa, spunându-ne că șefa de la Finanțe este în
              concediu și ne-a cerut să revenim peste o săptămână. Așa că i-am
              trimis Olesei Pșenițchi întrebările pe adresa de e-mail, cu
              speranța să aflăm cu ce se ocupă tatăl său de reușește să îi
              doneze anual sume mari de bani, ce s-a întâmplat cu creditul
              contractat pe 20 de ani care a dispărut din declarația sa de
              avere, dar și cum a reușit să-și procure un automobil la preț de
              30 de mii de lei. Până la publicarea anchetei, șefa de la Finanțe
              nu a revenit cu un răspuns.
            </p>

            <p>
              Femeile donatoare din viața șefului de la Economie Roman Vitiuc
              este șeful Direcţiei generale economie, comerț și turism. Și el
              trăiește din donațiile membrilor familiei.
            </p>

            <p>
              Și-a început activitatea în administrația publică pe când avea 20
              de ani. Atunci, în 2003, Vitiuc a fost angajat în calitate de
              contabil la Primăria orașului Durlești, fiind în subordinea mamei
              sale, care, tot atunci, a fost angajată în funcția de
              contabilă-șefă. În 2008 face mutarea la Direcția Finanțe, unde
              este angajat pe poziția de specialist.
            </p>

            <p>
              Doi ani mai târziu, este numit consultant în cadrul Primăriei
              Chișinău. Pe această poziție a lucrat patru luni, după care devine
              șef de secție, apoi de sector, iar în 2018 e numit șef al
              Direcției audit intern tot în cadrul Primăriei Capitalei. În 2020,
              Vitiuc e numit șeful Direcţiei generale economie, comerț și turism
              pe care o conduce până în prezent.
            </p>
            <p>
              În ceea ce privește donațiile primite de familia Vitiuc, șeful de
              Direcție le declară drept remitențe, adică transferuri bănești de
              peste hotare. În declarația pentru anul 2015, Roman Vitiuc a
              indicat că a primit de peste hotare patru mii de dolari.
              Următoarele transferuri au avut loc în 2021. Este vorba despre
              3.800 de dolari primiți de la trei femei, dintre care mama și sora
              sa, aflate în Statele Unite ale Americii.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-3424-08db91acb4d1_w1200.jpg"
              caption="Roman Vitiuc (dreapta), mama sa (centru) și sora (stânga)."
            />
            <p>
              În 2022, cele două femei i-au transferat lui Vitiuc încă 10 mii de
              dolari, iar alți 1.800 de dolari i-au fost transmiși prin Paypal
              de George Myers. Tot în 2022, familia a obținut 40 de mii de lei
              la evenimente de familie, iar 156 de mii de lei au fost primiți de
              soția lui Vitiuc, Irina, drept moștenire. Anul trecut, veniturile
              din salarii și indemnizații s-au ridicat la peste 530 de mii de
              lei.
            </p>

            <p>
              „Sora mea este cetățean al SUA, care pentru o perioadă mai mare de
              10 ani locuiește și lucrează în SUA. La momentul actual ea este
              angajată a unei companii care se ocupă de marketing-ul pieței
              imobiliare și vânzarea imobilelor. […] Mama mea este rezident al
              SUA, care pe parcursul mai multor ani se află în SUA și practică
              activitatea de muncă independentă”, a răspuns Vitiuc, care susține
              că donațiile primite au fost un suport pentru a putea accesa un
              credit ipotecar, dar și pentru că i s-a născut cel de-al doilea
              copil.
            </p>
            <ClickableImage
              data-graphics
              src="https://gdb.rferl.org/01000000-0a00-0242-faf1-08db92976e67_w1200.png"
              caption=""
            />
            <p>
              Familia Vitiuc declară patru apartamente, dintre care două sunt
              deținute împreună cu alte rude. În 2017, soții Vitiuc și-au
              cumpărat un apartament de aproape 42 de metri pătrați. Anul trecut
              au mai procurat unul. Este vorba despre un apartament vecin celui
              pe care îl dețineau. Noul imobil are 61,2 metri pătrați și a
              costat 1,7 milioane de lei. Familia a împrumutat o parte din bani
              de la rude și de la bancă.
            </p>

            <p>
              Vitiuc conduce o Skoda Superb din 2008, în proprietatea căreia a
              intrat în 2013. El declară că automobilul valorează 15 mii de lei
              (circa 750 de euro). Pe site-urile de vânzări, astăzi, o Skoda
              Superb din 2008 costă începând cu șase mii de euro și poate depăși
              suma de 10 mii de euro.
            </p>

            <p>
              „A fost obținut prin schimb în locul autoturismului deținut
              anterior”, susține Roman Vitiuc, menționând că legea prevede: „la
              compartimentul Valoarea bunului urma a fi indicată valoarea
              menționată în documentul care certifică proveniența bunului
              mobil”, adică prețul indicat în contractul de vânzare sau de
              schimb.
            </p>

            <h3>„Apare riscul unor plăți neoficiale, deghizate în donații”</h3>
            <p>
              În Republica Moldova donațiile primite nu sunt supuse impozitării.
              Astfel, persoana care primește o sumă de bani drept donație, nu
              este obligată să o declare la Serviciul Fiscal de Stat (SFS). O
              donație bănească nu se autentifică la notar și nu este supusă
              vreunui raport contractual, e un gest între cel care donează și
              cel care primește suma.
            </p>

            <p>
              Potrivit SFS, sunt obligați să declare că au făcut o donație doar
              cei care au donat mai mult decât au câștigat oficial în anul în
              care au oferit darul bănesc și, respectiv, să plătească impozitul
              de 12% din diferența donată.
            </p>

            <p>
              De exemplu, dacă o persoană a avut un venit oficial de 100 de mii
              de lei și a donat unui prieten 150 de mii, este obligată să
              declare la SFS cei 50 de mii de lei și să plătească impozitul.
              Această regulă nu se aplică însă și donatorilor care-s rude de
              gradul I, cum ar fi părinții, frații și surorile, copiii sau soțul
              și soția.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/8776C779-E078-48AE-B38F-2FC0F3280370_r5_w1200.jpg"
              caption="Ianina Spinei, expertă anticorupție de la Transparency International Moldova"
            />
            <p>
              Doar cei obligați de lege să depună declarații de avere indică, în
              documentele depuse la ANI, donațiile primite, însă nu sunt
              obligați să demonstreze, prin acte suplimentare, că donația chiar
              a avut loc sau că donatorul a dispus de acele mijloace financiare,
              decât în cazul unui control inițiat de Autoritate.
            </p>

            <p>
              Potrivit ANI, în procesul de control al averii și intereselor
              personale au fost identificate două cazuri în care persoanele
              obligate să depună declarații de avere au indicat donații, însă
              inspectorii au constatat că donatorii fie nu au prezentat acte
              care ar demonstra că ar fi avut acei bani, fie nu dispuneau de
              mijloace financiare suficiente pentru acordarea donațiilor.
            </p>

            <p>
              Donațiile angajaților din domeniul public au fost subiectul mai
              multor materiale de presă, dar și rapoarte făcute de organizații
              internaționale. Experta anticorupție de la Transparency
              International Moldova, Ianina Spinei, a analizat și publicat un{' '}
              <a
                href="https://www.transparency.md/wp-content/uploads/2022/05/TI_Moldova_Raport_analitic_declararea_veniturilor_%C5%9Fi_averilor_procurorilor_din_Republica_Moldova_2022.pdf"
                target="_blank"
              >
                Raport privind averile și veniturile procurorilor
              </a>{' '}
              și a constatat că „donațiile au devenit un fenomen care se
              perpetuează din an în an. O treime din procurorii care au indicau
              donații, aveau donații de peste 100 de mii de lei anual”.
            </p>

            <p>
              Fenomenul donațiilor generoase, spune Ianina Spinei, „nu poate să
              nu trezească semne de întrebare, fiindcă apare riscul unor plăți
              neoficiale care se deghizează sub forma unor donații, unor
              transferuri consistente sau a unor împrumuturi primite de la
              persoane fizice. Organele abilitate – FISC-ul [n.r. Serviciul
              Fiscal de Stat], Procuratura, ANI-ul, trebuie să coopereze
              eficient pentru a identifica inclusiv capacitatea acestor donatori
              – persoanele care transferă aceste mijloace din străinătate”.
            </p>

            <p>
              Serviciul Fiscal de Stat susține că în 2022 a identificat 445 de
              persoane care au efectuat donații ce au depășit suma venitului și
              nu au declarat acest fapt la organul fiscal. Acestor persoane li
              s-a solicitat să declare voluntar suma și să plătească impozitul
              pe venit.
            </p>

            <p>Autor: Marcela ZĂMOSTEANU</p>
            <p>
              Fact-checking pentru acest text a fost efectuat de către
              Departamentul de Fact-checking al RISE Moldova.
            </p>
          </LongRead>
          <LongRead
            isFollowedByLongread
            menuTitle="Candidatul PAS la șefia capitalei, Lilian Carp: interzis în Rusia, membru al unui partid din România și coaliția cu PDM din trecut"
          >
            <h2>
              Candidatul PAS la șefia capitalei, Lilian Carp: interzis în Rusia,
              membru al unui partid din România și coaliția cu PDM din trecut
            </h2>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-9635-08dbb9053d4e_w1200.jpg"
              caption="Lilian Carp candidează la funcția de primar al capitalei din partea partidului de guvernământ, PAS"
            />
            <p>
              Membru a două partide, PAS în R. Moldova și USR în România, Lilian
              Carp se declară unionist convins. Fost membru al Partidului
              Liberal, acesta este cosemnatarul unor proiecte de legi
              controversate în perioada alianței PL-PDM.
            </p>
            <p>
              La sfârșitul anului trecut, candidatul din partea PAS la șefia
              primăriei, Lilian Carp, a devenit și membru al Uniunii Salvați
              România (USR). Acesta este al treilea cel mai mare partid din
              Parlamentul României și este condus de Cătălin Drulă. Carp a
              menționat că legea permite să fie membru al unui partid în R.
              Moldova și, totodată, al unui partid din România.
            </p>
            <p>
              În conferința din noiembrie 2022, în care a făcut anunțul că
              devine membru al USR, un partid de centru-dreapta, Carp a spus că{' '}
              <a
                href="https://newsmaker.md/ro/video-deputatul-lilian-carp-a-aderat-la-usr-romania-in-moldova-sunt-membru-pas-legea-nu-imi-interzice/"
                target="_blank"
              >
                „R. Moldova și România sunt două state și o singură țară”
              </a>
              , iar astfel și-a dorit să întărească legăturile dintre ele. Carp
              mai consideră că apropierea dintre Moldova și România se va
              produce prin integrarea în UE.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-4f43-08dbb9070aa2_w1200.jpg"
              caption="Lilian Carp în vizită la primarul Municipiului București, Nicușor Dan, susținut inclusiv de USR, iulie 2023"
            />
            <p>
              Lilian Carp are 45 de ani și este la al treilea mandat de deputat
              în Parlamentul R. Moldova. Este unionist, opinie pe care și-a
              păstrat-o de-a lungul activității sale parlamentare. Este co-autor
              al unui{' '}
              <a
                href="https://www.parlament.md/ProcesulLegislativ/Proiectedeactelegislative/tabid/61/LegislativId/4128/language/ro-RO/Default.aspx"
                target="_blank"
              >
                proiect de lege
              </a>{' '}
              privind „Declarația de Unire” a R. Moldova cu România din 2018,
              ulterior declarat nul.
            </p>
            <p>
              A început să fie cunoscut pentru activitatea sa politică atunci
              când era în PL. În 2009-2014 a fost șef al Cabinetului primarului
              general al municipiului Chișinău, Dorin Chirtoacă. În perioada
              2012-2014 a fost și membru al Consiliului de Administrare al
              Întreprinderii Municipale Apă Canal.
            </p>
            <p>
              În 2014, Lilian Carp devine deputat pe lista Partidului Liberal.
              În Parlament, în 2015, alături de alți colegi liberali, a semnat{' '}
              <a
                href="https://www.parlament.md/ProcesulLegislativ/Proiectedeactelegislative/tabid/61/LegislativId/2656/language/ro-RO/Default.aspx"
                target="_blank"
              >
                proiectul hotărârii
              </a>{' '}
              privind constituirea comisiei de anchetă care să stabilească
              legalitatea actelor de studii ale prim-ministrului de atunci,
              Chiril Gaburici. La o săptămână după ce presupusa{' '}
              <a
                href="https://moldova.europalibera.org/a/27068386.html"
                target="_blank"
              >
                diplomă falsă
              </a>{' '}
              intrase în vizorul procurorilor, Gaburici pleca în demisie.
            </p>
            <p>
              În 2016, tot alături de PL, a semnat un{' '}
              <a
                href="https://www.parlament.md/ProcesulLegislativ/Proiectedeactelegislative/tabid/61/LegislativId/3554/language/ro-RO/Default.aspx"
                target="_blank"
              >
                proiect
              </a>{' '}
              privind constituirea unei comisii de anchetă pentru verificarea
              legalității declarațiilor cu caracter antistatal, xenofob și
              distructiv făcute la adresa R. Moldova, cât și a statelor vecine
              de către Igor Dodon, pe atunci proaspăt ales președinte. Proiectul
              n-a mai ajuns să fie votat în lectură finală.
            </p>
            <h3>
              Coaliția cu PDM și semnături alături de Candu, Sîrbu și Țuțu
            </h3>
            <p>
              În 2016, Lilian Carp, alături de mai mulți deputați din PDM,
              printre care Adrian Candu, Sergiu Sîrbu sau Constantin Țuțu, dar
              și deputați PL, care formaseră alianță cu democrații, a semnat un
              controversat{' '}
              <a
                href="https://www.parlament.md/ProcesulLegislativ/Proiectedeactelegislative/tabid/61/LegislativId/3196/language/ro-RO/Default.aspx"
                target="_blank"
              >
                proiect de lege
              </a>{' '}
              privind modificarea Constituției, care ar fi dus la eliminarea
              imunității parlamentare.
            </p>
            <p>
              Opoziția a criticat proiectul ajuns să fie votat în primă lectură,
              invocând creșterea dosarelor selective pe motive politice în acel
              an, precum și ingerința din justiție. Ulterior, proiectul
              respectiv a fost retras.
            </p>
            <p>
              De asemenea, lui Carp i s-a reproșat în mai multe rânduri faptul
              că în 2015 a semnat pentru numirea lui Vladimir Plahotniuc în
              funcția de prim-ministru al R. Moldova. La rândul său, acesta a
              spus că a fost vorba de o decizie colectivă a partidului din care
              făcea parte și din care, mai apoi, a plecat.
            </p>
            <p>
              În aprilie 2017, când a părăsit majoritatea parlamentară formată
              din democrați și liberali, Carp a{' '}
              <a
                href="https://www.zdg.md/stiri/politic/liberalul-lilian-carp-a-parasit-majoritatea-parlamentara/"
                target="_blank"
              >
                invocat
              </a>{' '}
              că alianța dintre cele două partide avea, de fapt, scopul ca PDM
              să lichideze PL. La timpul respectiv, Carp{' '}
              <a
                href="https://www.jurnal.md/ro/politic/2017/4/28/un-deputat-pl-paraseste-majoritatea-parlamentara-suntem-in-masina-de-tocat-a-lui-plahotniuc/"
                target="_blank"
              >
                critica
              </a>{' '}
              și Partidul Popular European, în frunte cu Iurie Leancă, precum și
              pe Igor Dodon, pentru că ar fi șantajabili, iar prin asta, comozi
              pentru democrați în frunte cu Plahotniuc.
            </p>
            <h3>Membru al Blocului ACUM și PAS</h3>
            <p>
              În 2019, Carp se alătură blocului ACUM, format din Platforma DA și
              PAS, o alianță politică declarată antioligarhică și pro-europeană.
              După alegerile parlamentare din acel an, ACUM ajungea în
              legislativ cu{' '}
              <a
                href="https://alegeri.md/w/Blocul_electoral_%E2%80%9CACUM_Platforma_DA_%C8%99i_PAS%E2%80%9D_la_alegerile_parlamentare_din_2019"
                target="_blank"
              >
                26 de deputați
              </a>
              .
            </p>
            <p>
              În cadrul acelui mandat, Carp a participat la propunerea creării
              unei comisii parlamentare care să investigheze{' '}
              <a
                href="https://www.parlament.md/ProcesulLegislativ/Proiectedeactelegislative/tabid/61/LegislativId/4563/language/ro-RO/Default.aspx"
                target="_blank"
              >
                furtul miliardului
              </a>{' '}
              din sistemul bancar, dar și la recunoașterea de către parlament a{' '}
              <a
                href="https://www.parlament.md/ProcesulLegislativ/Proiectedeactelegislative/tabid/61/LegislativId/4567/language/ro-RO/Default.aspx"
                target="_blank"
              >
                caracterului captiv
              </a>{' '}
              al statului R. Moldova.
            </p>
            <p>
              Ulterior, parte a partidului PAS, Lilian Carp a criticat, în mai
              multe rânduri, PSRM sau Partidul Șor, ajungându-se până la{' '}
              <a
                href="https://www.zdg.md/stiri/politic/video-altercatii-intre-deputatii-lilian-carp-si-grigore-novac-la-editura-universul-unde-sunt-tiparite-ziarele-electorale-ale-lui-igor-dodon/"
                target="_blank"
              >
                altercații
              </a>{' '}
              sau{' '}
              <a
                href="https://tvrmoldova.md/ro/politic/marina-tauber-a-depus-plangere-la-politie-pe-numele-lui-lilian-carp-pentru-limbaj-necorespunzator-si-calomnii"
                target="_blank"
              >
                plângeri la Poliție
              </a>{' '}
              pentru denigrare.
            </p>
            <p>
              Înainte de alegerile prezidențiale din 2020, împreună cu Iurie
              Reniță, Lilian Carp a prezentat{' '}
              <a
                href="https://protv.md/actualitate/ultima-ora-imagini-noi-in-care-apar-igor-dodon-si-vlad-plahotniuc-presedintele-primeste-o-punga-in-care-ar-fi-bani-de-la-fostul-lider-pd-video---2528744.html"
                target="_blank"
              >
                imagini video
              </a>{' '}
              din 2019, de la o întâlnire a lui Igor Dodon cu Vlad Plahotniuc și
              Serghei Iaralov, în care apare și o sacoșă care ar fi fost plină
              cu bani. În acele{' '}
              <a
                href="https://newsmaker.md/ro/video-alte-secvente-video-cu-presedintele-igor-dodon-si-vladimir-plahotniuc-publicate-de-iurie-renita/"
                target="_blank"
              >
                videouri
              </a>{' '}
              se poate observa cum lui Dodon îi este oferit un pachet negru, pe
              care nu-l ia, dar spune să fie transmis unei persoane pe nume
              Cornel, ca să „plătească salariile”. Imaginile au devenit virale
              și au dat denumirea generică acestui dosar penal - „kuliok”, adică
              pachet din limba rusă. Ulterior, autoritățile au pornit o
              cercetare în baza imaginilor respective.
            </p>
            <h3>Interzis în Rusia</h3>
            <p>
              În luna aprilie a acestui an, Lilian Carp a fost inclus pe{' '}
              <a
                href="https://newsmaker.md/ro/video-minunata-lista-carp-a-comentat-decizia-rusiei-de-a-i-interzice-intrarea-in-tara/"
                target="_blank"
              >
                lista cu oficiali
              </a>{' '}
              din R. Moldova cărora le este interzis accesul în Rusia. Măsura a
              venit ca urmare a unor declarații „anti-ruse” de la Chișinău,
              potrivit Moscovei.
            </p>

            <p>
              În opinia sa, el a fost inclus pe lista respectivă pentru că este
              unul dintre semnatarii{' '}
              <a
                href="https://moldova.europalibera.org/a/separatismul-va-fi-pedepsit-penal-parlamentul-a-dat-votul-final-/32252312.html"
                target="_blank"
              >
                legii
              </a>{' '}
              prin care s-au introdus modificări la Codul Penal privind
              acțiunile de separatism. Liderii regiunii transnistrene, susținuți
              de Rusia, au criticat noile prevederi.
            </p>

            <p>
              În calitatea sa de președinte al Comisiei parlamentare pentru
              securitate națională, apărare și ordine publică, Carp a criticat
              Rusia pentru invaza la scară largă a Ucrainei.
            </p>

            <p>
              „Am condamnat și voi condamna în continuare acțiunile de terorism
              pe care le face Federația Rusă pe teritoriul Ucrainei”, a spus
              Carp după ce a primit interdicția de a intra pe teritoriul Rusiei,
              numind regimul lui Putin un{' '}
              <a
                href="https://www.facebook.com/liliancarp.21/videos/908352367284348/"
                target="_blank"
              >
                „regim criminal”
              </a>
              .
            </p>

            <p>
              Parlamentarul Lilian Carp este și autorul{' '}
              <a href="https://moldova.europalibera.org/a/lilian-carp-interzicerea-simbolurilor-r%C4%83zboiului-intr%C4%83-%C3%AEn-vigoare-%C3%AEn-cel-mult-dou%C4%83-s%C4%83pt%C4%83m%C3%A2ni/31796844.html?nocache=1">
                {' '}
                inițiativei
              </a>{' '}
              de a interzice folosirea „panglicii Sfântului Gheorghe” și altor
              simboluri care ar promova agresiunea rusească în Ucraina.
            </p>

            <ClickableImage
              src="https://gdb.rferl.org/02e70000-0aff-0242-b299-08da2dc3bfaf_w1200.jpg"
              caption="Deputatul PAS Lilian Carp și președintele Parlamentului R. Moldova, Igor Grosu, în vizită la Bucea, în Ucraina, 4 mai 2022"
            />

            <h3>
              Interesele și veniturile declarate ale candidatului PAS la șefia
              capitalei
            </h3>
            <p>
              <a
                href="https://www.parlament.md/StructuraParlamentului/Deputa%C8%9Bii/tabid/87/Id/540/language/fr-FR/Default.aspx"
                target="_blank"
              >
                Carp
              </a>{' '}
              este magistru în științe istorice și a făcut studii de doctorat la
              Universitatea Pedagogică de Stat „Ion Creangă”. Și-a început
              activitatea ca profesor de istorie, iar din 2002 este lector
              universitar la Universitatea de Stat de Educația Fizică și Sport.
              În perioada 2013 - 2019 a fost președinte al Federației
              Moldovenești de Polo pe apă.
            </p>
            <p>
              În 2021, Autoritatea Națională de Integritate (ANI) a fost
              sesizată cu privire la faptul că Lilian Carp ar fi admis{' '}
              <a
                href="https://ani.md/sites/default/files/2021-12/Lilian%20Carp.PDF"
                target="_blank"
              >
                încălcarea
              </a>{' '}
              regimului juridic al incompatibilităților prin exercitarea
              simultană a funcției de deputat în Parlamentul R. Moldova și a
              celei de președinte al Federației de Polo pe apă.
            </p>
            <p>
              Inspectorul de integritate a fost informat însă că pe durata
              activității sale la federația respectivă, Carp nu a fost
              remunerat, întrucât funcția sa ar fi una obștească și nu prevede
              profit financiar și salarizare. Respectiv, concluzia ANI a fost că
              Lilian Carp nu a încălcat regimul incompatibilităților.
            </p>
            <p>
              În declarația sa de avere pentru anul trecut, Lilian Carp indică
              venituri de aproape 250.000 de lei pentru activitatea din
              Parlament, precum și aproape 80.000 de lei în calitate de profesor
              la Universitatea de Stat de Educație Fizică și Sport. În cadrul
              aceleiași instituții de învățământ a lucrat și soția sa, care
              pentru anul trecut a primit un salariu de peste 172.000 de lei.
            </p>
            <p>
              Lilian Carp a mai beneficiat anul trecut de la parlament de
              aproximativ 250.000 de lei drept compensații și indemnizații
              pentru transport, tratamente și alte cheltuieli.
            </p>
            <p>
              În declarația sa de avere, Carp mai indică trei conturi bancare pe
              care deține aproximativ 31.000 de lei. Soția sa declară un cont cu
              36.000 de lei.
            </p>
            <p>
              Din 2012, familia Carp deține un apartament de 90.3 m.p., pentru
              care ar fi achitat peste jumătate de milion de lei. Din 2021,
              Lilian Carp conduce un automobil marca Volkswagen, pentru
              procurarea căruia arată că a cheltuit 100.000 de lei.
            </p>
            <p>
              De asemenea, el deține o cotă parte de 30% la Întreprinderea Mixtă
              ,,Jereasdava” S.R.L, cu sediul la Zamciogi, Strășeni, a cărei
              activitate se concentrează pe comerț angro cu carne.
            </p>
          </LongRead>
          <LongRead
            menuTitle="Candidatul Platformei DA la șefia capitalei, Victor Chironda: Scandalul cu Ceban, referendumul anti-Chirtoacă și partidele din trecut"
          >
            <h2>
              Candidatul Platformei DA la șefia capitalei, Victor Chironda:
              Scandalul cu Ceban, referendumul anti-Chirtoacă și partidele din
              trecut
            </h2>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-ddf0-08dbbab23a15_w1200.jpg"
              caption="Victor Chironda alături de colegii de campanie din Platforma DA, 20 septembrie 2023"
            />
            <p>
              După ce Partidul Platforma Demnitate și Adevăr a pierdut în fața
              actualului primar, Ion Ceban, la alegerile locale din 2019,
              formațiunea își mai încearcă șansele, doar că de data asta nu cu
              Andrei Năstase – vechiul candidat, dar cu unul nou – un fost
              subaltern al lui Ceban.
            </p>
            <p>
              Victor Chironda, urbanist și activist civic, și-a mai dorit în
              trecut să ajungă primar. În toamna lui 2019, el a participat la
              alegerile locale pe platforma Mișcării social-politice{' '}
              <a
                href="https://www.ipn.md/ro/echipa-lui-victor-chironda-candideaza-pe-platforma-miscarii-forta-8006_1067996.html"
                target="_blank"
              >
                „Forța nouă”
              </a>
              . Acesta a acumulat atunci 2,73% din voturi, în turul I.
            </p>
            <p>
              De data aceasta, Victor Chironda, în vârstă de 34 de ani, va
              candida din partea Platformei DA, deși nu face parte din partidul
              respectiv. În ultimii 14 ani, el a fost asociat cu mai multe
              formațiuni și alianțe politice din R. Moldova.
            </p>
            <p>
              Pe 5 noiembrie, Chironda se va duela inclusiv cu primarul Ion
              Ceban, căruia i-a fost subaltern în 2019 când a deținut funcția de
              viceprimar.
            </p>
            <h3>Scandalul cu Ceban, demiterea și restabilirea în funcție</h3>
            <p>
              Victor Chironda a pierdut alegerile pentru funcția de primar în
              2019, dar voturile acumulate i-au permis să fie{' '}
              <a
                href="https://a.cec.md/storage/ckfinder/files/CR_PV_Scanate/1_Chi%C8%99in%C4%83u_PV_CM.pdf"
                target="_blank"
              >
                consilier municipal
              </a>
              , funcție în care nu s-a aflat însă mult timp. Ion Ceban l-a
              propus pentru funcția de viceprimar, responsabil de Urbanism și
              Mobilitate Urbană.
            </p>
            <p>
              Chironda a fost viceprimar până în 2021, când a fost demis cu
              scandal. Neînțelegerea cu Ceban a apărut după ce Chironda i-a
              solicitat{' '}
              <a
                href="https://newsmaker.md/ro/chironda-i-a-solicitat-lui-ceban-initierea-unei-anchete-de-serviciu-in-privinta-arhitectului-sef-al-mun-chisinau-care-este-motivul/"
                target="_blank"
              >
                inițierea unei anchete de serviciu
              </a>{' '}
              în privința Svetlanei Dogotaru, arhitecta-șefă a capitalei,
              afirmând că aceasta ar fi emis, cu încălcări, autorizația pentru
              demolarea Hotelului Național.
            </p>
            <p>
              Primarul i-a cerut lui Chironda să demisioneze, invocând că a
              lipsit nemotivat de la muncă și că{' '}
              <a
                href="https://www.jurnaltv.md/news/04b2202827e1fa58/primarul-ion-ceban-i-a-cerut-viceprimarului-victor-chironda-sa-demisioneze.html"
                target="_blank"
              >
                „murdărește”
              </a>{' '}
              primăria și echipa. Ulterior, Consiliul Municipal Chișinău (CMC) a{' '}
              <a
                href="https://www.chisinau.md/ro/victor-chironda-a-fost-demis-din-functia-de-viceprimar-de-20292_245784.html"
                target="_blank"
              >
                votat demiterea din funcție
              </a>{' '}
              a lui Chironda, la propunerea primarului general, Ion Ceban.
              Decizia a fost aprobată cu votul a 28 de consilieri.
            </p>
            <p>
              Drept răspuns, Chironda a mers în instanță, ajungând până la
              Curtea de Apel, care a suspendat decizia de demitere a CMC. La
              anunțul că se va prezenta la serviciu, Ion Ceban i-a{' '}
              <a
                href="https://newsmaker.md/ro/chironda-de-luni-ma-voi-prezenta-la-serviciu-ceban-birouri-libere-nu-avem/"
                target="_blank"
              >
                spus
              </a>{' '}
              lui Chironda că nu sunt birouri libere la primărie și că lucrează
              cu echipa pe care o alege.
            </p>
            <p>
              Chironda, care a invocat că a fost dat afară din motive politice,
              nu a mai revenit la postul său de muncă și a continuat să critice
              primăria și acțiunile lui Ion Ceban.
            </p>
            <p>
              Activitatea din trecut îl urmărește pe Chironda și în această
              campanie. Pe lângă realizările despre care vorbește în{' '}
              <a
                href="https://www.facebook.com/chironda.victor/videos/306952471861883"
                target="_blank"
              >
                spoturile
              </a>{' '}
              sale electorale, printre care și{' '}
              <a
                href="https://www.jurnal.md/ro/news/f328052e37c62b41/un-nou-spatiu-public-modern-si-multifunctional-in-inima-chisinaului-cum-arata-scuarul-cehov-dupa-inaugurare.html"
                target="_blank"
              >
                renovarea
              </a>{' '}
              Scuarului Cehov pentru care a organizat o campanie de colectare de
              fonduri, Victor Chironda este și ținta unor critici.
            </p>
            <p>
              Cel mai recent exemplu a fost mediatizat de jurnaliștii de la
              publicația online „Cu sens”. Reporterii au documentat cazul unei
              clădiri din capitală care a fost construită cu mai multe
              încălcări, pentru care Victor Chironda, în calitate de viceprimar,
              alături de arhitecta-șefă a Chișinăului, Svetlana Dogotaru, a
              semnat certificatul de urbanism în 2020.
            </p>
            <p>
              „Discutați cu arhitectul-șef al orașului. Ea a semnat prima acest
              document. […] Lasă să răspundă în calitate de funcționar în
              funcție. Eu am fost demis fix pe lucruri de genul acesta. Eu nu
              mai vreau să răspund pentru alți oameni. Eu mi-am făcut onest
              activitatea și am tras suficient pentru chestiile acestea”, a
              răspuns Chironda jurnaliștilor de la „Cu sens”.
            </p>
            <h3>PL(R) și referendumul împotriva lui Chirtoacă</h3>
            <p>
              Victor Chironda s-a remarcat inclusiv prin faptul că în 2016 și-a
              asumat rolul de lider al grupului de{' '}
              <a href="https://moldova.europalibera.org/a/27884513.html">
                inițiativă
              </a>{' '}
              pentru organizarea unui referendum de demitere a primarului de
              atunci, Dorin Chirtoacă. Inițiativa apărea în urma unei legi
              privind statutul municipiului Chișinău, care oferea primarului
              împuterniciri sporite, în defavoarea consilierilor.
            </p>

            <p>
              Chironda a fost un critic al primarului Chirtoacă, deși anterior
              au fost colegi în cadrul Partidului Liberal (PL), în perioada 2009
              – 2012.
            </p>

            <p>
              După PL, în 2013, Chironda a aderat la Partidul Liberal Reformator
              (PLR), formațiune lansată de foști membri PL, care n-au fost de
              acord cu deciziile conducerii partidului, în frunte cu Mihai
              Ghimpu. În acel an, PLR ajungea la guvernare alături de PLDM și
              PDM, într-o{' '}
              <a href="https://moldova.europalibera.org/a/26629178.html">
                alianță
              </a>{' '}
              numită „Coaliția pentru Guvernare Pro-Europeană”, care a votat
              pentru guvernul Leancă.
            </p>

            <p>
              Chironda a părăsit PLR după alegerile parlamentare din 2014, când
              partidul n-a mai intrat în legislativ.
            </p>
            <p>
              Un an mai târziu, în 2015, Victor Chironda era deja alături de
              echipa Blocului Electoral „Platforma Populară Europeană din
              Moldova – Iurie Leancă” (PPEM), care participa la alegerile locale
              din acel an. A reușit să facă parte din Consiliul Municipal
              Chișinău abia în perioada iulie – septembrie 2019, pe final de
              mandat, venind în locul Alei Nemerenco, care a devenit ministra
              Sănătății în guvernul Maiei Sandu.
            </p>
            <h3>Studii și avere declarată</h3>
            <p>
              Victor Chironda și-a făcut studiile la Universitatea de Stat din
              Moldova, Facultatea de Istorie, luându-și, în 2011, licența în
              Științe Umanitare. A urmat, mai apoi, studii de masterat în
              Administrație Publică, iar în 2018, a obținut o altă diplomă de
              master, în Studii Urbane, la Bruxelles, Viena, Copenhaga, Madrid,
              în cadrul 4Cities – Euromaster in Urban Studies.
            </p>
            <p>
              În CV-ul său mai sunt enumerate activitățile precedente de
              consultant național în cadrul proiectului „Orașe verzi pentru
              Moldova” și cea de autor și coordonator pentru proiectul „Scuarul
              Cehov”.
            </p>
            <p>
              În 2013 - 2014, Chironda a mai lucrat în departamentul de
              comunicare la Air Moldova, pe atunci companie de stat. În
              următorii doi ani a fost la Fondul de Investiții Sociale din
              Moldova.
            </p>
            <p>
              În ultima sa declarație de avere și interese, depusă în martie
              2021, Chironda indica salariul de funcție, în calitatea sa de
              viceprimar și un venit de 35.000 de lei, obținut din vânzarea unui
              automobil marca Honda Jazz din 2007. De asemenea, Chironda își
              procura în 2020 un autoturism Hyundai Creta, produs în 2017,
              indicând costul de 99.000 de lei.
            </p>
            <p>
              Candidatul PPDA mai indica, în declarația de atunci, calitatea sa
              de președinte al Asociației „Centrul pentru Politici Europene”,
              precum și pe cea de asociat al comunității „WatchDog”.
            </p>
          </LongRead>
          <LongRead menuTitle="Candidatul Partidului Renaștere la șefia capitalei, Vasile Bolea: conexiunile cu Șor, integrarea în Uniunea Vamală și comentariile homofobe"
          >
            <h2>
              Candidatul Partidului Renaștere la șefia capitalei, Vasile Bolea: conexiunile cu Șor, integrarea în Uniunea Vamală și comentariile homofobe
            </h2>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-2e3c-08dbbf3ba5e0_w1200.jpg"
              caption="Vasile Bolea spune că a început să se intereseze de politică după protestele din 2009. În prezent face parte din Partidul Renaștere, după ce a fost la PCRM și PSRM"
            />
            <p>
              Înainte de alegerile locale, Vasile Bolea părăsește Partidul Socialiștilor și se alătură Partidului Renaștere, care în prag de campanie ajunge în vizorul procurorilor, într-un dosar privind finanțarea ilegală din partea „grupului criminal organizat” al oligarhului Ilan Șor.
            </p>
            <p>
              A doua zi după ce și-a anunțat candidatura la funcția de primar al capitalei, pe 5 septembrie, Vasile Bolea a fost primit la Ambasada Federației Ruse în R. Moldova, unde a avut o <a href="https://moldova.mid.ru/mo/news/20230905_/" target="_blank">întâlnire</a> cu ambasadorul Oleg Vasnețov. Cei doi au discutat despre „agenda bilaterală ruso-moldovenească, tendințele regionale și alegerile locale”.
            </p>
            <p>
              În săptămânile următoare, au urmat o serie de <a href="https://www.facebook.com/moldovarenastete/posts/pfbid029rWbGPGcFYNiCfFR9NZB7KNUQ5efEaMqxKJfQLdYPw42CAz3Dc9fJJZrJwZiX1Qol" target="_blank">discuții</a> cu diferiți oficiali ruși, <a href="https://www.facebook.com/photo/?fbid=201173306314930&set=a.126295083802753" target="_blank">vizite</a> la Moscova, dar și un <a href="https://www.facebook.com/moldovarenastete/videos/1259556071421368" target="_blank">protest</a> în fața Ambasadei UE de la Chișinău, la care liderii Partidului Renaștere au rupt drapelul UE, steagul LGBT și au strigat „Jos ocupația europeană!”.
            </p>
            <p>
              Acum, Partidul Renaștere își propune să organizeze un referendum privind aderarea R. Moldova la Uniunea Economică Euroasiatică. Vasile Bolea nu este la prima sa tentativă de a organiza un astfel de plebiscit. În 2014, când era în Partidul Socialiștilor, el protesta la Bruxelles <a href="https://www.youtube.com/watch?v=afh8kzHrDqQ" target="_blank">împotriva semnării Acordului</a> de Asociere cu Uniunea Europeană, alături de actualul primar, Ion Ceban, cu care va concura la aceste alegeri locale.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-b51f-08dbbf30b939_w1200.jpg"
              caption="Partidul Socialiștilor a protestat la Bruxelles în ziua în care R. Moldova semna Acordul de Asociere, 27 iunie 2014"
            />
            <p>
              „Chiar din inima Europei, îi îndemnăm pe oficialii europeni să înceteze haosul politic şi să dea indicaţii autorităţilor moldoveneşti de a organiza un referendum”, <a href="https://socialistii.md/proteste-si-marsuri-la-bruxelles-impotriva-semnarii-acordului-privind-asocierea/" target="_blank">spunea</a> atunci juristul PSRM, Vasile Bolea, care cerea organizarea unui plebiscit național privind vectorul de dezvoltare al R. Moldova. În acel an, PSRM a avut mai multe tentative de a organiza referendumuri în Găgăuzia și la nivel național, dar acestea au eșuat.
            </p>
            <h3>
              Migrarea către Șor
            </h3>
            <p>
              În martie 2023, PSRM acuza Partidul Șor că încearcă să corupă deputații și aleșii locali socialiști. Secretarul executiv al PSRM, Vlad Batrîncea, făcea aceste <a href="https://moldova.europalibera.org/a/sociali%C8%99tii-%C3%AEl-acuz%C4%83-pe-%C8%99or-c%C4%83-%C3%AEncearc%C4%83-s%C4%83-le-cumpere-deputa%C8%9Bii-/32336103.html" target="_blank">declarații</a> după ce deputații Irina Lozovan și Alexandr Nesterovschi au părăsit PSRM și s-au alăturat Mișcării pentru Popor – un grup de inițiativă creat, în februarie, de mai multe formațiuni politice cu Partidul Șor în rol central. Printre acestea era și Partidul Renaștere, la care au aderat ulterior cei doi politicieni.
            </p>

            <p>
              „Partidului lui Ilan Șor n-am să mă alătur. De ce? Deoarece viitorul acestui partid trebuie să fie decis de către Curtea Constituțională”, a răspuns Bolea, surprins de jurnaliști în fața aeroportului în luna mai, când se întorcea din Israel, acolo unde s-a întâlnit cu Ilan Șor. În următoarele zile Vasile Bolea avea să anunțe că aderă la Partidul Renaștere, un <a href="https://moldova.europalibera.org/a/cum-se-scurg-socialistii-intr-un-satelit-al-partidului-sor-/32424432.html" target="_blank">satelit</a> al Partidului Șor, scos în afara legii de către Curtea Constituțională în luna <a href="https://moldova.europalibera.org/a/partidul-sor-declarat-ilegal-de-curtea-constitutionala-/32465777.html">iunie</a>, din cauza că a acționat în contradicție cu principiile statului de drept și a pus în pericol suveranitatea și independența R. Moldova.
            </p>
            <p>
              La sfârșitul lunii septembrie, în toiul pregătirilor pentru alegerile locale, cei doi deputați transfugi Irina Lozovan și Alexandr Nesterovschi, rămași fără imunitate parlamentară, sunt arestați într-un dosar penal privind finanțarea ilegală a formațiunii politice din partea unui „grup criminal organizat”.
            </p>
            <p>
              Vasile Bolea <a href="https://www.facebook.com/bolea.deputat/videos/293808996621607" target="_blank">spunea</a> că cei doi politicieni urmau să participe la alegerile locale, iar acuzațiile împotriva lor sunt „o încercare de a priva Partidul Renaștere de posibilitatea de a câștiga alegerile în nordul țării”.
            </p>
            <h3>
              Declarațiile pro-ruse, homofobe și minciuna despre 30.000 de sirieni
            </h3>
            <p>
              Vasile Bolea consideră că R. Moldova trebuie să <a href="https://www.youtube.com/watch?v=xUADwkfGvi4" target="_blank">rămână</a> în CSI și nu vede Rusia drept stat <a href="https://www.youtube.com/watch?v=jXsAZGHeU04" target="_blank">agresor</a> în războiul din Ucraina. În aparițiile sale publice, el a susținut că „autoritățile Ucrainei puteau să se comporte altfel, să găsească acele compromisuri, dacă voiau”.
            </p>
            <p>
              Bolea a fost unul dintre promotorii falsului cu 30.000 de sirieni. Minciuna, care a <a href="https://www.zdg.md/stiri/politic/cum-a-evoluat-minciuna-cu-sirienii-de-la-un-site-de-pamflete-la-cea-mai-urmarita-televiziune-din-tara/" target="_blank">luat proporții ample în campania prezidențială</a> din 2016, pe care le-a câștigat Igor Dodon, fusese promovată de PSRM și în materialele lor politice împotriva contracandidatei Maia Sandu. „Dacă era să vină Maia Sandu președinte, avea să aducă cei 30.000 de sirieni” în R. Moldova, continua să <a href="https://tv8.md/2018/05/23/video-unde-sunt-cei-30-000-de-sirieni-din-alegerile-prezidentiale-deputat-socialist-cand-ultima-data-ati-fost-in-mall-la-etajul-patru" target="_blank">spună</a> Bolea chiar și după doi ani de la alegeri, în 2018.
            </p>
            <p>
              În 2021, Bolea își continua luptele politice spunând că <a href="https://www.zdg.md/importante/bolea-despre-autodizolvarea-parlamentului-inventie-a-unui-autist-bagata-pe-gat-deputati-dar-si-reprezentanti-ai-ong-urilor-critica-declaratia-lui-bolea/" target="_blank">„autodizolvarea parlamentului este invenția unui autist”</a> — declarație criticată de Federația pentru Drepturi și Resurse a Persoanelor cu Tulburări din Spectrul Autism din R. Moldova drept „stigmatizare” a persoanelor neurodivergente. Câteva zile mai târziu, Bolea <a href="https://www.zdg.md/stiri/stiri-sociale/bolea-isi-cere-scuze-publice-dupa-ce-a-folosit-expresia-autodizolvarea-parlamentului-este-inventia-unui-autist-bagata-pe-gat/" target="_nlank">a cerut scuze</a> „familiilor și persoanelor care s-au simțit ofensate”.
            </p>
            <p>
              În același an, Bolea, alături de alți doi colegi socialiști, a prezentat, la o conferință de presă, <a href="https://www.ipn.md/ro/socialistii-propun-completarea-constitutiei-la-articolul-48-familia-7965_1081604.html" target="_blank">inițiativa de modificare a articolului 48</a> (Familia) din Constituția R. Moldova, adăugând următorul text: „Statul interzice căsătoria partenerilor de același sex”, dar și următoarea prevedere: „Părinții unui copil reprezintă un tată (bărbat) și o mamă (femeie)”.
            </p>
            <p>
              Organizația pentru drepturile LGBT+, <a href="https://gdm.md/files/untitled%20folder/Raport-GENDERDOC-M-2021_romana.pdf?fbclid=IwAR3k1hmiV6DKDT7kr4lSMgX8wQsPTkIkUv049eU-9Z_po1F-Pyl5KjjmmyE" target="_blank">Centrul Genderdoc-M</a> nota în raportul său pentru acel an că electorala a fost „acompaniată, tradițional deja, de un val de mesaje homofobe din partea PSRM”.
            </p>
            <p>
              Alături de alți doi deputați transfugi din Partidul Renaștere, în luna iunie a acestui an, Bolea a înregistrat în Parlament un alt proiect de lege care ar <a href="https://newsmaker.md/ro/bolea-nesterovschi-si-lozovan-organizeaza-propriul-mars-proiect-de-lege-privind-interzicerea-propagandei-lgbt-in-randul-tinerilor/" target="_blank">interzice „propaganda LGBT în rândurile minorilor”</a>.
            </p>
            <p>
              „A devenit deja o tradiție ca politicienii pro-ruși să propună această lege, copie a legii din Federația Rusă, care evident rămâne doar ca propunere și nu este nici pusă pe agenda Parlamentului, pentru că este una discriminatorie. Lor, de fapt, nici nu le prea pasă de soarta acestei legi, după ce declară propunerea proiectului”, spune activista și administratoarea Centrului GDM, Angela Frolov.
            </p>
            <h3>
              De la activitatea sportivă la cea legislativă
            </h3>
            <p>
              În pofida declarațiilor și acțiunilor politice din ultimii ani, <a href="https://www.parlament.md/StructuraParlamentului/Deputa%C8%9Bii/tabid/87/Id/537/language/ro-RO/Default.aspx" target="_blank">biografia lui Bolea de pe pagina Parlamentului</a> arată, în lista sa de calități, faptul că ar fi o „persoană bine determinată pe plan profesional și personal”.
            </p>
            <p>
              Vasile Bolea a studiat dreptul economic la Academia „Ştefan cel Mare” a Ministerului Afacerilor Interne, obținând mai apoi, în 2005, gradul de Magistru în Dreptul Antreprenoriatului.
            </p>
            <p>
              Până a intra în politică, Bolea a fost jurist la mai multe companii și instituții publice, dar și sportiv. El a fost rugbist de performanță și a făcut parte inclusiv din lotul național al R. Moldova.
            </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-e54f-08dbbf334864_w1200.jpg"
              caption="Vasile Bolea a făcut parte din Lotul R. Moldova la Rugby în perioada 2006-2009"
            />
            <p>
              Vasile Bolea <a href="https://www.youtube.com/watch?v=nIXh_ge_x0I" target="_blank">spune</a> că a început să se intereseze de politică după protestele anticomuniste din aprilie 2009. S-a implicat în activitatea Partidului Comuniștilor din R. Moldova (PCRM) datorită colegului său din școală, Vlad Batrîncea, care era deja în partid.
            </p>
            <p>
              În 2011 începe să lucreze pentru Fundația de binefacere „<a href="https://protv.md/politic/fundatie-de-caritate-cu-iz-politic-dodon-fundatia-solutia-isi---73675.html" target="_blank">Soluția</a>”, unde este angajat șef al departamentului juridic. Fundația a fost lansată în acel an de Igor Dodon, care la acea vreme era membru al Partidului Comuniștilor.
            </p>
            <p>
              În noiembrie 2014, este ales deputat pe listele Partidului Socialiștilor, partid în care rămâne până în luna mai a acestui an.
            </p>
            <p>
              În Parlament, Vasile Bolea, pe lângă inițiativele legate de limitarea <a href="https://newsmaker.md/ro/pedeapsa-penala-pentru-propaganda-homosexualitatii-psrm-va-inregistra-proiectul-de-lege-privind-modificarea-constitutiei-video/" target="_blank">drepturilor</a> persoanelor LGBT, a mai promovat <a href="https://www.parlament.md/ProcesulLegislativ/Proiectedeactelegislative/tabid/61/LegislativId/4068/language/ro-RO/Default.aspx" target="_blank">condamnarea</a> unionismului, dar și oferirea limbii ruse a unui <a href="https://www.parlament.md/ProcesulLegislativ/Proiectedeactelegislative/tabid/61/LegislativId/5314/language/ro-RO/Default.aspx" target="_blank">statut</a> special.
            </p>
            <h3>
              Averea declarată, apartamente donate și credite
            </h3>
            <p>
              În declarația sa de avere pentru 2022, Vasile Bolea indică un salariu anual, din activitatea de deputat, de 239.329 de lei. Acesta susține că soția sa a primit, anul trecut, 261.987 de lei, din activitatea de juristă la Agenția Informațională de Stat Moldpres.
            </p>
            <p>
              Pe lângă asta, Bolea a beneficiat de la Parlament de indemnizație de transport, în sumă de 69.756 de lei, compensație pentru tratament de sănătate de 19.930 de lei, precum și de compensație pentru îndeplinirea atribuțiilor de serviciu de 156.000 de lei. Soția sa ar fi făcut 70.833 de lei și, respectiv, 2.160 euro din locațiunea a două imobile.
            </p>
            <p>
              De asemenea, familia Bolea are în proprietate mai multe apartamente. A primit, printr-un contract de donație, un teren agricol estimat la 72.000 de lei. În 2022, familia Bolea ar mai fi beneficiat de o altă donație: un apartament de 82,3 m.p., despre care arată că ar costa 600.936 de lei.
            </p>
            <p>
              Tot anul trecut, familia Bolea a cumpărat un bun imobil pentru care declară că a plătit 25.000 de euro.
            </p>
            <p>
              Fostul deputat socialist mai are trei conturi în sumă totală de 25.000 de lei, iar soția sa unul de 10.000 de lei. În declarația de avere Vasile Bolea indică și două credite pe numele său - unul de 500.000 de lei și altul de 25.000 de euro.
            </p>
          </LongRead>
          <LongRead
            menuTitle="Candidatul Partidului MAN la șefia capitalei, Ion Ceban: Rusia vs. România, Uniunea Vamală vs. UE și „primăria-partid”"
            hasNextButton={false}
          >
            <h2>
              Candidatul Partidului MAN la șefia capitalei, Ion Ceban: Rusia vs. România, Uniunea Vamală vs. UE și „primăria-partid
          </h2>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-4c05-08dbc00ea4a6_w1200.jpg"
              caption="Ion Ceban vrea să obțină cel de-al doilea mandat de primar al capitalei"
            />
            <p>
              În 2014, protesta la Bruxelles față de Acordul de Asociere cu Uniunea Europeană. La nouă ani distanță, Ion Ceban se declară pro-european și spune că „integrarea europeană ar trebui să devină idee națională”.
            </p>

            <p>
              Ion Ceban a fost învestit în funcția de primar al Chișinăului în noiembrie 2019. În acest context, noul primar părăsea oficial Partidul Socialiștilor pe listele căruia fusese ales, spunând că la primărie <a href="https://agora.md/stiri/63765/vizita-negeopolitica-la-primarie--iata-primul-musafir-al-lui-ceban" target="_blank">„nu va mai fi loc pentru geopolitică și politică”</a>.
            </p>
            <p>
              <a href="https://agora.md/stiri/63765/vizita-negeopolitica-la-primarie--iata-primul-musafir-al-lui-ceban" target="_blank">Primul</a> său oaspete la primărie a fost ambasadorul Federației Ruse în R. Moldova, Oleg Vasnețov, cu care au stabilit <a href="https://www.chisinau.md/ro/vizita-es-ambasadorul-federatiei-ruse-oleg-vasnetov-la-primaria-municipiului-20292_243358.html" target="_blank">„o agendă comună”</a> de lucru. „Contăm mult pe prietenia și experiența orașelor mari din Rusia”, declara în cadrul acelei vizite edilul, căruia ambasadorul îi promitea că primarii orașelor din Rusia vor fi dispuși să-i acorde „susținere și asistență”.
              </p>
            <p>
              La două săptămâni după vizita lui Vasnețov, Ceban zbura la Moscova, în prima sa vizită oficială externă. Primarul s-a întâlnit cu doi oficiali de rang înalt care-l puteau ajuta în a crea relații mai apropiate cu Rusia. Primul - omologul său, primarul Moscovei, Serghei Sobianin, iar al doilea - Dmitri Kozak, reprezentantul președintelui rus, Vladimir Putin, pentru dezvoltarea relațiilor comercial-economice cu R. Moldova.
              </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-1de8-08dbc00e9e2f_w1200.jpg"
              caption="„Am primit felicitări cu ocazia alegerii mele în funcția de primar și mi-a dorit succes în exercitarea mandatului”, spunea Ion Ceban despre întâlnirea cu Dmitrii Kozak, noiembrie 2019"
            />
            <p>
              Cu Kozak, Ceban a convenit <a href="https://www.facebook.com/Ion.Ceban.public/posts/2492802837608316" target="_blank">„să aibă o colaborare strânsă”</a>, iar lui Sobianin i-a cerut printre altele <a href="https://www.chisinau.md/ro/intrevederea-primarului-general-al-capitalei-ion-ceban-cu-primarul-orasului-20292_243411.html" target="_blank">„suport sub formă tehnică și utilaje”</a> pentru necesitățile municipalității.
            </p>

            <p>
              Pe parcursul mandatului, Ion Ceban a căutat sprijin pentru proiectele primăriei și în România. După ce l-a primit pe ambasadorul rus la primărie pe 13 noiembrie 2019, a doua zi primarul îl avea ca oaspete pe ambasadorul României în R. Moldova, Daniel Ioniță, pe care „l-a rugat să ajute echipa Primăriei Chișinău în vederea realizării <a href="https://www.chisinau.md/ro/excelenta-sa-ambasadorul-romaniei-in-republica-moldova-domnul-daniel-ionita-20292_243359.html" target="_blank">proiectelor comune</a> cu primăriile din România”.
              </p>
            <p>
              În calitate de primar al capitalei, în 2022 Ion Ceban a avut peste 30 de întâlniri cu primarii și liderii consiliilor municipale din România, căutând sprijin financiar pentru proiectele sale.
              </p>
            <p>
              Spre deosebire de perioadele anterioare din cariera sa politică, pe durata mandatului de la primărie, Ion Ceban nu s-a manifestat în mod deschis împotriva legăturilor cu Vestul, cu România, cu Uniunea Europeană sau cu NATO. Dimpotrivă, în ultimul an înainte de alegeri, a lansat un partid declarat pro-european cu care își propune să câștige cel de-al doilea mandat de primar al Chișinăului.
              </p>
            <h3>
              Schimbarea la față
              </h3>
            <p>
              În fruntea noului său <a href="https://www.facebook.com/photo/?fbid=630262118462207&set=a.413216126833475" target="_blank">partid</a> - Mișcarea Alternativă Națională (MAN), înregistrat oficial în anul 2022, cu un nucleu format în bună parte din angajați ai primăriei, Ion Ceban declară că <a href="https://realitatea.md/video-ceban-man-pledeaza-pentru-ca-integrarea-europeana-sa-fie-ideea-nationala-a-republicii-moldova/" target="_blank">„integrarea europeană ar trebui să devină idee națională”</a>. Fraza respectivă devine, treptat, o mantră politică și este repetată la aparițiile sale publice din 2023.
              </p>
            <p>
              Constituirea oficială a partidului MAN avea loc la câteva luni după ce Departamentul Trezoreriei al Statelor Unite ale Americii a <a href="https://home.treasury.gov/news/press-releases/jy1049" target="_blank">anunțat</a> sancțiuni împotriva mai multor persoane și entități care au acționat cu scopul destabilizării R. Moldova în interesul Moscovei.
              </p>

            <p>
              Autoritățile americane spuneau atunci că noul partid anunțat de Ion Ceban a primit ajutor din partea consilierilor politici apropiați guvernului rus. Potrivit SUA, o echipă condusă de Igor Gudilin, tehnolog politic rus și fost ofițer FSB, care a fost în R. Moldova pentru a-l consilia în alegeri pe fostul președinte Igor Dodon și Partidul Socialiștilor, „a oferit sprijin pentru un grup politic de opoziție – Mișcarea Alternativă Națională”. Ion Ceban califica aceste informații ca fiind neadevărate.
                </p>

            <p>
              În aprilie 2023, Ceban sugera crearea unui „Pact” de la Chișinău, prin care semnatarii s-ar angaja să-și unească eforturile pentru integrarea R. Moldova în Uniunea Europeană, spunând că s-a inspirat după modelul „Declarației de la Snagov” din România.
                </p>

            <p>
              La sfârșitul anului trecut, Centrul Român de Politici Europene (CRPE) a publicat un <a href="https://www.crpe.ro/wp-content/uploads/2022/12/Raport-CRPE-2022-Aventurile-lui-Ion-Ceban-in-Romania-.pdf?fbclid=IwAR0k9oik9S6iWX9wW2A8lE1vIajpOv31Gx6n4TuI9pBXUzm_7H6JWml8sfc" target="_blank">raport</a> despre numărul crescând de vizite ale primarului Ion Ceban în România în anul 2022, în încercarea sa de a atrage finanțări și suport de la partidele și administrațiile românești.
                </p>
            <p>
              „Dacă finanțările sunt încă limitate, multe primării sau consilii județene fiind reticente sau alocând bugete reduse, nu același lucru se poate spune despre rezultatele campaniei sale de PR. Ceea ce Ion Ceban știe să exploateze foarte bine acasă, în R. Moldova”, se menționa în raport.
                </p>
            <p>
              Autorii acestuia subliniau că Ceban și turul său prin localitățile românești se înscriu într-o listă lungă de decizii aparent inexplicabile luate de București în relația strategică dintre România și R. Moldova.
                </p>
            <h3>
              Drumul (scurt) de la Moscova spre Washington
                </h3>
            <p>
              Cariera politică a lui Ion Ceban a început la Partidul Comuniștilor din R. Moldova (PCRM), condus de Vladimir Voronin. Ceban s-a făcut cunoscut publicului mai ales pentru <a href="https://www.youtube.com/watch?v=9aw5RMGhZiQ" target="_blank">discursurile</a> sale de la tribună, în timpul protestelor față de Alianța pentru Integrare Europeană, o coaliție politică declarată pro-europeană și care a venit la guvernare după protestele anticomuniste din aprilie 2009.
                </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-6758-08dbc01ecf85_w1200.jpg"
              caption="Primarul mun. Chișinău, Ion Ceban, în vizită la Igor Dodon, președintele R. Moldova de atunci, 10 martie 2020"
            />
            <p>
              În 2012, Ion Ceban a migrat către Partidul Socialiștilor din R. Moldova (PSRM), în calitate de secretar pentru ideologie, urmându-l pe colegul său Igor Dodon, un politician pro-rus în ascensiune la acea vreme.
              </p>
            <p>
              Relația dintre cei doi s-a dovedit a fi una de durată. După instalarea în funcție în 2019, Ceban și-a luat de două ori concediu de la primărie pentru a-l ajuta pe Igor Dodon în campania prezidențială din 2020, când a pierdut în fața candidatei Maia Sandu.
                </p>
            <p>
              În perioada când era membru al PSRM, Ceban avea să-și asume unele dintre cele mai memorabile acțiuni pro-ruse ale sale.
                </p>
            <p>
              Cu o lună înainte ca Rusia să anexeze Crimeea în 2014, Ceban organiza împreună cu colegii săi de partid un referendum <a href="https://moldova.europalibera.org/a/25411572.html">ilegal</a>, în care locuitorii Găgăuziei erau întrebați inclusiv dacă vor să adere la UE sau la Uniunea Vamală.
                </p>
            <p>
              Urmare a plebiscitului declarat ilegal din Găgăuzia, socialiştii au lansat iniţiativa de a organiza asemenea acțiuni în toate raioanele ţării, dar și această tentativă a eșuat.
                </p>
            <p>
              „Referendumul din Găgăuzia este un atac asupra statalității”, constatau autorii unei <a href="https://expert-grup.org/ro/activitate/item/920-%25252525E2%2525252580%252525259Ereferendumul%25252525E2%2525252580%252525259D-din-g%25252525C4%2525252583g%25252525C4%2525252583uzia-atac-asupra-integrit%25252525C4%2525252583%25252525C8%252525259Bii-republicii-moldova" target="_blank">analize</a> publicate de Centrul Analitic Independent Expert-Grup, potrivit căruia „un rol important în organizarea plebiscitelor l-a avut Federația Rusă, inclusiv din punct de vedere financiar”.
                </p>
            <ClickableImage
              src="https://gdb.rferl.org/CBAAEBEF-8E75-4B70-B1D0-2A993CD04595_w1200.jpg"
              caption="Publicitate afișată pe străzile orașului Comrat, UTA Găgăuzia, pe care apar liderii PSRM, alături de sloganul „Pentru Moldova în Uniunea Vamală. Este cea mai bună alegere!”, noiembrie 2014"
            />
            <p>
              În iunie 2014, Ceban a semnat alături de alți socialiști un proiect de lege prin care solicita denunțarea Acordului de Asociere dintre R. Moldova și UE. Tot atunci, a mers la Bruxelles pentru a <a href="https://socialistii.md/proteste-si-marsuri-la-bruxelles-impotriva-semnarii-acordului-privind-asocierea/" target="_blank">protesta</a>. „Nu suntem de acord cu asta (cu integrarea europeană n.r.), […] populația R. Moldova alege integrarea în Uniunea Vamală (Rusia-Belarus-Kazahstan n.r.)”, <a href="https://www.zdg.md/stiri/video-socialistii-la-bruxelles-au-fost-alungati-pentru-ca-nu-aveau-autorizatie/comment-page-1/" target="_blank">spunea</a> Ion Ceban, ajuns în fața sediului Comisiei Europene în ziua în care R. Moldova semna Acordul de Asociere.
                </p>
            <p>
              Opt ani mai târziu, cu Dodon plecat din funcție și inculpat pentru mai multe <a href="https://moldova.europalibera.org/a/trei-dosare-intr-un-an-cum-este-judecat-fostul-presedinte-igor-dodon/32425343.html">infracțiuni</a>, de la corupție la trădare de patrie, Ceban, din fruntea unui partid declarat pro-european, nu mai vorbește despre integrarea în Uniunea Vamală și nici nu mai protestează față de Acordul de Asociere.
                </p>
            <p>
              Cu toate acestea, presa a scris în <a href="https://tv8.md/ru/2022/22/10/ion-ceban-recunoaste-rusia-ca-stat-agresor-condamn-razboiul-condamn-agresiunea/213372" target="_blank">octombrie</a> anul trecut, la opt luni de la invazia Ucrainei de către armata rusă, că Ion Ceban condamnă acțiunile Kremlinului spunând că „Rusia este stat agresor”. „Cel mai grav este că eu nu o dată am spus despre aceste lucruri”, a adăugat primarul, care a acuzat mass-media că a creat o <a href="https://www.facebook.com/tvchannelN4/videos/1319286178850142" target=" _blank">„percepție”</a> greșită despre poziția sa față de războiul din Ucraina. Ceban a fost acuzat și de promovarea unui <a href="https://moldova.europalibera.org/a/refugiati-moldova-ura-politicieni/31734333.html">discurs de ură</a> față de refugiații ucraineni, în <a href="https://euvsdisinfo.eu/ro/dezinformare-la-granita-razboiului/" target="_blank">martie 2022</a>, când a spus că refugiații ar pune în pericol securitatea locuitorilor capitalei.
                </p>


            <p>
              În schimb, politicianul, care odată indica în <a href="https://www.moldovacurata.md/profil-candidat-ion-ceban-1-1579" target="_blank">CV</a>-ul său că vorbește „limba moldovenească”, mai nou, de 1 decembrie 2022, îi felicita pe români cu ocazia Zilei Naționale, oferind drept cadou din partea Primăriei Chișinău un concert Zdob și Zdub la Alba Iulia.
                </p>

            <p>
              „Mulțumesc personal, și în numele locuitorilor Chișinăului, și al cetățenilor R. Moldova, pentru suportul pe care România și poporul român îl oferă. Sunt recunoscător pentru colaborarea pe care o avem și reușim să realizăm proiecte frumoase la Chișinău. La mulți ani, România!”, spunea Ceban în mesajul său.
              </p>
            <p>
              Primăria Chișinău, condusă de Ion Ceban, a reușit să obțină suport din partea unor administrații, cum ar fi autoritățile locale din Cluj-Napoca, care în 2022 au oferit un sprijin financiar mai multor orașe din Ucraina și din R. Moldova, în valoare de 175.000 de euro (60.000 de euro au provenit de la orașul Dijon, Franța) pentru achiziționarea echipamentelor casnice destinate refugiaților.
              </p>
            <p>
              Alte ori, în schimb, interacțiunile lui Ceban cu primăriile românești s-au rezumat la proiecte lipsite de concretețe, cum e în cazul municipiului Vaslui sau cel al Consiliului Județean Botoșani.
              </p>
            <p>
              Primăria municipiului Vaslui a declarat pentru Europa Liberă că l-a primit de mai multe ori în vizită pe primarul Ion Ceban, ocazie cu care s-au discutat posibile direcții de colaborare între cele două orașe, în special în domeniile cultural, sportiv și de tineret, doar că în urma acestora nu s-au realizat niciun fel tranzacții financiare.
              </p>
            <p>
              Și Consiliul Județean Botoșani menționează că interacțiunea cu primarul municipiului Chişinău s-a limitat la întâlnirea care a avut loc la sediul instituției respective, în luna aprilie a anului 2022. Discuțiile s-au axat pe viitoare proiecte de colaborare atât în domeniul cultural, cât și în privința atragerii fondurilor europene disponibile, fără a se concretiza într-un parteneriat de cooperare sau finanţare.
              </p>

            <p>
              Totodată, la Primăria sectorului 2 din București, Ion Ceban și-a făcut apariția neanunțat, potrivit unui răspuns al instituției transmis Europei Libere.
              </p>

            <p>
              La finele anului 2021, Consiliul Local al Sectorului 2 a votat pentru un Acord de cooperare cu Pretura sectorului Botanica. În luna februarie 2022, a avut loc o nouă întâlnire la Primăria Sectorului 2 cu pretorul Diana Guba și o echipă din administrația locală pentru un schimb de experiență.
              </p>

            <p>
              „Am constatat că și-a făcut apariția – total neanunțat și neinvitat – domnul Ion Ceban. Subliniem faptul că domnul Ceban nu a fost niciodată invitat nici de către primarul Sectorului 2, Radu Mihaiu, nici de Primăria Sectorului 2, în nicio calitate, oficială sau privată. De asemenea, prezența sa ca parte din delegația oficială nu a fost adusă la cunoștința instituției noastre și nici a primarului prin nicio formă de comunicare inter-instituțională”, se spune în răspunsul transmis Europei Libere de către Primăria sectorului 2 București.
              </p>
            <p>
              Tot în 2022, în aprilie, la Constanța a fost anulat evenimentul la care urma să fie semnat un acord de înfrățire cu Chișinău. Ion Ceban a reacționat într-o postare pe Facebook în care lua în derâdere decizia primarului Vergil Chițac. „Amiralul a capitulat sub presiuni și frustrări personale! Este o vorbă la moldoveni „singur cânt, singur dansez”, așa și în cazul lui. El a anunțat, el a renunțat”, a comentat primarul Chișinăului.
              </p>
            <p>
              Ulterior, Primăria Constanța a precizat într-un răspuns transmis Europei Libere că a decis anularea evenimentului din motiv că acestuia i s-a atribuit o semnificație politică.
              </p>
            <p>
              „Pentru a evita utilizarea în sens politic a acestui eveniment, s-a decis anularea evenimentului respectiv și reanalizarea oportunității demersului de semnare a documentului menționat”, au spus autoritățile constănțene.
              </p>
            <p>
              Pe 10 mai 2023, Ceban a anunțat o vizită în SUA, urmând să ajungă în mai multe orașe, la întâlniri cu parlamentari în Washington DC, interacțiuni cu diaspora moldovenească de la Chicago la Seattle și chiar cu reprezentanți Amazon, pe care spune că i-a invitat să-și deschidă birou la Chișinău.
              </p>
            <p>
              Ambasada SUA de la Chișinău a menționat pentru Europa Liberă că a ajutat la stabilirea contactului dintre primărie și unele organizații din Statele Unite prin intermediul Biroului de Diplomație Subnațională al Departamentului de Stat, dar nu a organizat vizita primarului Ceban în SUA.
              </p>
            <ClickableImage
              src="https://gdb.rferl.org/01000000-0aff-0242-fc32-08dbc0273ccf_w1200.jpg"
              caption="Primarul Ion Ceban alături de viceprimarul orașului Seattle, mai 2023"
            />
            <p>
              Autoritățile de la Seattle au precizat că întâlnirea cu primarul Ceban nu a fost o vizită diplomatică oficială, deși reprezintă o oportunitate de a construi relații la nivel subnațional. „Ion Ceban ne-a informat că va fi în oraș și am aranjat o conversație de bun venit pentru a ne exprima ospitalitatea și pentru a afla mai mult despre regiunile noastre. Nu s-a discutat sau ajuns la niciun acord oficial privind o colaborare sau vreo tranzacție financiară”, a precizat pentru Europa Liberă Primăria Seattle.
              </p>

            <p>
              La rândul său, primăria din Naperville a declarat pentru Europa Liberă că vizita primarului de Chișinău a fost „improvizată”, dar au avut o „conversație foarte bună”.
              </p>
            <p>
              Pe 24 mai 2023, Ion Ceban a făcut o postare rezumat pe Facebook, în care evalua drept productivă vizita sa în Statele Unite. Declară că s-a întâlnit cu oficiali, inclusiv la Washington și New York, cu care a convenit asupra unor acorduri de colaborare, iar cu administrația din Carolina de Nord, primarul spune că a stabilit să facă „un schimb de experiență pentru multe proiecte”.
              </p>
            <h3>
              Scandaluri cu jurnaliști, amenințări de interzicere a marșului. Pride și demiteri ilegale
              </h3>
            <p>
              Dincolo de turneele sale internaționale, activitatea lui Ion Ceban în fruntea Primăriei Chișinău nu s-a derulat fără scandaluri. Timp de patru ani, a reușit să fie <a href="https://www.zdg.md/stiri/stiri-justitie/reactia-primarului-ion-ceban-dupa-ce-a-fost-amendat-de-instanta-de-judecata-pentru-neexecutarea-unor-decizii-judecatoresti-care-vizeaza-un-vicepretor-al-sectorului-rascani/a" target="_blank">mendat de magistrați</a> pentru refuzul de a-l restabili în funcție pe vicepretorul de Râșcani, Valentin Vdovicenco, sfidând decizia instanței; a făcut <a href="https://www.moldova.org/ceban-spera-dar-a-sperat-si-dodon/" target="_blank">declarații homofobe</a> și a <a href="https://radiochisinau.md/ceban-acuza-presa-de-minciuni-la-randul-lor-institutiile-de-media-mentioneaza-ca-un-primar-trebuie-sa-se-ocupe-de-probleme-municipalitatii-iar-jurnalistii-au-libertatea-sal-critice-ori-sal-laude-daca-exista-merite---177683.html" target="_blank">acuzat presa</a> în repetate rânduri că minte, atunci când îi adresa întrebări incomode.
                </p>
            <p>
              Atitudinea sa controversată față de mass-media a culminat cu un moment din martie 2023. Ceban s-a arătat gata să facă din banii proprii un monument funerar pentru <a href="https://tribuna.md/2023/03/20/ion-ceban-sunt-gata-din-bani-proprii-sa-fac-un-monument-funerar-pentru-presa-libera-care-a-murit/" target="_blank">„așa-zisa presă liberă care a murit”</a>, acuzând jurnaliștii de afiliere cu guvernarea. De partea cealaltă, presa s-a confruntat, pe tot parcursul mandatului lui Ceban, cu o transparență tot mai redusă a proceselor primăriei, asta deși instituția s-a lăudat că ar fi „cea mai transparentă din țară”.
                </p>
            <p>
              Nici cu colegii săi de muncă relațiile n-au mers mereu bine. În 2021, la inițiativa lui Ion Ceban a fost demis viceprimarul Victor Chironda, despre care primul spunea, când i-a făcut invitația de a munci împreună, că este o persoană competentă, în care are încredere. Chironda a mers în judecată și a câștigat procesul împotriva primăriei, obținând o decizie irevocabilă.
                </p>
            <p>
              Anticoruptie.md a scris despre nouă demiteri ilegale pe care le-a ordonat Ceban, pentru care primăria era obligată să plătească despăgubiri în valoare de peste două milioane de lei, iar edilul risca dosar penal pentru sfidarea deciziilor Curții Supreme de Justiție.
              </p>
            <h3>
              Averea declarată a lui Ion Ceban
              </h3>
            <p>
              Pentru anul 2022, Ion Ceban declară un venit de 227.964 de lei, obținut din activitatea sa la Primăria Chișinău. Soția acestuia ar fi primit 37.500 de lei de la firma sa, TIVI KIDS SRL, care prestează servicii fotografice.
              </p>
            <p>
              Tot anul trecut, Ion Ceban arată că a primit de la socrul său, Ghenadie Țaulean, administrator al Asociației Internaționale a Transportatorilor Auto, o donație de 81.500 de lei.
              </p>
            <p>
              De asemenea, familia Ceban susține că în 2022 a primit 40.000 de lei la o zi de naștere și câte 20.000 de lei la alte două evenimente de familie. În afară de asta, a mai obținut dividende de 106.382 de lei de la TIVI KIDS SRL.
              </p>
            <p>
              Familia Ceban mai are în proprietate un apartament de 230,4 m.p., din 2016, obținut printr-un contract de donație de la părinții actualului primar — tatăl său fiind medic pensionar, iar mama — consilieră municipală. Din 2017, soția primarului are un automobil Skoda Kodiaq, fabricat în același an, valoarea estimată a acestuia fiind de aproximativ jumătate de milion de lei.
              </p>
          </LongRead>
        </Chapter>
      </Container>
    </Layout>
  )
}
